import React from 'react';
import * as Cesium from 'cesium';
import cesiumPackage from 'cesium/package.json';
import 'cesium/Build/Cesium/Widgets/widgets.css';
import Button from '@material-ui/core/Button';
import SvgIcon from '@material-ui/core/SvgIcon';
import MaterialIcon from './../../../components/MaterialIcon';
import { notification } from 'antd';

import Config from '../../../constants/appConfig';
import Calls from '../../../components/util/calls';

import emb200Model from './../../../assets/replay_3d/models/emb-200/aeronave.glb';
import airTractorAt502bModel from './../../../assets/replay_3d/models/air-tractor-at-502b/aeronave.glb';
import airTractorAt802Model from './../../../assets/replay_3d/models/air-tractor-at-802/aeronave.glb';
import cessna188Model from './../../../assets/replay_3d/models/cessna-188/aeronave.glb';
import emb203Model from './../../../assets/replay_3d/models/emb-203/aeronave.glb';
import thrush510Model from './../../../assets/replay_3d/models/thrush-510/aeronave.glb';
import weatherStationVehicleModel from './../../../assets/replay_3d/models/weather-station/veiculo.glb';
import iconA from './../../../assets/images/marker/a.svg';
import iconB from './../../../assets/images/marker/b.svg';
import './map3d.css';

import Units from '../../../components/util/units';
const cesiumIonToken = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJqdGkiOiJmMjFlMzc4ZC1hMjkzLTRhYTQtYTcxZi01Y2EzMTM0NTczYmUiLCJpZCI6MzIwMzMwLCJpYXQiOjE3NTIxNTc3OTV9.3Hevk1CLhRrpuR2DSjyD2pGdzwmmsKMn0PApg0FoxNc';
const cesiumBaseUrl = 'https://cdn.jsdelivr.net/npm/cesium@' + cesiumPackage.version + '/Build/Cesium/';
const loadingTimeoutMs = 60000;
const planePositionRefreshIntervalMs = 5 * 1000;
const planePositionActiveWindowMs = 20 * 60 * 1000;
const activitySampleIntervalMs = 200;
const activityCursorOverlapMs = 2 * 1000;
const activityQueueLogIntervalMs = 1000;
const realtimePlaybackStartupBufferMs = 2 * 1000;
const realtimePlaybackBufferMs = 10 * 1000;
const realtimePlaybackLowWaterMs = 7 * 1000;
const realtimePlaybackHighWaterMs = 12 * 1000;
const realtimePlaybackMinimumSpeedFactor = 0.35;
const realtimePlaybackMaximumSpeedFactor = 3.5;
const heartbeatPlaybackBufferMs = 0;
const heartbeatHeadingAlignmentMaxMs = 450;
const heartbeatHeadingAlignmentMinMs = 120;
const heartbeatHeadingAlignmentMinAngleDeg = 4;
const heartbeatTravelDurationFallbackMs = 5 * 1000;
const heartbeatTravelDurationMinMs = 700;
const heartbeatTravelDurationMaxMs = 6500;
const heartbeatMovementFreshWindowMs = 30 * 1000;
const heartbeatMovementFutureToleranceMs = 10 * 1000;
const heartbeatMovementMaxSampleGapMs = 20 * 1000;
const activityIdleStopMs = 20 * 60 * 1000;
const realtimePlaybackMinimumMovementMeters = 0.35;
const realtimePlaybackVisualSpeedMinMs = 2;
const realtimePlaybackVisualSpeedMaxMs = 120;
const realtimeSprayMistLineCount = 5;
const realtimeSprayMistMaxTrailPoints = 14;
const realtimeSprayMistUpdateIntervalMs = 120;
const realtimeSprayMistTrailSpacingMeters = 4.5;
const realtimeApplicationFrameBudgetMs = 3;
const realtimeApplicationVisualWidthFactor = 1;
const realtimeTurnBankMaxDeg = 35;
const realtimeTurnBankFactor = 3.2;
const realtimeTurnBankSmoothingSeconds = 0.18;
const propellerIdleStopDelayMs = 8 * 1000;
const heartbeatTeleportDistanceMeters = 1000;
const heartbeatStoppedSpeedKmh = 10;
const realtimeDiscontinuityMinimumDistanceMeters = 60;
const realtimeDiscontinuityMaximumSpeedMetersPerSecond = 140;
const realtimeDiscontinuityMaximumSampleGapMs = 3 * 1000;

const aircraftHeadingOffsetDeg = 270;
const weatherStationVehicleScale = 2.7;
const weatherStationVehicleMinimumPixelSize = 90;
const weatherStationVehicleMaximumScale = 250;
const weatherStationVehicleAltitudeOffsetMeters = 0;
const weatherStationVehicleHeadingOffsetDeg = 180;
const weatherStationVehiclePitchOffsetDeg = 0;
const weatherStationVehicleRollOffsetDeg = 0;
const weatherStationCoverageRadiusMeters = 5 * 1000;
const weatherStationCoverageDefaultFillAlpha = 0.05;
const weatherStationCoverageOutlineAlpha = 0.95;
const weatherStationCoverageOutlineWidth = 4;
const weatherStationWindTrackCount = 9;
const weatherStationWindArrowsPerTrack = 6;
const weatherStationWindArrowCount = weatherStationWindTrackCount * weatherStationWindArrowsPerTrack;
const weatherStationWindAnimationDurationMinMs = 7000;
const weatherStationWindAnimationDurationMaxMs = 45000;
const weatherStationWindCrossTrackFactor = 0.82;
const cameraRangeMinimumMeters = 50;
const cameraRangeMaximumMeters = 3000;
const aircraftCloseRangeScaleStartMeters = 700;
const aircraftCloseRangeScaleMinimumMeters = 50;
const aircraftCloseRangeMinimumScaleFactor = 0.18;
const parkedEntityOverlapThresholdMeters = 30;
const parkedEntityFirstRingRadiusMeters = 24;
const parkedEntityRingStepMeters = 18;
const parkedEntityItemsPerRing = 8;

const createWeatherStationWindArrowImage = () => {
  if (typeof document === 'undefined') return undefined;
  const canvas = document.createElement('canvas');
  canvas.width = 40;
  canvas.height = 40;
  const context = canvas.getContext('2d');
  if (!context) return canvas;

  context.clearRect(0, 0, canvas.width, canvas.height);
  context.translate(20, 20);
  context.fillStyle = '#f28c28';
  context.strokeStyle = '#7a3b00';
  context.lineWidth = 1.2;
  context.lineJoin = 'round';
  context.beginPath();
  context.moveTo(0, -15);
  context.lineTo(-7, 12);
  context.lineTo(0, 8);
  context.lineTo(7, 12);
  context.closePath();
  context.fill();
  context.stroke();
  return canvas;
};

const weatherStationWindArrowImage = createWeatherStationWindArrowImage();

const aircraftModels = [
  {
    id: 'emb200',
    label: 'EMB-200 Ipanema',
    url: emb200Model,
    scale: 0.2,
    modelHeadingOffsetDeg: aircraftHeadingOffsetDeg,
    modelPitchOffsetDeg: 90,
    modelRollOffsetDeg: 0,
    minimumPixelSize: 18,
    modelAltitudeOffsetMeters: 5,
    sprayMistVerticalOffsetMeters: 0.3,
    propellerGroups: [
      {
        id: 'helice_aviao',
        nodes: [
          'Cylinder32_pivot',
          'Cylinder104_pivot',
          'Cylinder108_pivot',
          'Cylinder01',
          'Cylinder31',
          'Cylinder301',
          'Cylinder102_pivot',
          'Cylinder106_pivot',
          'Cylinder110_pivot'
        ],
        axis: 'y',
        rpm: 920,
        spinSign: -1,
        pivot: [-0.05, -83.95, 60.03]
      },
      {
        id: 'bomba_eolica_cubo',
        nodes: ['Turbine_2_Spinner', 'Cylinder144', 'Cylinder136'],
        axis: 'y',
        rpm: 1283,
        spinSign: -1,
        pivot: [-23.242, -5.978, 33.37]
      },
      {
        id: 'bomba_eolica_pas',
        nodes: ['Cylinder145', 'Cylinder146', 'Cylinder147', 'Cylinder148', 'Cylinder149', 'Cylinder150', 'Cylinder151'],
        axis: 'y',
        rpm: 1319,
        spinSign: -1,
        pivot: [-23.242, -5.978, 33.37]
      }
    ]
  },
  {
    id: 'air-tractor-at-502b',
    label: 'Air Tractor AT-502B',
    url: airTractorAt502bModel,
    scale: 0.05,
    modelHeadingOffsetDeg: aircraftHeadingOffsetDeg,
    modelPitchOffsetDeg: -8,
    modelRollOffsetDeg: 0,
    minimumPixelSize: 18,
    modelAltitudeOffsetMeters: 13,
    sprayMistVerticalOffsetMeters: -8.5,
    propellerGroups: [
      {
        id: 'helice_aviao',
        nodes: ['objpropeller_basic_airtractor'],
        axis: 'z',
        rpm: 900,
        spinSign: -1,
        pivot: [-0.5831, 87.3068, 531.6978]
      },
      {
        id: 'bomba_eolica',
        nodes: ['objpropeller_small_airtractor'],
        axis: 'z',
        rpm: 1400,
        spinSign: -1,
        pivot: [-0.1409, -59.7121, 262.3489]
      }
    ]
  },
  {
    id: 'air-tractor-at-802',
    label: 'Air Tractor AT-802',
    url: airTractorAt802Model,
    scale: 0.125,
    modelHeadingOffsetDeg: aircraftHeadingOffsetDeg,
    modelPitchOffsetDeg: -8,
    modelRollOffsetDeg: 0,
    minimumPixelSize: 18,
    modelAltitudeOffsetMeters: 13,
    sprayMistVerticalOffsetMeters: -8.5,
    propellerGroups: [
      {
        id: 'helice_aviao',
        nodes: ['objpropeller_basic'],
        axis: 'z',
        rpm: 900,
        spinSign: -1,
        pivot: [-0.25, 40.8, 249.05]
      },
      {
        id: 'bomba_eolica',
        nodes: ['objpropeller_small'],
        axis: 'z',
        rpm: 1400,
        spinSign: -1,
        pivot: [-0.06608963012694957, -28.006233215331928, 123.04706573486402]
      }
    ]
  },
  {
    id: 'emb-203',
    label: 'EMB-203 Ipanema',
    url: emb203Model,
    scale: 50,
    modelHeadingOffsetDeg: 180,
    modelPitchOffsetDeg: 0,
    modelRollOffsetDeg: 0,
    minimumPixelSize: 18,
    modelAltitudeOffsetMeters: 10,
    sprayMistVerticalOffsetMeters: -7,
    propellerGroups: [
      {
        id: 'helice_aviao',
        nodes: [
          'emb203_helice_Cylinder32_pivot',
          'emb203_helice_Cylinder104_pivot',
          'emb203_helice_Cylinder108_pivot',
          'emb203_helice_Cylinder01',
          'emb203_helice_Cylinder31',
          'emb203_helice_Cylinder301',
          'emb203_helice_Cylinder102_pivot',
          'emb203_helice_Cylinder106_pivot',
          'emb203_helice_Cylinder110_pivot'
        ],
        axis: 'x',
        rpm: 780,
        spinSign: -1,
        pivot: [-0.653, 0.09, 0]
      }
    ]
  },
  {
    id: 'thrush-510',
    label: 'Thrush 510',
    url: thrush510Model,
    scale: 40,
    modelHeadingOffsetDeg: 180,
    modelPitchOffsetDeg: 0,
    modelRollOffsetDeg: 0,
    minimumPixelSize: 18,
    modelAltitudeOffsetMeters: 5,
    sprayMistVerticalOffsetMeters: -8.5,
    sprayMistBackOffsetMeters: 1.5,
    propellerGroups: [
      {
        id: 'helice_aviao',
        nodes: ['thrush510_helice_objpropeller_basic_airtractor'],
        axis: 'x',
        rpm: 780,
        spinSign: -1,
        pivot: [-0.736658, -0.049432, -0.000088],
        translationOffset: [0.095, 0.004, 0],
        scale: [1, 1.25, 1.25],
        tiltAxis: 'z',
        tiltDeg: 6
      },
      {
        id: 'bomba_eolica',
        nodes: ['thrush510_bomba_eolica_objpropeller_small_airtractor'],
        axis: 'x',
        rpm: 1200,
        spinSign: -1,
        pivot: [-0.447026, -0.206878, 0.000396]
      }
    ]
  },
  {
    id: 'cessna-188',
    label: 'Cessna 188 AG',
    url: cessna188Model,
    scale: 1.5,

    modelHeadingOffsetDeg: (aircraftHeadingOffsetDeg + 180) % 360,
    modelPitchOffsetDeg: -8,
    modelRollOffsetDeg: 0,
    minimumPixelSize: 18,
    modelAltitudeOffsetMeters: 5,
    sprayMistVerticalOffsetMeters: 0.3,
    propellerGroups: [
      {
        id: 'helice_aviao',
        nodes: [
          'cessna_188_ag_ipanema_propeller_Cylinder32_pivot',
          'cessna_188_ag_ipanema_propeller_Cylinder104_pivot',
          'cessna_188_ag_ipanema_propeller_Cylinder108_pivot',
          'cessna_188_ag_ipanema_propeller_Cylinder01',
          'cessna_188_ag_ipanema_propeller_Cylinder31',
          'cessna_188_ag_ipanema_propeller_Cylinder301',
          'cessna_188_ag_ipanema_propeller_Cylinder102_pivot',
          'cessna_188_ag_ipanema_propeller_Cylinder106_pivot',
          'cessna_188_ag_ipanema_propeller_Cylinder110_pivot'
        ],
        axis: 'z',
        rpm: 900,
        spinSign: -1,
        pivot: [0.002090245485305786, 7.712601900100708, -17.66800308227539]
      }
    ]
  }
];

const toNumber = (value, defaultValue) => {
  const numberValue = Number(value);
  return isNaN(numberValue) ? defaultValue : numberValue;
};

class Map3D extends React.Component {
  aircraftBaseOrientation = new Cesium.Quaternion();
  aircraftModelOrientation = new Cesium.Quaternion();
  aircraftLevelOrientation = new Cesium.Quaternion();
  aircraftBankOrientation = new Cesium.Quaternion();
  aircraftFinalOrientation = new Cesium.Quaternion();
  aircraftFlightHpr = new Cesium.HeadingPitchRoll();
  aircraftModelHpr = new Cesium.HeadingPitchRoll();
  aircraftEnuTransform = new Cesium.Matrix4();
  aircraftEnuRotation = new Cesium.Matrix3();
  aircraftForwardEnu = new Cesium.Cartesian3();
  aircraftForwardWorld = new Cesium.Cartesian3();
  aircraftOrientationByPlaneId = {};
  pendingCameraRangeStateFrame = null;
  pendingCameraRangeStateValue = null;
  planeCameraRangeMeters = 1800;

  state = {
    fullscreen: false,
    panelCollapsed: false,
    mobileCompact: typeof window !== 'undefined' && (
      window.innerWidth <= 540 ||
      (window.innerWidth <= 980 && window.innerHeight <= 540)
    ),
    loading: true,
    jobs: [],
    planes: [],
    message: '',
    currentJobIndex: 0,
    currentPlaneIndex: 0,
    currentStationIndex: 0,
    jobFocusFilter: 'all',
    planeFocusFilter: 'all',
    stationFocusFilter: 'all',
    itemSelectorOpen: null,
    hiddenJobFocusKeys: {},
    hiddenPlaneIds: {},
    hiddenStationIds: {},
    desktopExecutedStationVisible: false,
    desktopStationManuallyHidden: false,
    freeMode: false,
    cameraFocusType: 'job',
    cameraHeadingDeg: 0,
    cameraPitchDeg: -54,
    cameraRangeMeters: 1800,
    weatherStationCoverageOpacity: weatherStationCoverageDefaultFillAlpha,
    focusItems: [],
    planeFocusItems: [],
    stationFocusItems: [],
    positionsByPlane: {}
  };

  mounted = false;
  loaded = false;
  refreshTimer = null;
  planePositionTimer = null;
  nextPlanePositionRefreshAt = 0;
  planePositionRequestInProgress = false;
  planePositionsById = {};
  initialPlaneFocusOrderIds = null;
  jobEventCursorByPlaneId = {};
  jobEventHistoryLoadedByPlaneId = {};
  jobEventHistoryRequestByPlaneId = {};
  jobEventSegmentsByPlaneId = {};
  jobEventEntitiesByPlaneId = {};
  realtimePlaneIds = {};
  realtimeJobIdsByPlaneId = {};
  jobEventLastPositionByPlaneId = {};
  realtimeExecutionsByKey = {};
  consolidatedThroughByPlaneId = {};
  planeOnlineStateByPlaneId = {};
  aircraftEntitiesByPlaneId = {};
  weatherStationEntitiesById = {};
  weatherStationCoverageEntitiesById = {};
  weatherStationWindEntitiesById = {};
  weatherStationVisualStateById = {};
  jobVisualEntitiesByKey = {};
  jobSelectionOutlineEntitiesByKey = {};
  parkedDisplayPointByKey = {};
  aircraftMotionByPlaneId = {};
  aircraftAnimationFrameByPlaneId = {};
  aircraftPlaybackQueueByPlaneId = {};
  aircraftPropellerStartedAtByPlaneId = {};
  aircraftPropellerRuntimeByPlaneId = {};
  aircraftPropellerFlyingByPlaneId = {};
  aircraftPropellerIdleTimerByPlaneId = {};
  pendingApplicationUpdateTimerByPlaneId = {};
  pendingApplicationRectanglesByPlaneId = {};
  pendingApplicationFrame = null;
  pendingApplicationPlaneCursor = 0;
  aircraftBankRuntimeByPlaneId = {};
  pendingFocusRefreshTimerByPlaneId = {};
  playbackTimelineOffsetByPlaneId = {};
  pendingReplayActivitiesByPlaneId = {};
  smoothedTelemetrySpeedByPlaneId = {};
  visualPlaybackSpeedByPlaneId = {};
  playbackStartedByPlaneId = {};
  playbackSourceByPlaneId = {};
  playbackFinalizingByPlaneId = {};
  activityMonitoringByPlaneId = {};
  activityMonitoringStartedAtByPlaneId = {};
  activityLastReceivedAtByPlaneId = {};
  activityBootstrapCheckedByPlaneId = {};
  activityBootstrapPendingByPlaneId = {};
  lastEffectiveSourceTimeByPlaneId = {};
  activityOrderIdsByPlaneId = {};
  activityTimingStateByPlaneId = {};
  activityRequestLogByPlaneId = {};
  activityQueueLogByPlaneId = {};
  activityLastResponseAtByPlaneId = {};
  activityLastProgressAtByPlaneId = {};
  activityActiveSampleByPlaneId = {};
  heartbeatLastResponseAtByPlaneId = {};
  heartbeatLastMovementSampleByPlaneId = {};
  queueSpeedFactorByPlaneId = {};
  sprayMistRuntimeByPlaneId = {};
  jobAreas = {};
  jobAreaRequests = {};
  planningJobs = {};
  planningJobRequests = {};
  jobResults = {};
  jobResultRequests = {};
  applicationReports = {};
  applicationReportRequests = {};
  telemetryReports = {};
  jobClimateSummaries = {};
  jobClimateSummaryRequests = {};
  telemetryReportRequests = {};
  cameraInteractionHandler = null;
  cameraFollowEnabled = false;
  cameraFollowPlaneId = 0;
  cameraFollowRangeMeters = 1800;
  cameraFollowSmoothedPositionByPlaneId = {};
  cameraFollowLastUpdatedAtByPlaneId = {};
  cameraManualInteractionActive = false;
  cameraManualInteractionSource = null;
  cameraFocusNavigationDetached = false;
  cameraPinchPreviousZoomEnabled = null;
  freeModePanActive = false;
  freeModePanLastPosition = null;
  loadingTimer = null;
  loadingTilesTimer = null;
  loadingContentTimer = null;
  loadingStartedAt = 0;
  initialDataReady = false;
  initialTilesReady = false;
  initialLoadComplete = false;
  initialCameraFocusApplied = false;
  tileLoadProgressListener = null;
  viewer = null;
  focusItems = [];
  aircraftEntities = [];
  containerId = 'onlineFarmCesiumContainer_' + Date.now();
  flightCompassArrowElement = null;
  flightCompassHeadingElement = null;
  screenCompassElements = {};
  screenCompassPostRenderListener = null;
  screenCompassMoveEndListener = null;


  componentDidMount() {
    this.mounted = true;
    this.configureCesiumRuntime();
    if (this.props.hasWeatherStations) this.preloadWeatherStationVehicleModel();
    document.addEventListener('fullscreenchange', this.handleFullscreenChange);
    window.addEventListener('resize', this.handleViewportResize);
    this.startInitialLoading();
    setTimeout(this.boot, 0);
  }

  componentDidUpdate(prevProps, prevState) {
    if (prevProps.jobs !== this.props.jobs || prevProps.planes !== this.props.planes || prevProps.weatherStations !== this.props.weatherStations || prevProps.hasWeatherStations !== this.props.hasWeatherStations || prevProps.initialDataLoaded !== this.props.initialDataLoaded || prevProps.realTime3D !== this.props.realTime3D) {
      this.loadMapData();
    }

    if (!prevProps.hasWeatherStations && this.props.hasWeatherStations) {
      this.preloadWeatherStationVehicleModel();
    }

    if (
      prevState.currentJobIndex !== this.state.currentJobIndex ||
      prevState.cameraFocusType !== this.state.cameraFocusType
    ) {
      this.updateSelectedJobHighlight();
    }

    if (prevState.panelCollapsed !== this.state.panelCollapsed && this.viewer && !this.viewer.isDestroyed()) {
      setTimeout(() => {
        if (this.viewer && !this.viewer.isDestroyed()) {
          this.viewer.resize();
          if (!this.state.freeMode) this.applyCameraControls();
          this.viewer.scene.requestRender();
        }
      }, 240);
    }
  }

  componentWillUnmount() {
    this.mounted = false;
    if (this.refreshTimer) clearTimeout(this.refreshTimer);
    if (this.planePositionTimer) clearTimeout(this.planePositionTimer);
    if (this.loadingTimer) clearTimeout(this.loadingTimer);
    if (this.loadingTilesTimer) clearTimeout(this.loadingTilesTimer);
    if (this.loadingContentTimer) clearTimeout(this.loadingContentTimer);
    Object.keys(this.aircraftAnimationFrameByPlaneId || {}).forEach((idPlane) => {
      cancelAnimationFrame(this.aircraftAnimationFrameByPlaneId[idPlane]);
    });
    this.aircraftAnimationFrameByPlaneId = {};
    this.aircraftOrientationByPlaneId = {};
    if (this.pendingCameraRangeStateFrame) cancelAnimationFrame(this.pendingCameraRangeStateFrame);
    this.pendingCameraRangeStateFrame = null;
    this.pendingCameraRangeStateValue = null;
    this.aircraftMotionByPlaneId = {};
    this.aircraftPlaybackQueueByPlaneId = {};
    this.aircraftPropellerStartedAtByPlaneId = {};
    Object.keys(this.aircraftPropellerIdleTimerByPlaneId || {}).forEach((idPlane) => clearTimeout(this.aircraftPropellerIdleTimerByPlaneId[idPlane]));
    this.aircraftPropellerRuntimeByPlaneId = {};
    this.aircraftPropellerFlyingByPlaneId = {};
    this.aircraftPropellerIdleTimerByPlaneId = {};
    Object.keys(this.pendingApplicationUpdateTimerByPlaneId || {}).forEach((id) => clearTimeout(this.pendingApplicationUpdateTimerByPlaneId[id]));
    Object.keys(this.pendingFocusRefreshTimerByPlaneId || {}).forEach((id) => clearTimeout(this.pendingFocusRefreshTimerByPlaneId[id]));
    this.pendingApplicationUpdateTimerByPlaneId = {};
    this.pendingApplicationRectanglesByPlaneId = {};
    if (this.pendingApplicationFrame) cancelAnimationFrame(this.pendingApplicationFrame);
    this.pendingApplicationFrame = null;
    this.aircraftBankRuntimeByPlaneId = {};
    this.pendingFocusRefreshTimerByPlaneId = {};
    this.playbackTimelineOffsetByPlaneId = {};
    this.pendingReplayActivitiesByPlaneId = {};
    this.smoothedTelemetrySpeedByPlaneId = {};
    this.visualPlaybackSpeedByPlaneId = {};
    this.playbackStartedByPlaneId = {};
    this.playbackSourceByPlaneId = {};
    this.playbackFinalizingByPlaneId = {};
    this.activityMonitoringByPlaneId = {};
    this.activityMonitoringStartedAtByPlaneId = {};
    this.activityLastReceivedAtByPlaneId = {};
    this.activityBootstrapCheckedByPlaneId = {};
    this.activityBootstrapPendingByPlaneId = {};
    this.lastEffectiveSourceTimeByPlaneId = {};
    this.activityOrderIdsByPlaneId = {};
    this.activityTimingStateByPlaneId = {};
    this.activityRequestLogByPlaneId = {};
    this.activityQueueLogByPlaneId = {};
    this.activityLastResponseAtByPlaneId = {};
    this.activityLastProgressAtByPlaneId = {};
    this.activityActiveSampleByPlaneId = {};
    this.heartbeatLastResponseAtByPlaneId = {};
    this.heartbeatLastMovementSampleByPlaneId = {};
    this.queueSpeedFactorByPlaneId = {};
    this.cameraFollowSmoothedPositionByPlaneId = {};
    this.cameraFollowLastUpdatedAtByPlaneId = {};
    this.removeScreenCompassCameraListeners();
    this.removeCameraInteractionHandler();
    this.removeInitialTilesListener();
    Object.keys(this.sprayMistRuntimeByPlaneId || {}).forEach((idPlane) => this.clearRealtimeSprayMist(idPlane));
    this.sprayMistRuntimeByPlaneId = {};
    this.jobAreas = {};
    this.jobAreaRequests = {};
    this.jobResults = {};
    this.jobResultRequests = {};
    this.applicationReports = {};
    this.applicationReportRequests = {};
    this.telemetryReports = {};
    this.jobClimateSummaries = {};
    this.jobClimateSummaryRequests = {};
    this.telemetryReportRequests = {};
    document.removeEventListener('fullscreenchange', this.handleFullscreenChange);
    window.removeEventListener('resize', this.handleViewportResize);
    if (this.viewer && !this.viewer.isDestroyed()) this.viewer.destroy();
    this.viewer = null;
    this.planePositionTimer = null;
    this.nextPlanePositionRefreshAt = 0;
    this.planePositionRequestInProgress = false;
    this.jobEventCursorByPlaneId = {};
    this.jobEventHistoryLoadedByPlaneId = {};
    this.jobEventHistoryRequestByPlaneId = {};
    this.jobEventSegmentsByPlaneId = {};
    this.jobEventEntitiesByPlaneId = {};
    this.realtimePlaneIds = {};
    this.realtimeJobIdsByPlaneId = {};
    this.jobEventLastPositionByPlaneId = {};
    this.realtimeExecutionsByKey = {};
    this.consolidatedThroughByPlaneId = {};
  }

  configureCesiumRuntime = () => {
    window.CESIUM_BASE_URL = cesiumBaseUrl;
    window.Cesium = Cesium;
    Cesium.Ion.defaultAccessToken = cesiumIonToken;
  };

  preloadWeatherStationVehicleModel = () => {
    if (typeof fetch !== 'function' || !weatherStationVehicleModel) return;

    fetch(weatherStationVehicleModel, { cache: 'force-cache' })
      .then((response) => {
        if (!response || !response.ok) return null;
        return response.arrayBuffer();
      })
      .catch(() => null);
  };

  boot = () => {
    if (!this.mounted || this.viewer) return;

    const target = document.getElementById(this.containerId);
    if (!target) return;

    this.viewer = new Cesium.Viewer(this.containerId, {
      shouldAnimate: false,
      animation: false,
      timeline: false,
      geocoder: false,
      homeButton: false,
      sceneModePicker: false,
      navigationHelpButton: false,
      baseLayerPicker: false,
      infoBox: false,
      selectionIndicator: false,
      shadows: false,
      terrainShadows: Cesium.ShadowMode.DISABLED,
      fullscreenButton: false,
      requestRenderMode: false
    });

    this.viewer.scene.rethrowRenderErrors = false;
    this.viewer.useBrowserRecommendedResolution = true;
    this.viewer.targetFrameRate = 60;
    this.viewer.resolutionScale = 0.85;

    if (this.viewer.scene && this.viewer.scene.globe) {
      this.viewer.scene.globe.maximumScreenSpaceError = 3;
      this.viewer.scene.globe.preloadAncestors = true;
      this.viewer.scene.globe.preloadSiblings = true;
    }

    const controller = this.viewer.scene && this.viewer.scene.screenSpaceCameraController;
    if (controller) {
      controller.enableRotate = true;
      controller.enableZoom = true;
      controller.enableTranslate = true;
      controller.enableTilt = true;
      controller.enableLook = true;
    }

    this.applyBasemap();
    this.installCameraInteractionHandler();
    this.installScreenCompassCameraListeners();
    this.installInitialTilesListener();
    this.loadMapData();
  };

  normalizeCameraHeadingForSlider = (headingDeg) => {
    const normalized = ((toNumber(headingDeg, 0) + 180) % 360 + 360) % 360 - 180;
    return Math.abs(normalized + 180) < 0.0001 ? 180 : normalized;
  };

  getScreenCompassPosition = (worldBearingDeg) => {
    const cameraHeadingDeg = this.viewer && !this.viewer.isDestroyed()
      ? Cesium.Math.toDegrees(this.viewer.camera.heading)
      : this.state.cameraHeadingDeg;
    const screenBearingRad = Cesium.Math.toRadians(worldBearingDeg - cameraHeadingDeg);
    const directionX = Math.sin(screenBearingRad);
    const directionY = -Math.cos(screenBearingRad);
    const halfWidthPercent = 48.2;
    const halfHeightPercent = 47.2;
    const scaleX = Math.abs(directionX) > 0.000001
      ? halfWidthPercent / Math.abs(directionX)
      : Number.POSITIVE_INFINITY;
    const scaleY = Math.abs(directionY) > 0.000001
      ? halfHeightPercent / Math.abs(directionY)
      : Number.POSITIVE_INFINITY;
    const edgeScale = Math.min(scaleX, scaleY);

    return {
      left: 50 + (directionX * edgeScale),
      top: 50 + (directionY * edgeScale)
    };
  };

  updateScreenCompassCardinals = () => {
    if (!this.viewer || this.viewer.isDestroyed()) return;
    const bearings = { north: 0, east: 90, south: 180, west: 270 };
    Object.keys(bearings).forEach((key) => {
      const element = this.screenCompassElements[key];
      if (!element) return;
      const position = this.getScreenCompassPosition(bearings[key]);
      element.style.left = position.left + '%';
      element.style.top = position.top + '%';
    });
  };

  syncCameraHeadingFromViewer = () => {
    if (!this.viewer || this.viewer.isDestroyed()) return;
    const headingDeg = this.normalizeCameraHeadingForSlider(Cesium.Math.toDegrees(this.viewer.camera.heading));
    if (Math.abs(headingDeg - this.state.cameraHeadingDeg) >= 0.5) {
      this.setState({ cameraHeadingDeg: headingDeg });
    }
  };

  installScreenCompassCameraListeners = () => {
    if (!this.viewer || this.viewer.isDestroyed() || !this.viewer.scene) return;
    this.removeScreenCompassCameraListeners();

    this.screenCompassPostRenderListener = () => this.updateScreenCompassCardinals();
    this.viewer.scene.postRender.addEventListener(this.screenCompassPostRenderListener);

    this.screenCompassMoveEndListener = () => this.syncCameraHeadingFromViewer();
    this.viewer.camera.moveEnd.addEventListener(this.screenCompassMoveEndListener);
    this.updateScreenCompassCardinals();
  };

  removeScreenCompassCameraListeners = () => {
    if (this.viewer && !this.viewer.isDestroyed()) {
      if (this.screenCompassPostRenderListener && this.viewer.scene) {
        this.viewer.scene.postRender.removeEventListener(this.screenCompassPostRenderListener);
      }
      if (this.screenCompassMoveEndListener && this.viewer.camera) {
        this.viewer.camera.moveEnd.removeEventListener(this.screenCompassMoveEndListener);
      }
    }
    this.screenCompassPostRenderListener = null;
    this.screenCompassMoveEndListener = null;
  };

  isFollowingPlaneCamera = () => (
    this.cameraFollowEnabled &&
    !this.state.freeMode &&
    this.state.cameraFocusType === 'plane' &&
    toNumber(this.cameraFollowPlaneId, 0) > 0
  );

  getCollapsedMobileFocusOffsetRatio = () => {
    if (
      !this.isMobileCompactViewport() ||
      this.state.freeMode
    ) {
      return 0;
    }

    return 0.1;
  };

  applyCollapsedMobileFocusOffset = (rangeMeters) => {
    if (!this.viewer || this.viewer.isDestroyed()) return;

    const ratio = this.getCollapsedMobileFocusOffsetRatio();
    if (!ratio) return;

    const safeRange = Math.max(cameraRangeMinimumMeters, Math.min(cameraRangeMaximumMeters, toNumber(rangeMeters, this.state.cameraRangeMeters)));
    this.viewer.camera.moveRight(safeRange * ratio);
  };

  normalizeCollapsedMobileCameraRange = (rangeMeters) => {
    const ratio = this.getCollapsedMobileFocusOffsetRatio();
    if (!ratio || !Number.isFinite(rangeMeters)) return rangeMeters;

    return rangeMeters / Math.sqrt(1 + (ratio * ratio));
  };

  beginManualCameraInteraction = (source) => {
    if (!this.viewer || this.viewer.isDestroyed() || this.state.freeMode) return;

    this.cameraManualInteractionActive = true;
    this.cameraManualInteractionSource = source || 'manual';
    this.cameraFocusNavigationDetached = true;

    // Mantém o item selecionado, mas solta a câmera do ponto de foco para que
    // mouse e touch possam navegar normalmente sem entrar no Modo livre.
    this.cameraFollowEnabled = false;
    this.cameraFollowPlaneId = 0;
    this.viewer.camera.lookAtTransform(Cesium.Matrix4.IDENTITY);

    const controller = this.viewer.scene && this.viewer.scene.screenSpaceCameraController;
    if (controller) {
      controller.enableRotate = true;
      controller.enableTranslate = true;
      controller.enableZoom = true;
      controller.enableTilt = true;
      controller.enableLook = true;
    }

    this.viewer.scene.requestRender();
  };

  finishManualCameraInteraction = () => {
    if (!this.cameraManualInteractionActive) return;
    if (!this.viewer || this.viewer.isDestroyed()) {
      this.cameraManualInteractionActive = false;
      this.cameraManualInteractionSource = null;
      return;
    }

    window.setTimeout(() => {
      if (!this.mounted || !this.viewer || this.viewer.isDestroyed()) return;

      const focusPosition = this.getCameraFocusPosition();
      const camera = this.viewer.camera;
      const headingDeg = Cesium.Math.toDegrees(camera.heading);
      const pitchDeg = Cesium.Math.toDegrees(camera.pitch);
      const measuredRangeMeters = focusPosition
        ? Cesium.Cartesian3.distance(camera.positionWC, focusPosition)
        : this.state.cameraRangeMeters;
      const normalizedRangeMeters = this.normalizeCollapsedMobileCameraRange(measuredRangeMeters);
      const rangeMeters = Math.max(cameraRangeMinimumMeters, Math.min(cameraRangeMaximumMeters, normalizedRangeMeters));

      this.cameraFollowRangeMeters = rangeMeters;
      this.planeCameraRangeMeters = rangeMeters;

      this.setState({
        cameraHeadingDeg: Number.isFinite(headingDeg) ? headingDeg : this.state.cameraHeadingDeg,
        cameraPitchDeg: Number.isFinite(pitchDeg) ? pitchDeg : this.state.cameraPitchDeg,
        cameraRangeMeters: rangeMeters
      }, () => {
        this.cameraManualInteractionActive = false;
        this.cameraManualInteractionSource = null;
        this.cameraFollowLastUpdatedAtByPlaneId[this.cameraFollowPlaneId] = performance.now();
      });
    }, 0);
  };

  setFreeModeCameraNavigation = (enabled) => {
    if (!this.viewer || this.viewer.isDestroyed() || !this.viewer.scene) return;

    const controller = this.viewer.scene.screenSpaceCameraController;

    if (enabled) {
      this.viewer.camera.lookAtTransform(Cesium.Matrix4.IDENTITY);
      if (controller) {
        controller.enableRotate = false;
        controller.enableTranslate = true;
        controller.enableZoom = true;
        controller.enableTilt = true;
        controller.enableLook = true;
      }
    } else if (controller) {
      controller.enableRotate = true;
    }

    this.freeModePanActive = false;
    this.freeModePanLastPosition = null;
    this.viewer.scene.requestRender();
  };

  beginFreeModePan = (movement) => {
    if (!this.state.freeMode || !movement || !movement.position) return;

    this.freeModePanActive = true;
    this.freeModePanLastPosition = Cesium.Cartesian2.clone(movement.position, new Cesium.Cartesian2());
  };

  finishFreeModePan = () => {
    this.freeModePanActive = false;
    this.freeModePanLastPosition = null;
  };

  panFreeModeCamera = (movement) => {
    if (
      !this.state.freeMode ||
      !this.freeModePanActive ||
      !this.freeModePanLastPosition ||
      !movement ||
      !movement.endPosition ||
      !this.viewer ||
      this.viewer.isDestroyed()
    ) {
      return;
    }

    const scene = this.viewer.scene;
    const camera = this.viewer.camera;
    const globe = scene && scene.globe;
    if (!globe) return;

    const previousRay = camera.getPickRay(this.freeModePanLastPosition);
    const currentRay = camera.getPickRay(movement.endPosition);
    const previousPosition = previousRay ? globe.pick(previousRay, scene) : null;
    const currentPosition = currentRay ? globe.pick(currentRay, scene) : null;

    if (previousPosition && currentPosition) {
      const delta = Cesium.Cartesian3.subtract(previousPosition, currentPosition, new Cesium.Cartesian3());
      Cesium.Cartesian3.add(camera.position, delta, camera.position);
      scene.requestRender();
    }

    this.freeModePanLastPosition = Cesium.Cartesian2.clone(
      movement.endPosition,
      this.freeModePanLastPosition
    );
  };

  installCameraInteractionHandler = () => {
    if (!this.viewer || !this.viewer.scene || this.cameraInteractionHandler) return;

    this.cameraInteractionHandler = new Cesium.ScreenSpaceEventHandler(this.viewer.scene.canvas);

    this.cameraInteractionHandler.setInputAction(
      (movement) => {
        if (this.state.freeMode) this.beginFreeModePan(movement);
        else this.beginManualCameraInteraction('left');
      },
      Cesium.ScreenSpaceEventType.LEFT_DOWN
    );
    this.cameraInteractionHandler.setInputAction(
      () => {
        if (this.state.freeMode) this.finishFreeModePan();
        else this.finishManualCameraInteraction();
      },
      Cesium.ScreenSpaceEventType.LEFT_UP
    );
    this.cameraInteractionHandler.setInputAction(
      this.panFreeModeCamera,
      Cesium.ScreenSpaceEventType.MOUSE_MOVE
    );
    this.cameraInteractionHandler.setInputAction(
      () => this.beginManualCameraInteraction('middle'),
      Cesium.ScreenSpaceEventType.MIDDLE_DOWN
    );
    this.cameraInteractionHandler.setInputAction(
      this.finishManualCameraInteraction,
      Cesium.ScreenSpaceEventType.MIDDLE_UP
    );
    this.cameraInteractionHandler.setInputAction(
      () => this.beginManualCameraInteraction('right'),
      Cesium.ScreenSpaceEventType.RIGHT_DOWN
    );
    this.cameraInteractionHandler.setInputAction(
      this.finishManualCameraInteraction,
      Cesium.ScreenSpaceEventType.RIGHT_UP
    );
    this.cameraInteractionHandler.setInputAction(
      (movement) => this.handleMapEntityClick(movement && movement.position),
      Cesium.ScreenSpaceEventType.LEFT_CLICK
    );

    // No touch, o Cesium já trata pinch/arraste/rotação. Aqui apenas soltamos
    // o foco antes do gesto para não disputar a câmera com o acompanhamento.
    this.cameraInteractionHandler.setInputAction(
      () => this.beginManualCameraInteraction('pinch'),
      Cesium.ScreenSpaceEventType.PINCH_START
    );
    this.cameraInteractionHandler.setInputAction(
      this.finishManualCameraInteraction,
      Cesium.ScreenSpaceEventType.PINCH_END
    );

    this.cameraInteractionHandler.setInputAction(() => {
      if (!this.state.freeMode && !this.cameraFocusNavigationDetached) {
        this.beginManualCameraInteraction('wheel');
      }
      window.setTimeout(() => {
        this.syncCameraRangeFromViewer();
        this.finishManualCameraInteraction();
      }, 0);
    }, Cesium.ScreenSpaceEventType.WHEEL);
  };

  removeCameraInteractionHandler = () => {
    const controller = this.viewer && !this.viewer.isDestroyed() && this.viewer.scene
      ? this.viewer.scene.screenSpaceCameraController
      : null;

    if (controller && this.cameraPinchPreviousZoomEnabled !== null) {
      controller.enableZoom = this.cameraPinchPreviousZoomEnabled;
    }

    this.cameraPinchPreviousZoomEnabled = null;

    if (this.cameraInteractionHandler && !this.cameraInteractionHandler.isDestroyed()) {
      this.cameraInteractionHandler.destroy();
    }
    this.cameraInteractionHandler = null;
    this.cameraFollowEnabled = false;
    this.cameraFollowPlaneId = 0;
    this.cameraManualInteractionActive = false;
    this.cameraManualInteractionSource = null;
  };

  updateFollowCamera = (idPlane, position, forcePosition = false) => {
    if (!this.cameraFollowEnabled || toNumber(this.cameraFollowPlaneId, 0) !== toNumber(idPlane, 0)) return;
    if (!this.viewer || this.viewer.isDestroyed() || !position) return;
    if (this.state.freeMode || this.state.cameraFocusType !== 'plane') return;

    const now = performance.now();
    const previousAt = toNumber(this.cameraFollowLastUpdatedAtByPlaneId[idPlane], now);
    const elapsedSeconds = Math.max(0.001, Math.min(0.1, (now - previousAt) / 1000));
    const previousPosition = this.cameraFollowSmoothedPositionByPlaneId[idPlane];

    let followHeadingDeg = this.state.cameraHeadingDeg;
    let followPitchDeg = this.state.cameraPitchDeg;
    let followRangeMeters = Number.isFinite(this.cameraFollowRangeMeters)
      ? this.cameraFollowRangeMeters
      : this.state.cameraRangeMeters;

    const controller = this.viewer && this.viewer.scene
      ? this.viewer.scene.screenSpaceCameraController
      : null;
    if (controller && controller.enableZoom) controller.enableZoom = false;

    if (this.cameraManualInteractionActive) {
      const camera = this.viewer.camera;
      const currentHeadingDeg = Cesium.Math.toDegrees(camera.heading);
      const currentPitchDeg = Cesium.Math.toDegrees(camera.pitch);
      const currentTarget = previousPosition || position;
      const measuredRangeMeters = currentTarget
        ? Cesium.Cartesian3.distance(camera.positionWC, currentTarget)
        : this.state.cameraRangeMeters;
      const currentRangeMeters = this.normalizeCollapsedMobileCameraRange(measuredRangeMeters);

      if (Number.isFinite(currentHeadingDeg)) followHeadingDeg = currentHeadingDeg;
      if (Number.isFinite(currentPitchDeg)) followPitchDeg = currentPitchDeg;
      if (Number.isFinite(currentRangeMeters)) {
        followRangeMeters = Math.max(cameraRangeMinimumMeters, Math.min(cameraRangeMaximumMeters, currentRangeMeters));
      }

      this.cameraFollowRangeMeters = followRangeMeters;
      this.planeCameraRangeMeters = followRangeMeters;
    }

    const smoothedPosition = forcePosition
      ? Cesium.Cartesian3.clone(position)
      : previousPosition
        ? Cesium.Cartesian3.lerp(
          previousPosition,
          position,
          1 - Math.exp(-elapsedSeconds / 0.12),
          previousPosition
        )
        : Cesium.Cartesian3.clone(position);

    this.cameraFollowSmoothedPositionByPlaneId[idPlane] = smoothedPosition;
    this.cameraFollowLastUpdatedAtByPlaneId[idPlane] = now;

    this.viewer.camera.lookAt(
      smoothedPosition,
      new Cesium.HeadingPitchRange(
        Cesium.Math.toRadians(followHeadingDeg),
        Cesium.Math.toRadians(followPitchDeg),
        followRangeMeters
      )
    );
    this.applyCollapsedMobileFocusOffset(followRangeMeters);
  };

  applyBasemap = () => {
    if (!this.viewer) return;

    if (Cesium.ArcGisMapServerImageryProvider && Cesium.ArcGisMapServerImageryProvider.fromUrl) {
      Cesium.ArcGisMapServerImageryProvider.fromUrl('https://services.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer')
        .then((provider) => {
          if (!this.viewer || this.viewer.isDestroyed()) return;
          this.viewer.imageryLayers.removeAll();
          this.viewer.imageryLayers.addImageryProvider(provider);
          this.viewer.scene.requestRender();
        })
        .catch(() => { });
    }
  };

  startInitialLoading = () => {
    if (!this.mounted) return;

    if (this.loadingTimer) clearTimeout(this.loadingTimer);
    if (this.loadingTilesTimer) clearTimeout(this.loadingTilesTimer);

    this.loadingStartedAt = Date.now();
    this.initialDataReady = false;
    this.initialTilesReady = false;
    this.initialLoadComplete = false;

    if (!this.state.loading) {
      this.setState({ loading: true });
    }

    this.loadingTimer = setTimeout(() => {
      this.finishInitialLoading();
    }, loadingTimeoutMs);
  };

  installInitialTilesListener = () => {
    if (!this.viewer || !this.viewer.scene || !this.viewer.scene.globe || !this.viewer.scene.globe.tileLoadProgressEvent) return;
    if (this.tileLoadProgressListener) return;

    this.tileLoadProgressListener = (remainingTiles) => {
      if (remainingTiles === 0) {
        this.initialTilesReady = true;
        this.tryHideInitialLoading();
      }
    };

    this.viewer.scene.globe.tileLoadProgressEvent.addEventListener(this.tileLoadProgressListener);
  };

  removeInitialTilesListener = () => {
    if (this.viewer && this.viewer.scene && this.viewer.scene.globe && this.viewer.scene.globe.tileLoadProgressEvent && this.tileLoadProgressListener) {
      this.viewer.scene.globe.tileLoadProgressEvent.removeEventListener(this.tileLoadProgressListener);
    }
    this.tileLoadProgressListener = null;
  };

  markInitialDataReady = () => {
    this.initialDataReady = true;

    if (this.viewer && this.viewer.scene && this.viewer.scene.globe && this.viewer.scene.globe.tilesLoaded) {
      this.initialTilesReady = true;
    }

    if (this.loadingTilesTimer) clearTimeout(this.loadingTilesTimer);
    this.loadingTilesTimer = setTimeout(() => {
      if (this.viewer && this.viewer.scene && this.viewer.scene.globe && this.viewer.scene.globe.tilesLoaded) {
        this.initialTilesReady = true;
      }
      this.tryHideInitialLoading();
    }, 900);

    this.tryHideInitialLoading();
  };

  hasPendingInitialContent = () => {
    if (Object.keys(this.jobEventHistoryRequestByPlaneId || {}).length > 0) return true;
    if (this.pendingApplicationFrame) return true;
    const planeIds = Object.keys(this.pendingApplicationRectanglesByPlaneId || {});
    for (let i = 0; i < planeIds.length; i++) {
      if ((this.pendingApplicationRectanglesByPlaneId[planeIds[i]] || []).length > 0) return true;
    }
    return false;
  };

  scheduleInitialLoadingCheck = () => {
    if (this.loadingContentTimer || this.initialLoadComplete || !this.mounted) return;
    this.loadingContentTimer = setTimeout(() => {
      this.loadingContentTimer = null;
      this.tryHideInitialLoading();
    }, 250);
  };

  tryHideInitialLoading = () => {
    if (!this.mounted || this.initialLoadComplete || !this.initialDataReady) return;

    const elapsed = Date.now() - this.loadingStartedAt;
    const visualContentReady = !this.hasPendingInitialContent();
    const minimumVisibleTimeReached = elapsed >= 1500;

    if (this.initialTilesReady && visualContentReady && minimumVisibleTimeReached) {
      if (this.viewer && !this.viewer.isDestroyed()) {
        this.viewer.resize();
        this.viewer.scene.requestRender();
      }
      requestAnimationFrame(() => {
        requestAnimationFrame(() => this.finishInitialLoading());
      });
      return;
    }

    if (elapsed >= loadingTimeoutMs) {
      this.finishInitialLoading();
      return;
    }

    this.scheduleInitialLoadingCheck();
  };

  finishInitialLoading = () => {
    if (!this.mounted || this.initialLoadComplete) return;

    this.initialLoadComplete = true;

    if (this.loadingTimer) {
      clearTimeout(this.loadingTimer);
      this.loadingTimer = null;
    }
    if (this.loadingTilesTimer) {
      clearTimeout(this.loadingTilesTimer);
      this.loadingTilesTimer = null;
    }
    if (this.loadingContentTimer) {
      clearTimeout(this.loadingContentTimer);
      this.loadingContentTimer = null;
    }

    this.removeInitialTilesListener();

    if (this.state.loading) {
      this.setState({ loading: false });
    }
  };

  handleFullscreenChange = () => {
    this.setState({ fullscreen: !!document.fullscreenElement });
  };

  toggleFullscreen = () => {
    const element = document.querySelector('.replay3d-base-overlay');
    if (!element) return;

    if (document.fullscreenElement) {
      document.exitFullscreen();
      this.setState({ fullscreen: false });
      return;
    }

    if (element.requestFullscreen) {
      element.requestFullscreen();
      this.setState({ fullscreen: true });
    }
  };

  isMobileCompactViewport = () => (
    typeof window !== 'undefined' &&
    (
      window.innerWidth <= 540 ||
      (window.innerWidth <= 980 && window.innerHeight <= 540)
    )
  );

  handleViewportResize = () => {
    const mobileCompact = this.isMobileCompactViewport();
    if (mobileCompact === this.state.mobileCompact) return;

    this.setState({ mobileCompact: mobileCompact }, () => {
      if (!this.viewer || this.viewer.isDestroyed()) return;
      this.viewer.resize();
      if (!this.state.freeMode) this.applyCameraControls();
      this.viewer.scene.requestRender();
    });
  };

  togglePanelVisibility = () => {
    if (this.state.mobileCompact) return;
    this.setState({ panelCollapsed: !this.state.panelCollapsed });
  };

  captureCameraState = () => {
    if (!this.viewer || this.viewer.isDestroyed()) return null;

    const camera = this.viewer.camera;
    return {
      destination: Cesium.Cartesian3.clone(camera.positionWC),
      direction: Cesium.Cartesian3.clone(camera.directionWC),
      up: Cesium.Cartesian3.clone(camera.upWC),
      right: Cesium.Cartesian3.clone(camera.rightWC)
    };
  };

  restoreCameraState = (cameraState) => {
    if (!cameraState || !this.viewer || this.viewer.isDestroyed()) return;

    this.viewer.camera.setView({
      destination: cameraState.destination,
      orientation: {
        direction: cameraState.direction,
        up: cameraState.up
      }
    });
    this.viewer.scene.requestRender();
  };

  isRealTime3DEnabled = () => {
    return this.props.realTime3D === true || this.props.realTime3D === 'true' || Number(this.props.realTime3D) === 1;
  };

  clearRealtimeActivityState = () => {
    Object.keys(this.aircraftAnimationFrameByPlaneId || {}).forEach((idPlane) => {
      cancelAnimationFrame(this.aircraftAnimationFrameByPlaneId[idPlane]);
    });

    this.aircraftAnimationFrameByPlaneId = {};
    this.aircraftPlaybackQueueByPlaneId = {};
    this.pendingReplayActivitiesByPlaneId = {};
    this.activityActiveSampleByPlaneId = {};
    this.playbackStartedByPlaneId = {};
    this.playbackTimelineOffsetByPlaneId = {};
    this.visualPlaybackSpeedByPlaneId = {};
    this.playbackFinalizingByPlaneId = {};
    this.activityMonitoringByPlaneId = {};
    this.activityMonitoringStartedAtByPlaneId = {};
    this.activityBootstrapPendingByPlaneId = {};
    this.realtimePlaneIds = {};
    this.realtimeJobIdsByPlaneId = {};
    this.jobEventSegmentsByPlaneId = {};
    this.jobEventLastPositionByPlaneId = {};
    this.realtimeExecutionsByKey = {};
  };

  loadMapData = () => {
    if (!this.viewer) return;

    if (this.props.initialDataLoaded !== true) {
      if (!this.state.loading) this.setState({ loading: true });
      return;
    }

    const preservedCameraState = this.initialCameraFocusApplied
      ? this.captureCameraState()
      : null;

    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer);
      this.refreshTimer = null;
    }

    if (this.planePositionTimer) {
      clearTimeout(this.planePositionTimer);
      this.planePositionTimer = null;
    }
    this.nextPlanePositionRefreshAt = 0;

    const jobs = this.getRunningJobs();
    const planes = this.getPlanesFromJobs(jobs);
    const weatherStations = Array.isArray(this.props.weatherStations) ? this.props.weatherStations : [];
    const hasData = jobs.length > 0 || planes.length > 0 || weatherStations.length > 0;
    const message = jobs.length > 0 ? '' : this.props.res.NENHUM_TRABALHO_EXECUCAO_DIA;

    if (this.isRealTime3DEnabled()) {
      this.syncRealtimePlaneTracking(jobs);
    } else {
      this.clearRealtimeActivityState();
    }

    if (!hasData) {
      this.viewer.entities.removeAll();
      this.setState({ jobs: [], planes: [], focusItems: [], planeFocusItems: [], stationFocusItems: [], message: message });
      if (!this.initialCameraFocusApplied) {
        this.flyToDefaultPlace();
        this.initialCameraFocusApplied = true;
      } else {
        this.restoreCameraState(preservedCameraState);
      }
      this.markInitialDataReady();
      return;
    }

    this.setState(this.initialLoadComplete ? { message: message } : { loading: true, message: message });

    Promise.all([
      this.handleJobAreas(jobs),
      this.loadPlanePositions(planes),
      this.handleApplicationReports(jobs),
      this.handleTelemetryReports(jobs),
      this.handleJobClimateSummaries(jobs),
      this.isRealTime3DEnabled()
        ? this.loadDailyJobEventHistoryForPlanes(planes)
        : Promise.resolve([])
    ]).then((result) => {
      if (!this.mounted || !this.viewer || this.viewer.isDestroyed()) return;

      const areasByJob = result[0];
      const positionsByPlane = result[1];
      const applicationReportsByJob = result[2];
      const telemetryReportsByJob = result[3];
      const climateSummariesByJob = result[4];

      for (let i = 0; i < jobs.length; i++) {
        const idJob = jobs[i] && jobs[i].id;
        if (areasByJob[idJob]) {
          areasByJob[idJob].applicationReport = applicationReportsByJob[idJob] || null;
          areasByJob[idJob].telemetryReport = telemetryReportsByJob[idJob] || null;
          areasByJob[idJob].climateSummary = climateSummariesByJob[idJob] || null;
        }
      }

      if (this.isRealTime3DEnabled()) this.reconcileRealtimeExecutions(jobs);
      const mapResult = this.drawOnlineMap(jobs, planes, areasByJob, positionsByPlane, weatherStations);
      const focusItems = mapResult.focusItems;
      const planeFocusItems = mapResult.planeFocusItems;
      const stationFocusItems = mapResult.stationFocusItems || [];
      if (this.isRealTime3DEnabled()) {
        const eventPlaneIds = Object.keys(this.jobEventSegmentsByPlaneId || {});
        for (let i = 0; i < eventPlaneIds.length; i++) {
          this.updateJobEventApplicationEntities(eventPlaneIds[i]);
        }
      }

      const safeIndex = this.getSafeJobIndex(this.state.currentJobIndex, focusItems.length);
      const safePlaneIndex = this.getSafeJobIndex(this.state.currentPlaneIndex, planeFocusItems.length);
      const safeStationIndex = this.getSafeJobIndex(this.state.currentStationIndex, stationFocusItems.length);
      this.focusItems = focusItems;
      this.planePositionsById = Object.assign({}, this.planePositionsById, positionsByPlane);
      const shouldInitializeFocus = !this.initialLoadComplete;
      this.setState({
        jobs: jobs,
        planes: planes,
        focusItems: focusItems,
        planeFocusItems: planeFocusItems,
        stationFocusItems: stationFocusItems,
        positionsByPlane: Object.assign({}, this.planePositionsById),
        currentJobIndex: safeIndex,
        currentPlaneIndex: safePlaneIndex,
        currentStationIndex: safeStationIndex,
        cameraFocusType: shouldInitializeFocus
          ? (focusItems.length ? 'job' : (planeFocusItems.length ? 'plane' : (stationFocusItems.length ? 'station' : this.state.cameraFocusType)))
          : this.state.cameraFocusType,
        freeMode: shouldInitializeFocus ? false : this.state.freeMode,
        message: message
      }, () => {
        if (preservedCameraState) this.restoreCameraState(preservedCameraState);
        this.applyMapItemSelectionVisibility();
        this.updateWeatherStationCoverageVisibility();
        this.markInitialDataReady();
        this.schedulePlanePositionRefresh();
      });

      this.refreshTimer = setTimeout(this.loadMapData, 60000);
    }).catch((error) => {
      if (!this.mounted) return;
      this.setState({ message: this.props.res.NAO_FOI_POSSIVEL_CARREGAR_MAPA_FAZENDA_ONLINE }, () => {
        this.markInitialDataReady();
      });
      notification.open({
        message: this.props.res.ERRO,
        description: this.props.res.ERRO_MSG,
        icon: <MaterialIcon icon="error" className="text-danger" />
      });
    });
  };

  getRunningJobs = () => {
    const jobs = Array.isArray(this.props.jobs) ? this.props.jobs : [];
    const dateIni = this.props.dateIni ? this.props.dateIni.getTime() : 0;
    const dateEnd = this.props.dateEnd ? this.props.dateEnd.getTime() : 0;

    return jobs.filter((job) => this.isOnlineFarmJobInPeriod(job, dateIni, dateEnd));
  };

  isOnlineFarmJobInPeriod = (job, dateIni, dateEnd) => {
    if (!job || job.status === 'canceled' || job.status === 'deleted') return false;

    const dates = this.getOnlineFarmJobDates(job);
    if (!dates.length) return true;

    for (let i = 0; i < dates.length; i++) {
      if (dates[i] >= dateIni && dates[i] < dateEnd) return true;
    }

    return false;
  };

  getOnlineFarmJobDates = (job) => {
    const dates = [];
    const addDate = (value) => {
      const numberValue = Number(value || 0);
      if (numberValue > 0) {
        dates.push(numberValue);
        return;
      }

      const parsed = value ? new Date(value).getTime() : 0;
      if (parsed > 0) dates.push(parsed);
    };

    const addItemDates = (item) => {
      if (!item) return;
      addDate(item.startDate);
      addDate(item.endDate);
      addDate(item.date);
      addDate(item.datetime);
      addDate(item.created);
      addDate(item.updated);
    };

    addItemDates(job);

    const lists = ['list_exe', 'list_plan', 'list_analise'];
    for (let i = 0; i < lists.length; i++) {
      const arr = Array.isArray(job[lists[i]]) ? job[lists[i]] : [];
      for (let j = 0; j < arr.length; j++) addItemDates(arr[j]);
    }

    return dates;
  };

  getPlaneId = (plane) => {
    if (!plane) return 0;
    const candidates = [plane.id, plane.id_plane, plane.idPlane, plane.id_aircraft, plane.id_aeronave];

    for (let i = 0; i < candidates.length; i++) {
      const id = toNumber(candidates[i], 0);
      if (id > 0) return id;
    }

    return 0;
  };

  getPlaneCatalog = () => {
    const currentPlanes = Array.isArray(this.props.planes) ? this.props.planes : [];
    const editPlanes = Array.isArray(this.props.planeCatalog) ? this.props.planeCatalog : [];
    return currentPlanes.concat(editPlanes);
  };

  findPlaneById = (idPlane) => {
    const id = toNumber(idPlane, 0);
    if (id <= 0) return null;

    const catalog = this.getPlaneCatalog();
    let fallback = null;

    for (let i = 0; i < catalog.length; i++) {
      const item = catalog[i];
      if (!item || this.getPlaneId(item) !== id) continue;

      if (!fallback) fallback = item;

      if (this.hasAircraftModelInfo(item)) {
        return item;
      }
    }

    return fallback;
  };

  hasAircraftModelInfo = (plane) => {
    if (!plane) return false;
    const model3D = this.normalizeReplay3DModelId(plane.model3D);
    if (model3D) return true;

    const text = this.buildAircraftSearchText(plane);
    return !!text && (
      text.indexOf('airtractor') >= 0 ||
      text.indexOf('at502') >= 0 ||
      text.indexOf('at802') >= 0 ||
      text.indexOf('cessna') >= 0 ||
      text.indexOf('embraer') >= 0 ||
      text.indexOf('ipanema') >= 0 ||
      text.indexOf('emb200') >= 0 ||
      text.indexOf('emb203') >= 0 ||
      text.indexOf('thrush') >= 0
    );
  };

  getPlanesFromJobs = (jobs) => {
    const ret = [];
    const added = {};

    const addPlane = (plane, override) => {
      const idPlane = this.getPlaneId(Object.assign({}, plane || {}, override || {}));
      if (idPlane <= 0 || added[idPlane]) return;

      const catalogPlane = this.findPlaneById(idPlane) || {};
      const merged = Object.assign({}, plane || {}, catalogPlane, override || {}, {
        id: idPlane,
        name: (override && override.name) || catalogPlane.name || (plane && (plane.name || plane.plane_name || plane.plane)) || (this.props.res.AERONAVE + ' ' + idPlane),
        plane_name: catalogPlane.name || (plane && (plane.plane_name || plane.plane || plane.name)) || ''
      });

      if (!this.hasPlaneDgps(merged)) return;

      added[idPlane] = true;
      ret.push(merged);
    };

    const propPlanes = Array.isArray(this.props.planes) ? this.props.planes : [];
    for (let i = 0; i < propPlanes.length; i++) addPlane(propPlanes[i]);

    for (let i = 0; i < jobs.length; i++) {
      const lists = ['list_exe', 'list_plan', 'list_analise'];
      for (let l = 0; l < lists.length; l++) {
        const arr = Array.isArray(jobs[i][lists[l]]) ? jobs[i][lists[l]] : [];
        for (let j = 0; j < arr.length; j++) {
          const idPlane = toNumber(arr[j].id_plane || arr[j].idPlane || arr[j].plane_id, 0);
          if (idPlane > 0) {
            addPlane(arr[j], {
              id: idPlane,
              name: arr[j].plane_name || arr[j].plane || arr[j].name || (this.props.res.AERONAVE + ' ' + idPlane),
              plane_name: arr[j].plane_name || arr[j].plane || arr[j].name || ''
            });
          }
        }
      }
    }

    return ret;
  };

  handleJobAreas = (jobs) => {
    const promises = jobs.map((job) => this.handleJobArea(job));
    return Promise.all(promises).then((result) => {
      const map = {};
      for (let i = 0; i < result.length; i++) map[jobs[i].id] = result[i];
      return map;
    });
  };

  handleJobArea = (job) => {
    return Promise.all([
      this.readJobArea(job.id, 'execucao'),
      this.readJobArea(job.id, 'planejamento'),
      this.readSavedPlanningJob(job)
    ]).then((result) => {
      const executionAreas = Array.isArray(result[0]) ? result[0] : [];
      const planningAreas = Array.isArray(result[1]) ? result[1] : [];
      const areas = planningAreas.length ? planningAreas.slice() : executionAreas.slice();

      areas.executionAreas = executionAreas;
      areas.planningAreas = planningAreas;
      areas.planningJob = result[2] || null;
      areas.applicationReport = null;


      return areas;
    });
  };

  getJobPlanningGuid = (job) => {
    if (!job) return '';

    const plans = Array.isArray(job.list_plan) ? job.list_plan : [];
    for (let i = 0; i < plans.length; i++) {
      const guid = String(plans[i] && plans[i].guid || '').trim();
      if (guid) return guid;
    }

    return '';
  };

  readSavedPlanningJob = (job) => {
    const guid = this.getJobPlanningGuid(job);
    if (!guid) return Promise.resolve(null);

    if (Object.prototype.hasOwnProperty.call(this.planningJobs, guid)) {
      return Promise.resolve(this.planningJobs[guid]);
    }

    if (this.planningJobRequests[guid]) {
      return this.planningJobRequests[guid];
    }

    const apiUrl = Config.server + '/job_load/' + guid;
    const options = {
      method: 'GET',
      headers: { 'Authorization': localStorage.getItem('access-token-jwt') }
    };

    const request = fetch(apiUrl, options)
      .then((res) => {
        if (res.status !== 200) return null;
        return res.json();
      })
      .then((result) => {
        const planningData = result || null;
        this.planningJobs[guid] = planningData;
        delete this.planningJobRequests[guid];
        return planningData;
      })
      .catch(() => {
        this.planningJobs[guid] = null;
        delete this.planningJobRequests[guid];
        return null;
      });

    this.planningJobRequests[guid] = request;
    return request;
  };

  handleApplicationReports = (jobs) => {
    if (!Array.isArray(jobs) || !jobs.length) return Promise.resolve({});

    const requests = jobs.map((job) => {
      const hasExecution = this.hasJobExecution(job);
      if (!hasExecution) return Promise.resolve(null);
      return this.handleApplicationReport(job);
    });

    return Promise.all(requests).then((reports) => {
      const reportsByJob = {};
      for (let i = 0; i < jobs.length; i++) {
        if (jobs[i] && jobs[i].id && reports[i]) {
          reportsByJob[jobs[i].id] = reports[i];
        }
      }
      return reportsByJob;
    });
  };

  hasJobExecution = (job) => {
    if (!job) return false;

    const executionLists = ['list_exe', 'list_analise'];
    for (let i = 0; i < executionLists.length; i++) {
      const list = Array.isArray(job[executionLists[i]]) ? job[executionLists[i]] : [];
      if (list.length > 0) return true;
    }

    if (toNumber(job.id_execution, 0) > 0) return true;
    if (toNumber(job.id_execucao, 0) > 0) return true;
    if (toNumber(job.id_exe, 0) > 0) return true;
    if (toNumber(job.area_spray, 0) > 0) return true;

    const type = String(job.type || job.tipo || '').toLowerCase();
    if (type === 'execucao' || type === 'execução' || type === 'analise' || type === 'análise') return true;

    return false;
  };

  getApplicationReportKey = (job) => {
    const idJob = toNumber(job && job.id, 0);
    const taskIds = this.getJobResultTaskIds(job);
    return idJob + ':' + taskIds.join(',');
  };

  handleApplicationReport = (job) => {
    const cacheKey = this.getApplicationReportKey(job);
    const isRunning = this.isJobRunning(job);

    if (!isRunning && Object.prototype.hasOwnProperty.call(this.applicationReports, cacheKey)) {
      return Promise.resolve(this.applicationReports[cacheKey]);
    }

    if (this.applicationReportRequests[cacheKey]) {
      return this.applicationReportRequests[cacheKey];
    }

    const request = this.readApplicationReport(job)
      .then((report) => {
        this.applicationReports[cacheKey] = report || null;
        return report || null;
      })
      .catch(() => {
        if (!isRunning) this.applicationReports[cacheKey] = null;
        return null;
      })
      .then((report) => {
        delete this.applicationReportRequests[cacheKey];
        return report;
      });

    this.applicationReportRequests[cacheKey] = request;
    return request;
  };

  readApplicationReport = (job) => {
    const embeddedReport = this.getApplicationReportData(job);
    if (embeddedReport) {
      return Promise.resolve(embeddedReport);
    }

    const taskIds = this.getJobResultTaskIds(job);

    if (!taskIds.length) {
      return Promise.resolve(null);
    }

    const tryTask = (index) => {
      if (index >= taskIds.length) {
        return Promise.resolve(null);
      }

      const taskId = taskIds[index];
      const forceRefresh = this.isJobRunning(job);

      return this.handleJobResult(taskId, forceRefresh).then((result) => {
        const items = this.getApplicationReportItems(result);


        if (items.length) return result;
        return tryTask(index + 1);
      });
    };

    return tryTask(0);
  };

  getApplicationReportData = (job) => {
    if (!job) return null;

    const candidates = [job];
    const lists = ['list_analise', 'list_exe', 'list_plan'];

    for (let i = 0; i < lists.length; i++) {
      const list = Array.isArray(job[lists[i]]) ? job[lists[i]] : [];
      for (let j = 0; j < list.length; j++) candidates.push(list[j]);
    }

    for (let i = 0; i < candidates.length; i++) {
      if (this.hasApplicationReport(candidates[i])) return candidates[i];
    }

    return null;
  };

  hasApplicationReport = (result) => {
    return this.getApplicationReportItems(result).length > 0;
  };

  handleJobResult = (taskId, forceRefresh) => {
    const id = toNumber(taskId, 0);
    if (id <= 0) return Promise.resolve(null);

    if (!forceRefresh && Object.prototype.hasOwnProperty.call(this.jobResults, id)) {
      return Promise.resolve(this.jobResults[id]);
    }

    if (this.jobResultRequests[id]) {
      return this.jobResultRequests[id];
    }


    const request = this.readJobResult(id)
      .then((result) => {
        this.jobResults[id] = result || null;
        return result || null;
      })
      .catch(() => {
        this.jobResults[id] = null;
        return null;
      })
      .then((result) => {
        delete this.jobResultRequests[id];
        return result;
      });

    this.jobResultRequests[id] = request;
    return request;
  };

  readJobResult = async (taskId) => {
    const apiUrl = Config.server + '/getResult/' + taskId;
    const options = {
      method: 'GET',
      headers: {
        'Authorization': localStorage.getItem('access-token-jwt')
      }
    };


    try {
      const response = await fetch(apiUrl, options);

      if (!response.ok) {
        return null;
      }

      const result = await Calls.toJSON(
        response.headers.get('content-length'),
        response.body,
        () => { }
      );


      if (!result || result === 'file not found.') {
        return null;
      }

      if (typeof result === 'object') {
        return result;
      }

      try {
        let parsed = JSON.parse(result);
        let parseDepth = 0;

        while (typeof parsed === 'string' && parseDepth < 5) {
          parsed = JSON.parse(parsed);
          parseDepth++;
        }

        return parsed;
      } catch (error) {
        return null;
      }
    } catch (error) {
      return null;
    }
  };

  getJobResultTaskIds = (job) => {
    const taskIds = [];
    const added = {};
    const reports = job && Array.isArray(job.list_rel) ? job.list_rel : [];
    for (let i = 0; i < reports.length; i++) {
      const idTask = toNumber(reports[i] && reports[i].id_task, 0);

      if (idTask > 0 && !added[idTask]) {
        added[idTask] = true;
        taskIds.push(idTask);
      }
    }

    return taskIds;
  };

  handleTelemetryReports = (jobs) => {
    if (!Array.isArray(jobs) || !jobs.length) return Promise.resolve({});

    const requests = jobs.map((job) => this.handleTelemetryReport(job));

    return Promise.all(requests).then((reports) => {
      const reportsByJob = {};
      for (let i = 0; i < jobs.length; i++) {
        if (jobs[i] && jobs[i].id && reports[i]) {
          reportsByJob[jobs[i].id] = reports[i];
        }
      }
      return reportsByJob;
    });
  };

  getTelemetryReportKey = (job) => {
    const idJob = toNumber(job && job.id, 0);
    const taskIds = this.getJobTelemetryTaskIds(job);
    return idJob + ':' + taskIds.join(',');
  };

  handleTelemetryReport = (job) => {
    const cacheKey = this.getTelemetryReportKey(job);

    if (Object.prototype.hasOwnProperty.call(this.telemetryReports, cacheKey)) {
      return Promise.resolve(this.telemetryReports[cacheKey]);
    }

    if (this.telemetryReportRequests[cacheKey]) {
      return this.telemetryReportRequests[cacheKey];
    }

    const request = this.readTelemetryReport(job)
      .then((report) => {
        this.telemetryReports[cacheKey] = report || null;
        return report || null;
      })
      .catch(() => {
        this.telemetryReports[cacheKey] = null;
        return null;
      })
      .then((report) => {
        delete this.telemetryReportRequests[cacheKey];
        return report;
      });

    this.telemetryReportRequests[cacheKey] = request;
    return request;
  };

  readTelemetryReport = (job) => {
    const taskIds = this.getJobTelemetryTaskIds(job);
    if (!taskIds.length) return Promise.resolve(null);

    const tryTask = (index) => {
      if (index >= taskIds.length) return Promise.resolve(null);

      return this.handleJobResult(taskIds[index]).then((result) => {
        if (this.getTelemetryReport(result)) return result;
        return tryTask(index + 1);
      });
    };

    return tryTask(0);
  };

  getJobTelemetryTaskIds = (job) => {
    const taskIds = [];
    const addId = (value) => {
      const id = toNumber(value, 0);
      if (id > 0 && taskIds.indexOf(id) < 0) taskIds.push(id);
    };
    const addTaskFields = (item) => {
      if (!item) return;
      addId(item.id_task);
      addId(item.idTask);
      addId(item.task_id);
      addId(item.task);
      addId(item.id_task_result);
      addId(item.id_result);
    };

    const reports = job && Array.isArray(job.list_rel) ? job.list_rel : [];
    for (let i = 0; i < reports.length; i++) addTaskFields(reports[i]);

    return taskIds;
  };

  readJobArea = (idJob, type) => {
    const jobId = toNumber(idJob, 0);
    const areaType = String(type || '');
    const cacheKey = jobId + ':' + areaType;

    if (Object.prototype.hasOwnProperty.call(this.jobAreas, cacheKey)) {
      return Promise.resolve(this.jobAreas[cacheKey]);
    }

    if (this.jobAreaRequests[cacheKey]) {
      return this.jobAreaRequests[cacheKey];
    }

    const apiUrl = Config.server + '/job_area/' + jobId + '/' + areaType;
    const options = {
      method: 'GET',
      headers: { 'Authorization': localStorage.getItem('access-token-jwt') }
    };

    const request = fetch(apiUrl, options)
      .then((res) => {
        if (res.status !== 200) return [];
        return res.json();
      })
      .then((result) => Array.isArray(result) ? result : [])
      .catch(() => [])
      .then((result) => {
        this.jobAreas[cacheKey] = result;
        delete this.jobAreaRequests[cacheKey];
        return result;
      });

    this.jobAreaRequests[cacheKey] = request;
    return request;
  };

  isJobRunning = (job) => {
    if (!job) return false;
    const status = String(job.status || job.type || job.state || '').toLowerCase();
    return status === 'running' || status === 'executing' || status === 'trabalhando';
  };

  getPlaneIdsFromJob = (job) => {
    const ids = [];
    const addId = (value) => {
      const id = toNumber(value, 0);
      if (id > 0 && ids.indexOf(id) < 0) ids.push(id);
    };
    const addPlane = (value) => {
      if (!value) return;
      if (typeof value === 'object') {
        addId(value.id);
        addId(value.id_plane);
        addId(value.idPlane);
        addId(value.plane_id);
        addId(value.id_aircraft);
        addId(value.id_aeronave);
        return;
      }
      addId(value);
    };

    addId(job && (job.id_plane || job.idPlane || job.plane_id));
    addPlane(job && job.plane);
    addPlane(job && job.aircraft);
    addPlane(job && job.aeronave);

    const lists = ['list_plan', 'list_exe', 'list_analise'];
    for (let i = 0; i < lists.length; i++) {
      const items = Array.isArray(job && job[lists[i]]) ? job[lists[i]] : [];
      for (let j = 0; j < items.length; j++) {
        const item = items[j];
        addId(item && (item.id_plane || item.idPlane || item.plane_id));
        addPlane(item && item.plane);
        addPlane(item && item.aircraft);
        addPlane(item && item.aeronave);
      }
    }

    return ids;
  };

  getJobExecutionDates = (job) => {
    const dates = [];
    const addDate = (value) => {
      const numeric = Number(value || 0);
      if (numeric > 0) {
        dates.push(numeric);
        return;
      }
      const parsed = value ? new Date(value).getTime() : 0;
      if (parsed > 0) dates.push(parsed);
    };
    const addItem = (item) => {
      if (!item) return;
      addDate(item.startDate);
      addDate(item.endDate);
      addDate(item.date);
      addDate(item.datetime);
      addDate(item.created);
      addDate(item.updated);
    };

    const lists = ['list_exe', 'list_analise'];
    for (let i = 0; i < lists.length; i++) {
      const items = Array.isArray(job && job[lists[i]]) ? job[lists[i]] : [];
      for (let j = 0; j < items.length; j++) addItem(items[j]);
    }

    if (!dates.length && this.hasJobExecution(job)) addItem(job);
    return dates;
  };

  hasOpenRealtimeExecutionForPlane = (idPlane) => {
    const keys = Object.keys(this.realtimeExecutionsByKey || {});
    for (let i = 0; i < keys.length; i++) {
      const execution = this.realtimeExecutionsByKey[keys[i]];
      if (execution && !execution.consolidated && execution.hasSpray &&
        toNumber(execution.idPlane, 0) === toNumber(idPlane, 0)) return true;
    }
    return false;
  };

  getDistanceBetweenPointsMeters = (pointA, pointB) => {
    if (!pointA || !pointB) return 0;
    const latitudeA = Cesium.Math.toRadians(toNumber(pointA.lat, 0));
    const latitudeB = Cesium.Math.toRadians(toNumber(pointB.lat, 0));
    const deltaLatitude = latitudeB - latitudeA;
    const deltaLongitude = Cesium.Math.toRadians(toNumber(pointB.lng, 0) - toNumber(pointA.lng, 0));
    const sinLatitude = Math.sin(deltaLatitude / 2);
    const sinLongitude = Math.sin(deltaLongitude / 2);
    const value = sinLatitude * sinLatitude +
      Math.cos(latitudeA) * Math.cos(latitudeB) * sinLongitude * sinLongitude;
    return 6371000 * 2 * Math.atan2(Math.sqrt(value), Math.sqrt(Math.max(0, 1 - value)));
  };

  normalizeRealtimeJobIdentifier = (value) => {
    return String(value || '').trim().toLowerCase();
  };

  getActivityJobGuid = (activity) => {
    if (!activity) return '';
    const values = [
      activity.job_guid,
      activity.jobGuid,
      activity.guid_job,
      activity.guidJob,
      activity.work_guid,
      activity.workGuid
    ];
    for (let i = 0; i < values.length; i++) {
      const value = this.normalizeRealtimeJobIdentifier(values[i]);
      if (value) return value;
    }
    return '';
  };

  getActivityJobTitle = (activity) => {
    if (!activity) return '';
    const values = [
      activity.title,
      activity.job_title,
      activity.jobTitle,
      activity.work_title,
      activity.workTitle,
      activity.job_name,
      activity.jobName,
      activity.name
    ];
    for (let i = 0; i < values.length; i++) {
      const value = String(values[i] || '').trim();
      if (value) return value;
    }
    return '';
  };

  getRealtimeExecutionKey = (idPlane, activity, created) => {
    const jobGuid = this.getActivityJobGuid(activity);
    if (jobGuid) return 'plane:' + idPlane + ':guid:' + jobGuid;

    const idJobLink = toNumber(activity && activity.id_job_link, 0);
    if (idJobLink > 0) return 'plane:' + idPlane + ':job-link:' + idJobLink;

    const title = this.normalizeRealtimeJobIdentifier(this.getActivityJobTitle(activity));
    if (title) return 'plane:' + idPlane + ':title:' + title;

    return 'plane:' + idPlane + ':day:' + this.getCurrentDayStart();
  };

  findCompatibleRealtimeExecution = (idPlane, activity) => {
    const jobGuid = this.getActivityJobGuid(activity);
    const jobTitle = this.normalizeRealtimeJobIdentifier(this.getActivityJobTitle(activity));
    const idJobLink = toNumber(activity && activity.id_job_link, 0);
    const executions = Object.keys(this.realtimeExecutionsByKey || {})
      .map(key => this.realtimeExecutionsByKey[key])
      .filter(execution => execution && toNumber(execution.idPlane, 0) === toNumber(idPlane, 0));

    for (let i = 0; i < executions.length; i++) {
      const execution = executions[i];
      if (jobGuid && this.normalizeRealtimeJobIdentifier(execution.jobGuid) === jobGuid) return execution;
    }

    for (let i = 0; i < executions.length; i++) {
      const execution = executions[i];
      const executionTitle = this.normalizeRealtimeJobIdentifier(execution.jobTitle);
      const executionLinks = Array.isArray(execution.idJobLinks) ? execution.idJobLinks : [];
      if (idJobLink > 0 && (toNumber(execution.idJobLink, 0) === idJobLink || executionLinks.indexOf(idJobLink) >= 0)) return execution;
      if (jobTitle && executionTitle === jobTitle) return execution;
    }

    return null;
  };

  rekeyRealtimeExecution = (execution, idPlane, activity, created) => {
    if (!execution) return null;
    const nextKey = this.getRealtimeExecutionKey(idPlane, activity, created);
    if (execution.key !== nextKey) {
      delete this.realtimeExecutionsByKey[execution.key];
      execution.key = nextKey;
      this.realtimeExecutionsByKey[nextKey] = execution;
    }
    const jobGuid = this.getActivityJobGuid(activity);
    const jobTitle = this.getActivityJobTitle(activity);
    if (jobGuid) execution.jobGuid = jobGuid;
    if (jobTitle) execution.jobTitle = jobTitle;
    return execution;
  };

  loadDailyJobEventHistoryForPlanes = (planes) => {
    const safePlanes = Array.isArray(planes) ? planes : [];
    const requests = [];
    for (let i = 0; i < safePlanes.length; i++) {
      const idPlane = this.getPlaneId(safePlanes[i]);
      if (idPlane > 0) requests.push(this.loadPlaneDailyJobEventHistory(safePlanes[i], idPlane));
    }
    return Promise.all(requests);
  };

  createRealtimeExecution = (idPlane, activity, created) => {
    const idJobLink = toNumber(activity && activity.id_job_link, 0);
    const jobGuid = this.getActivityJobGuid(activity);
    const jobTitle = this.getActivityJobTitle(activity);
    const key = this.getRealtimeExecutionKey(idPlane, activity, created);

    const execution = {
      key: key,
      idPlane: idPlane,
      idDgps: toNumber(activity.id_dgps, 0),
      idJobLink: idJobLink,
      idJobLinks: idJobLink > 0 ? [idJobLink] : [],
      jobGuid: jobGuid,
      jobTitle: jobTitle,
      shapeGuid: activity.shapeguid || '',
      shapeGuids: [],
      orderId: toNumber(activity.order_id, 0),
      orderIds: [],
      firstCreated: 0,
      lastCreated: 0,
      firstSprayCreated: 0,
      lastSprayCreated: 0,
      lastActivity: null,
      lastSprayActivity: null,
      lastSprayPoint: null,
      sprayActive: false,
      hasSpray: false,
      segments: [],
      consolidated: false
    };

    this.realtimeExecutionsByKey[key] = execution;
    return execution;
  };

  isRealtimeActivityDiscontinuity = (previousPoint, previousTime, currentPoint, currentTime) => {
    if (!previousPoint || !currentPoint) return false;
    if (!this.isValidLatLng(previousPoint.lat, previousPoint.lng) ||
      !this.isValidLatLng(currentPoint.lat, currentPoint.lng)) return false;

    const previousPosition = Cesium.Cartesian3.fromDegrees(previousPoint.lng, previousPoint.lat, 0);
    const currentPosition = Cesium.Cartesian3.fromDegrees(currentPoint.lng, currentPoint.lat, 0);
    const distanceMeters = Cesium.Cartesian3.distance(previousPosition, currentPosition);
    const deltaMs = previousTime > 0 && currentTime > previousTime
      ? currentTime - previousTime
      : activitySampleIntervalMs;
    const impliedSpeed = distanceMeters / Math.max(activitySampleIntervalMs, deltaMs) * 1000;

    return distanceMeters >= realtimeDiscontinuityMinimumDistanceMeters && (
      impliedSpeed > realtimeDiscontinuityMaximumSpeedMetersPerSecond ||
      deltaMs > realtimeDiscontinuityMaximumSampleGapMs
    );
  };

  appendRealtimeExecutionActivity = (idPlane, rawActivity) => {
    const activity = this.normalizeJobEventActivity(rawActivity);
    const point = this.getValidPosition(activity);
    if (!activity || !point) return;

    const created = this.getJobEventCreatedTime(activity);
    const idJobLink = toNumber(activity.id_job_link, 0);
    const executionKey = this.getRealtimeExecutionKey(idPlane, activity, created);
    let execution = this.realtimeExecutionsByKey[executionKey] || this.findCompatibleRealtimeExecution(idPlane, activity);

    if (!execution) execution = this.createRealtimeExecution(idPlane, activity, created);
    else execution = this.rekeyRealtimeExecution(execution, idPlane, activity, created);

    if (created > 0) {
      execution.firstCreated = execution.firstCreated > 0 ? Math.min(execution.firstCreated, created) : created;
      execution.lastCreated = Math.max(execution.lastCreated, created);
    }

    execution.lastActivity = activity;
    execution.idDgps = execution.idDgps || toNumber(activity.id_dgps, 0);
    if (!execution.jobGuid) execution.jobGuid = this.getActivityJobGuid(activity);
    if (!execution.jobTitle) execution.jobTitle = this.getActivityJobTitle(activity);

    if (!activity.spray) {
      execution.sprayActive = false;
      if (execution.segments.length && execution.segments[execution.segments.length - 1].length) {
        execution.segments.push([]);
      }
      return;
    }

    if (created > 0) {
      execution.firstSprayCreated = execution.firstSprayCreated > 0
        ? Math.min(execution.firstSprayCreated, created)
        : created;
      execution.lastSprayCreated = Math.max(execution.lastSprayCreated, created);
    }

    execution.lastSprayActivity = activity;
    execution.lastSprayPoint = { lat: point.lat, lng: point.lng, alt: toNumber(activity.alt, 0) };
    if (idJobLink > 0) {
      if (!Array.isArray(execution.idJobLinks)) execution.idJobLinks = [];
      if (execution.idJobLinks.indexOf(idJobLink) < 0) execution.idJobLinks.push(idJobLink);
      if (!execution.idJobLink) execution.idJobLink = idJobLink;
    }
    execution.sprayActive = true;
    execution.hasSpray = true;

    const shapeGuid = String(activity.shapeguid || '').trim();
    if (shapeGuid && execution.shapeGuids.indexOf(shapeGuid) < 0) execution.shapeGuids.push(shapeGuid);
    if (!execution.shapeGuid && shapeGuid) execution.shapeGuid = shapeGuid;

    const orderId = toNumber(activity.order_id, 0);
    if (orderId > 0 && execution.orderIds.indexOf(orderId) < 0) execution.orderIds.push(orderId);
    if (!execution.orderId && orderId > 0) execution.orderId = orderId;

    if (!execution.segments.length || !execution.segments[execution.segments.length - 1].length) {
      if (!execution.segments.length || execution.segments[execution.segments.length - 1].length) {
        execution.segments.push([]);
      }
    }

    let segment = execution.segments[execution.segments.length - 1];
    let last = segment.length ? segment[segment.length - 1] : null;
    if (last && this.isRealtimeActivityDiscontinuity(last, toNumber(last.sourceTime, 0), point, created)) {
      execution.segments.push([]);
      segment = execution.segments[execution.segments.length - 1];
      last = null;

    }
    if (!last || last.lat !== point.lat || last.lng !== point.lng) {
      segment.push({
        lat: point.lat,
        lng: point.lng,
        alt: toNumber(activity.alt, 0),
        swathWidth: toNumber(activity.swathWidth, 0),
        sourceTime: created
      });
    }
  };

  realtimeExecutionIdentifiersMatchJob = (execution, job) => {
    if (!execution || !job) return false;

    const executionJobGuid = this.normalizeRealtimeJobIdentifier(execution.jobGuid);
    if (executionJobGuid) {
      const values = [job.guid, job.job_guid, job.jobGuid, job.guid_job, job.guidJob];
      const lists = ['list_exe', 'list_analise', 'list_plan'];
      for (let i = 0; i < lists.length; i++) {
        const items = Array.isArray(job[lists[i]]) ? job[lists[i]] : [];
        for (let j = 0; j < items.length; j++) {
          const item = items[j];
          if (!item) continue;
          values.push(item.guid, item.job_guid, item.jobGuid, item.guid_job, item.guidJob);
        }
      }
      if (values.some(value => this.normalizeRealtimeJobIdentifier(value) === executionJobGuid)) return true;
    }

    const executionJobLinks = Array.isArray(execution.idJobLinks) && execution.idJobLinks.length
      ? execution.idJobLinks
      : (execution.idJobLink > 0 ? [execution.idJobLink] : []);
    if (executionJobLinks.length) {
      const candidates = [job.id_job_link, job.idJobLink, job.id, job.id_job]
        .map(value => toNumber(value, 0))
        .filter(value => value > 0);
      if (candidates.some(value => executionJobLinks.indexOf(value) >= 0)) return true;
    }

    const executionTitle = this.normalizeRealtimeJobIdentifier(execution.jobTitle);
    const jobTitle = this.normalizeRealtimeJobIdentifier(job.title || job.job_title || job.name);
    if (executionTitle && jobTitle && executionTitle === jobTitle) return true;

    return false;
  };

  realtimeExecutionMatchesJob = (execution, job) => {
    if (!execution || !job || !this.hasJobExecution(job)) return false;
    if (this.realtimeExecutionIdentifiersMatchJob(execution, job)) return true;

    const executionShapeGuids = Array.isArray(execution.shapeGuids) && execution.shapeGuids.length
      ? execution.shapeGuids
      : [execution.shapeGuid].filter(Boolean);
    if (executionShapeGuids.length) {
      const values = [job.guid, job.shapeguid, job.shape_guid];
      const lists = ['list_exe', 'list_analise', 'list_plan'];
      for (let i = 0; i < lists.length; i++) {
        const items = Array.isArray(job[lists[i]]) ? job[lists[i]] : [];
        for (let j = 0; j < items.length; j++) {
          values.push(items[j] && (items[j].guid || items[j].shapeguid || items[j].shape_guid));
        }
      }
      if (values.some(value => executionShapeGuids.indexOf(String(value || '').trim()) >= 0)) return true;
    }

    const planeIds = this.getPlaneIdsFromJob(job);
    if (planeIds.indexOf(toNumber(execution.idPlane, 0)) < 0) return false;

    const dates = this.getJobExecutionDates(job);
    if (!dates.length || execution.firstCreated <= 0) return false;

    const jobStart = Math.min.apply(null, dates);
    const jobEnd = Math.max.apply(null, dates);
    const executionStart = execution.firstSprayCreated || execution.firstCreated;
    const executionEnd = execution.lastSprayCreated || execution.lastCreated;
    const margin = 60 * 60 * 1000;

    return executionEnd >= jobStart - margin && executionStart <= jobEnd + margin;
  };

  getUploadedExecutionEnd = (job) => {
    if (!job || !this.hasJobExecution(job)) return 0;
    let latestEnd = 0;
    const addEnd = (value) => {
      const numeric = Number(value || 0);
      const parsed = numeric > 0 ? numeric : (value ? new Date(value).getTime() : 0);
      if (Number.isFinite(parsed) && parsed > latestEnd) latestEnd = parsed;
    };

    const lists = ['list_exe', 'list_analise'];
    for (let i = 0; i < lists.length; i++) {
      const items = Array.isArray(job[lists[i]]) ? job[lists[i]] : [];
      for (let j = 0; j < items.length; j++) {
        const item = items[j] || {};
        addEnd(item.endDate);
        addEnd(item.end_date);
        addEnd(item.endedAt);
        addEnd(item.finishedAt);
        addEnd(item.finishDate);
      }
    }

    return latestEnd;
  };

  trimRealtimeExecutionThrough = (execution, cutoff) => {
    const limit = toNumber(cutoff, 0);
    if (!execution || limit <= 0 || !Array.isArray(execution.segments)) return false;

    let removedPoints = 0;
    const nextSegments = [];
    for (let i = 0; i < execution.segments.length; i++) {
      const segment = Array.isArray(execution.segments[i]) ? execution.segments[i] : [];
      const kept = segment.filter(point => {
        const keep = toNumber(point && point.sourceTime, 0) > limit;
        if (!keep) removedPoints += 1;
        return keep;
      });
      if (kept.length) nextSegments.push(kept);
    }

    if (!removedPoints) return false;

    execution.segments = nextSegments;
    const remainingPoints = [];
    for (let i = 0; i < nextSegments.length; i++) {
      for (let j = 0; j < nextSegments[i].length; j++) remainingPoints.push(nextSegments[i][j]);
    }

    execution.hasSpray = remainingPoints.length > 0;
    execution.sprayActive = false;
    execution.firstSprayCreated = remainingPoints.length ? toNumber(remainingPoints[0].sourceTime, 0) : 0;
    execution.lastSprayCreated = remainingPoints.length ? toNumber(remainingPoints[remainingPoints.length - 1].sourceTime, 0) : 0;
    execution.lastSprayPoint = remainingPoints.length
      ? { lat: remainingPoints[remainingPoints.length - 1].lat, lng: remainingPoints[remainingPoints.length - 1].lng, alt: remainingPoints[remainingPoints.length - 1].alt }
      : null;

    return true;
  };

  reconcileRealtimeExecutions = (jobs) => {
    const safeJobs = Array.isArray(jobs) ? jobs : [];
    const keys = Object.keys(this.realtimeExecutionsByKey || {});
    const affectedPlanes = {};

    for (let i = 0; i < keys.length; i++) {
      const execution = this.realtimeExecutionsByKey[keys[i]];
      if (!execution) continue;

      const linkedJob = safeJobs.find(job => this.realtimeExecutionIdentifiersMatchJob(execution, job)) ||
        safeJobs.find(job => this.realtimeExecutionMatchesJob(execution, job));
      const idPlane = toNumber(execution.idPlane, 0);
      const uploadedEnd = linkedJob ? this.getUploadedExecutionEnd(linkedJob) : 0;
      const report = linkedJob ? this.applicationReports[this.getApplicationReportKey(linkedJob)] : null;
      const hasOfficialApplication = this.getApplicationReportItems(report).length > 0;

      if (linkedJob && uploadedEnd > 0 && hasOfficialApplication) {
        const trimmed = this.trimRealtimeExecutionThrough(execution, uploadedEnd);
        this.consolidatedThroughByPlaneId[idPlane] = Math.max(
          toNumber(this.consolidatedThroughByPlaneId[idPlane], 0),
          uploadedEnd
        );
        if (trimmed) affectedPlanes[idPlane] = true;

      }

      const hasRemainingRealtime = !!execution.hasSpray && Array.isArray(execution.segments) &&
        execution.segments.some(segment => Array.isArray(segment) && segment.length > 0);
      const canConsolidateCompletely = !!linkedJob && hasOfficialApplication && uploadedEnd > 0 && !hasRemainingRealtime;


      execution.pendingConsolidation = false;
      if (canConsolidateCompletely) {
        if (!execution.consolidated) affectedPlanes[idPlane] = true;
        execution.consolidated = true;
      } else {
        if (execution.consolidated) affectedPlanes[idPlane] = true;
        execution.consolidated = false;
      }
    }

    Object.keys(affectedPlanes).forEach(idPlane => this.updateJobEventApplicationEntities(idPlane));
    if (Object.keys(affectedPlanes).length) this.refreshRealtimeFocusItems();
  };

  finalizeConsolidatedExecutionsForPlane = (idPlane) => {
    const keys = Object.keys(this.realtimeExecutionsByKey || {});
    for (let i = 0; i < keys.length; i++) {
      const execution = this.realtimeExecutionsByKey[keys[i]];
      if (!execution || toNumber(execution.idPlane, 0) !== toNumber(idPlane, 0)) continue;
      execution.pendingConsolidation = false;
    }
    delete this.playbackFinalizingByPlaneId[idPlane];
    this.reconcileRealtimeExecutions(this.getRunningJobs());
    this.updateJobEventApplicationEntities(idPlane);
  };

  buildPlaneByDgpsId = (planes) => {
    const map = {};
    const catalog = this.getPlaneCatalog();
    const allPlanes = (Array.isArray(planes) ? planes : []).concat(catalog);

    for (let i = 0; i < allPlanes.length; i++) {
      const plane = allPlanes[i];
      const idDgps = this.getPlaneDgpsId(plane);
      if (idDgps <= 0) continue;
      map[idDgps] = map[idDgps] ? Object.assign({}, map[idDgps], plane) : plane;
    }

    return map;
  };

  hasOpenRealtimeExecutionForJob = (job) => {
    if (!job) return false;

    const keys = Object.keys(this.realtimeExecutionsByKey || {});
    for (let i = 0; i < keys.length; i++) {
      const execution = this.realtimeExecutionsByKey[keys[i]];
      if (!execution || execution.consolidated || !execution.lastActivity) continue;
      if (!this.realtimeExecutionIdentifiersMatchJob(execution, job)) continue;

      const hasRemainingRealtime = !!execution.hasSpray && Array.isArray(execution.segments) &&
        execution.segments.some(segment => Array.isArray(segment) && segment.length > 0);

      if (hasRemainingRealtime) return true;
    }

    return false;
  };

  getRealtimeExecutionFocusItems = (planes) => {
    const safePlanes = Array.isArray(planes) ? planes : [];
    const planeById = this.buildPlaneById(safePlanes);
    const planeByDgpsId = this.buildPlaneByDgpsId(safePlanes);
    const items = [];
    const keys = Object.keys(this.realtimeExecutionsByKey || {});

    for (let i = 0; i < keys.length; i++) {
      const execution = this.realtimeExecutionsByKey[keys[i]];
      if (!execution || execution.consolidated || !execution.lastActivity) continue;

      const officialJobMatch = this.getRunningJobs().some(job =>
        this.realtimeExecutionIdentifiersMatchJob(execution, job)
      );
      if (officialJobMatch) continue;

      const allPoints = [];
      for (let j = 0; j < execution.segments.length; j++) {
        const segment = execution.segments[j] || [];
        for (let k = 0; k < segment.length; k++) allPoints.push(segment[k]);
      }
      const activityPoint = this.getValidPosition(execution.lastActivity);
      const point = execution.lastSprayPoint ||
        (allPoints.length ? allPoints[allPoints.length - 1] : null) ||
        (activityPoint ? {
          lat: activityPoint.lat,
          lng: activityPoint.lng,
          alt: toNumber(execution.lastActivity && execution.lastActivity.alt, 0)
        } : null);
      if (!point) continue;

      const plane = planeById[execution.idPlane] ||
        planeByDgpsId[execution.idDgps] ||
        this.findPlaneById(execution.idPlane) ||
        { id: execution.idPlane, id_dgps: execution.idDgps };
      const planeName = this.getPlaneDisplayName(plane);

      items.push({
        type: 'realtime',
        key: execution.key,
        execution: execution,
        plane: plane,
        planeNames: planeName ? [planeName] : [],
        point: point,
        bounds: allPoints.length ? allPoints : [point],
        label: this.props.res.STATUS_EM_EXECUCAO + (execution.jobTitle ? ' - ' + execution.jobTitle : ''),
        jobTitle: execution.jobTitle || '',
        status: 'running',
        rawPosition: execution.lastSprayActivity || execution.lastActivity
      });
    }

    return items.sort((a, b) => toNumber(a.execution.firstCreated, 0) - toNumber(b.execution.firstCreated, 0));
  };

  refreshRealtimeFocusItems = () => {
    if (!this.mounted) return;
    const officialItems = (this.state.focusItems || []).filter(item => item && item.type !== 'realtime');
    const realtimeItems = this.getRealtimeExecutionFocusItems(this.state.planes || []);
    const focusItems = officialItems.concat(realtimeItems);
    const currentJobIndex = this.getSafeJobIndex(this.state.currentJobIndex, focusItems.length);
    this.focusItems = focusItems;
    this.setState({ focusItems: focusItems, currentJobIndex: currentJobIndex });
  };

  clearJobEventPlane = (idPlane) => {
    const entities = this.jobEventEntitiesByPlaneId[idPlane] || [];
    if (this.viewer && !this.viewer.isDestroyed()) {
      for (let i = 0; i < entities.length; i++) this.viewer.entities.remove(entities[i]);
    }

    delete this.jobEventEntitiesByPlaneId[idPlane];
    delete this.jobEventSegmentsByPlaneId[idPlane];
    delete this.jobEventCursorByPlaneId[idPlane];
    delete this.jobEventHistoryLoadedByPlaneId[idPlane];
    delete this.jobEventHistoryRequestByPlaneId[idPlane];
    delete this.jobEventLastPositionByPlaneId[idPlane];
    delete this.activityOrderIdsByPlaneId[idPlane];
    delete this.activityTimingStateByPlaneId[idPlane];
    delete this.lastEffectiveSourceTimeByPlaneId[idPlane];
    Object.keys(this.realtimeExecutionsByKey || {}).forEach(key => {
      if (toNumber(this.realtimeExecutionsByKey[key] && this.realtimeExecutionsByKey[key].idPlane, 0) === toNumber(idPlane, 0)) {
        delete this.realtimeExecutionsByKey[key];
      }
    });
  };

  syncRealtimePlaneTracking = (jobs) => {
    const safeJobs = Array.isArray(jobs) ? jobs : [];
    const jobsById = {};
    const nextRealtimePlaneIds = Object.assign({}, this.realtimePlaneIds);
    const nextRealtimeJobIds = Object.assign({}, this.realtimeJobIdsByPlaneId);

    for (let i = 0; i < safeJobs.length; i++) {
      const job = safeJobs[i];
      const idJob = String(job && (job.id || job.id_job || job.guid) || '');
      if (idJob) jobsById[idJob] = job;
      if (!this.isJobRunning(job)) continue;

      const planeIds = this.getPlaneIdsFromJob(job);
      for (let j = 0; j < planeIds.length; j++) {
        const idPlane = String(planeIds[j]);
        nextRealtimePlaneIds[idPlane] = true;
        if (!nextRealtimeJobIds[idPlane]) nextRealtimeJobIds[idPlane] = [];
        if (idJob && nextRealtimeJobIds[idPlane].indexOf(idJob) < 0) {
          nextRealtimeJobIds[idPlane] = nextRealtimeJobIds[idPlane].concat([idJob]);
        }
      }
    }

    Object.keys(nextRealtimePlaneIds).forEach(idPlane => {
      const trackedJobIds = nextRealtimeJobIds[idPlane] || [];
      if (!trackedJobIds.length) return;

      const consolidated = trackedJobIds.every(idJob => {
        const job = jobsById[idJob];
        return !!job && !this.isJobRunning(job) && this.hasJobExecution(job);
      });

      const planeState = this.planeOnlineStateByPlaneId[idPlane];
      const planeIsStillActive = !!(planeState && planeState.active);

      if (consolidated && !planeIsStillActive) {
        delete nextRealtimePlaneIds[idPlane];
        delete nextRealtimeJobIds[idPlane];
        this.clearJobEventPlane(idPlane);
      }
    });

    this.reconcileRealtimeExecutions(safeJobs);

    Object.keys(nextRealtimePlaneIds).forEach(idPlane => {
      if (!this.hasOpenRealtimeExecutionForPlane(idPlane)) {
        delete nextRealtimeJobIds[idPlane];
      }
    });

    this.realtimePlaneIds = nextRealtimePlaneIds;
    this.realtimeJobIdsByPlaneId = nextRealtimeJobIds;
  };

  loadPlanePositions = (planes) => {
    const requests = planes.map((plane) => this.handlePlaneLivePositionRequest(plane));

    return Promise.all(requests).then((positions) => {
      const positionsByPlane = {};

      for (let i = 0; i < positions.length; i++) {
        const idPlane = this.getPlaneId(planes[i]);
        const currentPosition = positions[i];

        if (idPlane > 0 && currentPosition) {
          positionsByPlane[idPlane] = currentPosition;
        }
      }

      return positionsByPlane;
    });
  };


  getHeadingDeltaDegrees = (fromHeading, toHeading) => {
    const from = this.normalizeHeadingDegrees(fromHeading);
    const to = this.normalizeHeadingDegrees(toHeading);
    return ((to - from + 540) % 360) - 180;
  };

  getHeartbeatAlignmentDurationMs = (fromHeading, toHeading) => {
    const delta = Math.abs(this.getHeadingDeltaDegrees(fromHeading, toHeading));
    if (delta < heartbeatHeadingAlignmentMinAngleDeg) return 0;
    return Math.max(
      heartbeatHeadingAlignmentMinMs,
      Math.min(heartbeatHeadingAlignmentMaxMs, heartbeatHeadingAlignmentMaxMs * (delta / 180))
    );
  };

  getPlanePositionTime = (position) => {
    if (!position) return 0;

    const values = [
      position.datetime,
      position.created,
      position.date,
      position.ping_time,
      position.pingTime,
      position.updated_at,
      position.updatedAt
    ];

    for (let i = 0; i < values.length; i++) {
      const value = values[i];
      if (value === null || value === undefined || value === '') continue;

      const numericValue = toNumber(value, 0);
      if (numericValue > 0) return numericValue;

      const parsed = new Date(value).getTime();
      if (!isNaN(parsed) && parsed > 0) return parsed;
    }

    return 0;
  };

  applyInitialPlaneFocusOrder = (planeFocusItems) => {
    const items = Array.isArray(planeFocusItems) ? planeFocusItems.slice() : [];

    if (!Array.isArray(this.initialPlaneFocusOrderIds)) {
      items.sort((a, b) => {
        const timeA = this.getPlanePositionTime(a && a.rawPosition);
        const timeB = this.getPlanePositionTime(b && b.rawPosition);

        if (timeA !== timeB) return timeB - timeA;
        return 0;
      });

      this.initialPlaneFocusOrderIds = items
        .map(item => this.getPlaneId(item && item.plane))
        .filter(idPlane => idPlane > 0);

      return items;
    }

    const initialOrder = {};
    for (let i = 0; i < this.initialPlaneFocusOrderIds.length; i++) {
      initialOrder[this.initialPlaneFocusOrderIds[i]] = i;
    }

    items.sort((a, b) => {
      const idPlaneA = this.getPlaneId(a && a.plane);
      const idPlaneB = this.getPlaneId(b && b.plane);
      const hasA = Object.prototype.hasOwnProperty.call(initialOrder, idPlaneA);
      const hasB = Object.prototype.hasOwnProperty.call(initialOrder, idPlaneB);

      if (hasA && hasB) return initialOrder[idPlaneA] - initialOrder[idPlaneB];
      if (hasA) return -1;
      if (hasB) return 1;
      return 0;
    });

    return items;
  };

  isPlanePositionRecent = (position) => {
    const positionTime = this.getPlanePositionTime(position);
    if (positionTime <= 0) return false;
    const age = Date.now() - positionTime;
    return age >= 0 && age <= planePositionActiveWindowMs;
  };

  getHeartbeatMovementEligibility = (position, nowValue) => {
    const now = toNumber(nowValue, 0) || Date.now();
    const sourceTime = this.getPlanePositionTime(position);
    const point = this.getValidPosition(position);
    const sourceDate = sourceTime > 0 ? new Date(sourceTime) : null;
    const nowDate = new Date(now);
    const sameDay = !!sourceDate &&
      sourceDate.getFullYear() === nowDate.getFullYear() &&
      sourceDate.getMonth() === nowDate.getMonth() &&
      sourceDate.getDate() === nowDate.getDate();
    const ageMs = sourceTime > 0 ? now - sourceTime : null;
    const fresh = !!point && sourceTime > 0 && sameDay &&
      ageMs >= -heartbeatMovementFutureToleranceMs &&
      ageMs <= heartbeatMovementFreshWindowMs;

    return {
      eligible: fresh,
      sourceTime: sourceTime,
      sourceTimeISO: sourceTime > 0 ? new Date(sourceTime).toISOString() : null,
      now: now,
      nowISO: new Date(now).toISOString(),
      ageMs: ageMs,
      ageSeconds: ageMs === null ? null : Number((ageMs / 1000).toFixed(3)),
      sameDay: sameDay,
      hasValidPosition: !!point,
      point: point
    };
  };

  resetHeartbeatPlayback = (idPlane) => {
    if (this.aircraftAnimationFrameByPlaneId[idPlane]) {
      cancelAnimationFrame(this.aircraftAnimationFrameByPlaneId[idPlane]);
      delete this.aircraftAnimationFrameByPlaneId[idPlane];
    }

    this.aircraftPlaybackQueueByPlaneId[idPlane] = [];
    delete this.playbackStartedByPlaneId[idPlane];
    delete this.playbackTimelineOffsetByPlaneId[idPlane];
    delete this.visualPlaybackSpeedByPlaneId[idPlane];
    delete this.queueSpeedFactorByPlaneId[idPlane];
    delete this.activityActiveSampleByPlaneId[idPlane];
  };

  setHeartbeatPositionOnly = (idPlane, entity, model, targetPosition, heading, sourceTime, reason) => {
    this.resetHeartbeatPlayback(idPlane);
    this.playbackSourceByPlaneId[idPlane] = 'heartbeat';
    this.setPlaneEntityTransform(entity, model, targetPosition, heading, idPlane, 0);
    this.aircraftMotionByPlaneId[idPlane] = {
      currentPosition: Cesium.Cartesian3.clone(targetPosition),
      currentHeading: this.normalizeHeadingDegrees(heading),
      targetPosition: Cesium.Cartesian3.clone(targetPosition),
      targetHeading: this.normalizeHeadingDegrees(heading),
      lastSampleKey: this.getMotionSampleKey(targetPosition, heading, sourceTime),
      lastSourceTime: sourceTime || Date.now()
    };
    this.setPlanePropellerFlying(idPlane, false, false);
    this.updateFollowCamera(idPlane, targetPosition, true);

  };

  getCurrentDayStart = () => {
    const now = new Date();
    now.setHours(0, 0, 0, 0);
    return now.getTime();
  };

  loadPlaneDailyJobEventHistory = (plane, idPlane) => {
    if (idPlane <= 0 || this.jobEventHistoryLoadedByPlaneId[idPlane]) {
      return Promise.resolve(null);
    }

    if (this.jobEventHistoryRequestByPlaneId[idPlane]) {
      return this.jobEventHistoryRequestByPlaneId[idPlane];
    }

    const startDate = this.getCurrentDayStart();
    const apiUrl = `${Config.server}/job_event/${idPlane}/${startDate}`;

    const options = {
      method: 'GET',
      headers: { 'Authorization': localStorage.getItem('access-token-jwt') }
    };

    const request = fetch(apiUrl, options)
      .then((res) => {
        if (res.status !== 200) return null;
        return res.json();
      })
      .then((result) => {
        let activities = this.getJobEventActivities(result);


        if (activities.length) {
          this.appendJobEventActivities(idPlane, activities);
          this.reconcileRealtimeExecutions(this.getRunningJobs());
          this.finalizeConsolidatedExecutionsForPlane(idPlane);
          this.updateJobEventApplicationEntities(idPlane);

          const latest = activities[activities.length - 1];
          const latestTime = this.getJobEventCreatedTime(latest);
          if (latestTime > 0) {
            this.jobEventCursorByPlaneId[idPlane] = Math.max(
              toNumber(this.jobEventCursorByPlaneId[idPlane], 0),
              Math.max(0, latestTime - activityCursorOverlapMs)
            );
          }

          const normalizedLatest = this.normalizeJobEventActivity(latest);
          if (normalizedLatest) this.jobEventLastPositionByPlaneId[idPlane] = normalizedLatest;
        }

        this.jobEventHistoryLoadedByPlaneId[idPlane] = true;
        return activities.length ? this.jobEventLastPositionByPlaneId[idPlane] : null;
      })
      .catch(() => null)
      .then((result) => {
        delete this.jobEventHistoryRequestByPlaneId[idPlane];
        return result;
      });

    this.jobEventHistoryRequestByPlaneId[idPlane] = request;
    return request;
  };

  handlePlaneLivePositionRequest = (plane) => {
    if (!this.isRealTime3DEnabled()) {
      return this.handlePlanePositionRequest(plane).then((planePosition) => {
        const positionPlaneId = toNumber(planePosition && (planePosition.id_plane || planePosition.idPlane), 0);
        const idPlane = positionPlaneId > 0 ? positionPlaneId : this.getPlaneId(plane);
        if (idPlane <= 0) return planePosition;

        const planePositionTime = this.getPlanePositionTime(planePosition);
        const validHeartbeatPosition = !!this.getValidPosition(planePosition);
        const now = Date.now();

        this.planeOnlineStateByPlaneId[idPlane] = Object.assign(
          {},
          this.planeOnlineStateByPlaneId[idPlane] || {},
          {
            active: this.isPlanePositionRecent(planePosition) && validHeartbeatPosition,
            datetime: planePositionTime,
            idDgps: this.getPlaneDgpsId(plane),
            heartbeatReceivedAt: validHeartbeatPosition
              ? now
              : toNumber(this.heartbeatLastResponseAtByPlaneId[idPlane], 0),
            updatedAt: now
          }
        );

        this.playbackSourceByPlaneId[idPlane] = 'heartbeat';
        return planePosition;
      });
    }

    return this.handlePlanePositionRequest(plane).then((planePosition) => {
      const positionPlaneId = toNumber(planePosition && (planePosition.id_plane || planePosition.idPlane), 0);
      const idPlane = positionPlaneId > 0 ? positionPlaneId : this.getPlaneId(plane);
      if (idPlane <= 0) return planePosition;

      const eventPlane = positionPlaneId > 0
        ? Object.assign({}, plane, { id: positionPlaneId, id_plane: positionPlaneId })
        : plane;
      const planePositionTime = this.getPlanePositionTime(planePosition);
      const validHeartbeatPosition = !!this.getValidPosition(planePosition);
      const heartbeatRecent = this.isPlanePositionRecent(planePosition) && validHeartbeatPosition;
      const now = Date.now();
      const currentDayStart = this.getCurrentDayStart();
      const nextDayStart = currentDayStart + 24 * 60 * 60 * 1000;
      const heartbeatIsFromCurrentDay = validHeartbeatPosition &&
        planePositionTime >= currentDayStart && planePositionTime < nextDayStart;

      if (heartbeatIsFromCurrentDay && !this.jobEventHistoryLoadedByPlaneId[idPlane]) {
        this.loadPlaneDailyJobEventHistory(eventPlane, idPlane);
      }

      this.planeOnlineStateByPlaneId[idPlane] = Object.assign(
        {},
        this.planeOnlineStateByPlaneId[idPlane] || {},
        {
          active: heartbeatRecent,
          datetime: planePositionTime,
          idDgps: this.getPlaneDgpsId(plane),
          heartbeatReceivedAt: validHeartbeatPosition ? now : toNumber(this.heartbeatLastResponseAtByPlaneId[idPlane], 0),
          updatedAt: now
        }
      );

      const activityTime = toNumber(
        this.activityLastReceivedAtByPlaneId[idPlane] ||
        (this.planeOnlineStateByPlaneId[idPlane] && this.planeOnlineStateByPlaneId[idPlane].activityDatetime),
        0
      );
      const activityRecent = activityTime > 0 && now - activityTime >= 0 &&
        now - activityTime <= activityIdleStopMs;

      let bootstrapPending = false;
      if (!this.activityBootstrapCheckedByPlaneId[idPlane] && validHeartbeatPosition && planePositionTime > 0) {
        this.activityBootstrapCheckedByPlaneId[idPlane] = true;

        if (heartbeatIsFromCurrentDay) {
          bootstrapPending = true;
          this.activityBootstrapPendingByPlaneId[idPlane] = true;
          if (toNumber(this.jobEventCursorByPlaneId[idPlane], 0) <= 0) {
            this.jobEventCursorByPlaneId[idPlane] = planePositionTime;
          }
        }
      }

      const shouldRequestActivity = heartbeatRecent || activityRecent ||
        bootstrapPending || !!this.activityBootstrapPendingByPlaneId[idPlane];

      if (!shouldRequestActivity) {
        delete this.activityMonitoringByPlaneId[idPlane];
        delete this.activityMonitoringStartedAtByPlaneId[idPlane];

        if (heartbeatRecent && this.playbackSourceByPlaneId[idPlane] === 'activity') {
          this.releaseActivityPlaybackToHeartbeat(idPlane);
        }

        return planePosition || this.jobEventLastPositionByPlaneId[idPlane] || null;
      }

      this.realtimePlaneIds[idPlane] = true;
      this.activityMonitoringByPlaneId[idPlane] = true;
      if (!this.activityMonitoringStartedAtByPlaneId[idPlane]) {
        this.activityMonitoringStartedAtByPlaneId[idPlane] = now;
      }

      return this.handlePlaneJobEventRequest(eventPlane).then((jobEventPosition) => {
        const queue = this.aircraftPlaybackQueueByPlaneId[idPlane] || [];
        const hasActiveSample = !!this.activityActiveSampleByPlaneId[idPlane];
        const motion = this.aircraftMotionByPlaneId[idPlane] || {};
        const latestActivityTime = Math.max(
          toNumber(this.activityLastReceivedAtByPlaneId[idPlane], 0),
          toNumber(motion.lastSourceTime, 0)
        );
        const heartbeatIsNewer = heartbeatRecent && planePositionTime > latestActivityTime;

        if (this.playbackSourceByPlaneId[idPlane] === 'activity' &&
          !queue.length && !hasActiveSample && heartbeatIsNewer) {
          this.releaseActivityPlaybackToHeartbeat(idPlane, true);
          return planePosition || jobEventPosition || this.jobEventLastPositionByPlaneId[idPlane] || null;
        }

        if (this.playbackSourceByPlaneId[idPlane] === 'activity') {
          return jobEventPosition || this.jobEventLastPositionByPlaneId[idPlane] || planePosition || null;
        }

        return planePosition || jobEventPosition || this.jobEventLastPositionByPlaneId[idPlane] || null;
      });
    });
  };

  releaseActivityPlaybackToHeartbeat = (idPlane, force) => {
    const queue = this.aircraftPlaybackQueueByPlaneId[idPlane] || [];
    const hasActiveSample = !!this.activityActiveSampleByPlaneId[idPlane];
    if (!force && (queue.length > 0 || hasActiveSample)) return false;

    if (this.aircraftAnimationFrameByPlaneId[idPlane]) {
      cancelAnimationFrame(this.aircraftAnimationFrameByPlaneId[idPlane]);
      delete this.aircraftAnimationFrameByPlaneId[idPlane];
    }

    this.aircraftPlaybackQueueByPlaneId[idPlane] = [];
    this.pendingReplayActivitiesByPlaneId[idPlane] = [];
    delete this.playbackStartedByPlaneId[idPlane];
    delete this.playbackTimelineOffsetByPlaneId[idPlane];
    delete this.visualPlaybackSpeedByPlaneId[idPlane];
    delete this.playbackFinalizingByPlaneId[idPlane];
    delete this.activityActiveSampleByPlaneId[idPlane];
    delete this.queueSpeedFactorByPlaneId[idPlane];
    this.playbackSourceByPlaneId[idPlane] = 'heartbeat';
    return true;
  };

  getJobEventStartDate = (idPlane) => {
    const cursor = toNumber(this.jobEventCursorByPlaneId[idPlane], 0);
    if (cursor > 0) return cursor;
    return this.getCurrentDayStart();
  };

  getJobEventActivities = (result) => {
    const activities = [];
    const appendActivities = (value, parentData) => {
      if (!value) return;
      if (Array.isArray(value)) {
        for (let i = 0; i < value.length; i++) appendActivities(value[i], parentData);
        return;
      }

      const inherited = Object.assign({}, parentData || {});
      const parentGuid = value.job_guid || value.jobGuid || value.guid_job || value.guidJob || value.work_guid || value.workGuid || value.guid;
      const parentTitle = value.title || value.job_title || value.jobTitle || value.work_title || value.workTitle || value.job_name || value.jobName || value.name;
      const parentJobLink = value.id_job_link || value.idJobLink;

      if (parentGuid) inherited.job_guid = parentGuid;
      if (parentTitle) inherited.title = parentTitle;
      if (parentJobLink) inherited.id_job_link = parentJobLink;

      if (Array.isArray(value.activity)) {
        for (let i = 0; i < value.activity.length; i++) {
          const item = value.activity[i];
          if (!item) continue;
          const activity = Object.assign({}, inherited, item);
          if (inherited.job_guid) activity.job_guid = inherited.job_guid;
          if (inherited.title) activity.title = inherited.title;
          if (inherited.id_job_link && !activity.id_job_link) activity.id_job_link = inherited.id_job_link;
          activities.push(activity);
        }
        return;
      }

      const activity = Object.assign({}, inherited, value);
      if (inherited.job_guid) activity.job_guid = inherited.job_guid;
      if (inherited.title) activity.title = inherited.title;
      if (this.getValidPosition(activity)) activities.push(activity);
    };

    appendActivities(result, null);
    return activities.sort((a, b) => {
      const orderA = toNumber(a && a.order_id, 0);
      const orderB = toNumber(b && b.order_id, 0);
      if (orderA > 0 && orderB > 0 && orderA !== orderB) return orderA - orderB;
      return this.getJobEventCreatedTime(a) - this.getJobEventCreatedTime(b);
    });
  };

  getJobEventCreatedTime = (activity) => {
    if (!activity) return 0;
    const numericValue = toNumber(activity.created, 0);
    if (numericValue > 0) return numericValue;
    const parsed = new Date(activity.created || activity.datetime || activity.date || 0).getTime();
    return isNaN(parsed) ? 0 : parsed;
  };

  normalizeJobEventActivity = (activity) => {
    if (!activity) return null;
    return Object.assign({}, activity, {
      datetime: activity.created || activity.datetime || activity.date,
      spray: toNumber(activity.active, 0) > 0,
      flow: toNumber(activity.lph, 0),
      altitude: toNumber(activity.alt, 0),
      swathWidth: toNumber(activity.swathwidth, 0),
      source: 'job_event'
    });
  };

  handlePlaneJobEventRequest = (plane) => {
    const idPlane = this.getPlaneId(plane);
    if (idPlane <= 0) return Promise.resolve(null);

    const startDate = this.getJobEventStartDate(idPlane);
    const apiUrl = `${Config.server}/job_event/${idPlane}/${startDate}`;
    const options = {
      method: 'GET',
      headers: { 'Authorization': localStorage.getItem('access-token-jwt') }
    };

    const requestedAt = Date.now();
    const previousRequestLog = this.activityRequestLogByPlaneId[idPlane] || {};
    this.activityRequestLogByPlaneId[idPlane] = Object.assign({}, previousRequestLog, {
      requestedAt: requestedAt
    });

    return fetch(apiUrl, options)
      .then((res) => {
        if (res.status !== 200) return null;
        return res.json();
      })
      .then((result) => {
        const activities = this.getJobEventActivities(result);


        const receivedOrderIds = activities
          .map(item => toNumber(item && item.order_id, 0))
          .filter(orderId => orderId > 0);

        const receivedAt = Date.now();
        this.activityLastResponseAtByPlaneId[idPlane] = receivedAt;
        const lastActivityTime = activities.length ? this.getJobEventCreatedTime(activities[activities.length - 1]) : 0;

        this.activityRequestLogByPlaneId[idPlane] = {
          requestedAt: requestedAt,
          receivedAt: receivedAt,
          lastActivityTime: lastActivityTime,
          totalReceived: activities.length
        };

        const state = this.planeOnlineStateByPlaneId[idPlane] || {};
        const heartbeatTime = toNumber(state.datetime, 0);
        const heartbeatRecent = heartbeatTime > 0 && Date.now() - heartbeatTime >= 0 &&
          Date.now() - heartbeatTime <= planePositionActiveWindowMs;

        if (!activities.length) {
          delete this.activityBootstrapPendingByPlaneId[idPlane];

          const lastActivityTime = toNumber(this.activityLastReceivedAtByPlaneId[idPlane], 0);
          const activityRecent = lastActivityTime > 0 && Date.now() - lastActivityTime >= 0 &&
            Date.now() - lastActivityTime <= activityIdleStopMs;

          if (!heartbeatRecent && !activityRecent) {
            delete this.activityMonitoringByPlaneId[idPlane];
            delete this.activityMonitoringStartedAtByPlaneId[idPlane];
          }

          return activityRecent ? (this.jobEventLastPositionByPlaneId[idPlane] || null) : null;
        }

        const latest = activities[activities.length - 1];
        const latestTime = this.getJobEventCreatedTime(latest);
        const latestActivityAge = latestTime > 0 ? Date.now() - latestTime : Number.POSITIVE_INFINITY;
        const latestActivityIsRecent = latestActivityAge >= 0 && latestActivityAge <= activityIdleStopMs;

        if (latestTime > 0) {
          this.activityLastReceivedAtByPlaneId[idPlane] = latestTime;
          this.jobEventCursorByPlaneId[idPlane] = Math.max(
            toNumber(this.jobEventCursorByPlaneId[idPlane], 0),
            Math.max(0, latestTime - activityCursorOverlapMs)
          );
        }

        this.planeOnlineStateByPlaneId[idPlane] = Object.assign(
          {},
          state,
          {
            activityDatetime: latestTime,
            activityReceivedAt: Date.now()
          }
        );

        delete this.activityBootstrapPendingByPlaneId[idPlane];

        if (latestActivityIsRecent) {
          this.activityMonitoringByPlaneId[idPlane] = true;
          this.activityMonitoringStartedAtByPlaneId[idPlane] = Date.now();
          this.enqueueJobEventMotionSamples(plane, idPlane, activities);
          return this.jobEventLastPositionByPlaneId[idPlane] || null;
        }

        if (!heartbeatRecent) {
          delete this.activityMonitoringByPlaneId[idPlane];
          delete this.activityMonitoringStartedAtByPlaneId[idPlane];
        }

        return null;
      })
      .catch((error) => {

        return null;
      });
  };

  appendJobEventActivities = (idPlane, activities) => {
    if (!this.jobEventSegmentsByPlaneId[idPlane]) this.jobEventSegmentsByPlaneId[idPlane] = [];
    const segments = this.jobEventSegmentsByPlaneId[idPlane];

    for (let i = 0; i < activities.length; i++) {
      const activity = this.normalizeJobEventActivity(activities[i]);
      const point = this.getValidPosition(activity);
      if (!activity || !point) continue;

      this.appendRealtimeExecutionActivity(idPlane, activities[i]);

      if (!activity.spray) {
        if (segments.length && segments[segments.length - 1].length) segments.push([]);
        continue;
      }

      if (!segments.length) segments.push([]);
      let segment = segments[segments.length - 1];
      if (!segment.length && segments.length > 1 && !segments[segments.length - 2].length) continue;

      let last = segment.length ? segment[segment.length - 1] : null;
      const currentTime = this.getJobEventCreatedTime(activity);
      if (last && this.isRealtimeActivityDiscontinuity(last, toNumber(last.sourceTime, 0), point, currentTime)) {
        segments.push([]);
        segment = segments[segments.length - 1];
        last = null;
      }

      if (!last || last.lat !== point.lat || last.lng !== point.lng) {
        const current = {
          lat: point.lat,
          lng: point.lng,
          alt: toNumber(activity.alt, 0),
          swathWidth: toNumber(activity.swathWidth, 0),
          sourceTime: currentTime
        };
        segment.push(current);

        if (last) {
          const width = current.swathWidth || last.swathWidth || 30;
          const positions = this.getApplicationRectanglePositions(last, current, width);
          if (positions.length) {
            if (!this.pendingApplicationRectanglesByPlaneId[idPlane]) this.pendingApplicationRectanglesByPlaneId[idPlane] = [];
            this.pendingApplicationRectanglesByPlaneId[idPlane].push(positions);
          }
        }
      }
    }


    this.schedulePendingApplicationFlush();

    if (!this.pendingFocusRefreshTimerByPlaneId[idPlane]) {
      this.pendingFocusRefreshTimerByPlaneId[idPlane] = setTimeout(() => {
        delete this.pendingFocusRefreshTimerByPlaneId[idPlane];
        this.refreshRealtimeFocusItems();
      }, 5000);
    }
  };

  getOffsetLatLng = (point, distanceMeters, bearingDegrees) => {
    const radius = 6378137;
    const angularDistance = distanceMeters / radius;
    const bearing = Cesium.Math.toRadians(bearingDegrees);
    const latitude = Cesium.Math.toRadians(point.lat);
    const longitude = Cesium.Math.toRadians(point.lng);

    const targetLatitude = Math.asin(
      Math.sin(latitude) * Math.cos(angularDistance) +
      Math.cos(latitude) * Math.sin(angularDistance) * Math.cos(bearing)
    );
    const targetLongitude = longitude + Math.atan2(
      Math.sin(bearing) * Math.sin(angularDistance) * Math.cos(latitude),
      Math.cos(angularDistance) - Math.sin(latitude) * Math.sin(targetLatitude)
    );

    return {
      lat: Cesium.Math.toDegrees(targetLatitude),
      lng: Cesium.Math.toDegrees(targetLongitude)
    };
  };

  getApplicationRectanglePositions = (start, end, widthMeters) => {
    if (!start || !end || !this.isValidLatLng(start.lat, start.lng) || !this.isValidLatLng(end.lat, end.lng)) return [];

    const startPosition = Cesium.Cartesian3.fromDegrees(start.lng, start.lat, 0);
    const endPosition = Cesium.Cartesian3.fromDegrees(end.lng, end.lat, 0);
    if (Cesium.Cartesian3.distance(startPosition, endPosition) < realtimePlaybackMinimumMovementMeters) return [];

    const startLat = Cesium.Math.toRadians(start.lat);
    const endLat = Cesium.Math.toRadians(end.lat);
    const deltaLng = Cesium.Math.toRadians(end.lng - start.lng);
    const y = Math.sin(deltaLng) * Math.cos(endLat);
    const x = Math.cos(startLat) * Math.sin(endLat) -
      Math.sin(startLat) * Math.cos(endLat) * Math.cos(deltaLng);
    const heading = (Cesium.Math.toDegrees(Math.atan2(y, x)) + 360) % 360;
    const visualWidthMeters = Math.max(0.1, toNumber(widthMeters, 30) * realtimeApplicationVisualWidthFactor);
    const halfWidth = visualWidthMeters / 2;

    const startLeft = this.getOffsetLatLng(start, halfWidth, heading - 90);
    const startRight = this.getOffsetLatLng(start, halfWidth, heading + 90);
    const endRight = this.getOffsetLatLng(end, halfWidth, heading + 90);
    const endLeft = this.getOffsetLatLng(end, halfWidth, heading - 90);

    return Cesium.Cartesian3.fromDegreesArray([
      startLeft.lng, startLeft.lat,
      startRight.lng, startRight.lat,
      endRight.lng, endRight.lat,
      endLeft.lng, endLeft.lat
    ]);
  };

  schedulePendingApplicationFlush = () => {
    if (this.pendingApplicationFrame || !this.mounted) return;
    this.pendingApplicationFrame = requestAnimationFrame(this.flushPendingApplicationRectangles);
  };

  flushPendingApplicationRectangles = () => {
    this.pendingApplicationFrame = null;
    if (!this.viewer || this.viewer.isDestroyed()) return;

    const planeIds = Object.keys(this.pendingApplicationRectanglesByPlaneId || {})
      .filter(idPlane => (this.pendingApplicationRectanglesByPlaneId[idPlane] || []).length);
    if (!planeIds.length) return;

    const startedAt = performance.now();
    let cursor = this.pendingApplicationPlaneCursor % planeIds.length;
    let processed = 0;

    while (performance.now() - startedAt < realtimeApplicationFrameBudgetMs && processed < planeIds.length * 4) {
      const idPlane = planeIds[cursor];
      const pending = this.pendingApplicationRectanglesByPlaneId[idPlane] || [];
      const positions = pending.shift();

      if (positions) {
        const entities = this.jobEventEntitiesByPlaneId[idPlane] || [];
        entities.push(this.viewer.entities.add({
          name: this.props.res.APLICACAO_TEMPO_REAL,
          polygon: {
            hierarchy: new Cesium.PolygonHierarchy(positions),
            perPositionHeight: false,
            height: 0,
            heightReference: Cesium.HeightReference.CLAMP_TO_GROUND,
            material: Cesium.Color.fromCssColorString('#32a852').withAlpha(0.9),
            outline: false
          }
        }));
        this.jobEventEntitiesByPlaneId[idPlane] = entities;
      }

      cursor = (cursor + 1) % planeIds.length;
      processed += 1;
    }

    this.pendingApplicationPlaneCursor = cursor;
    if (Object.keys(this.pendingApplicationRectanglesByPlaneId || {})
      .some(idPlane => (this.pendingApplicationRectanglesByPlaneId[idPlane] || []).length)) {
      this.schedulePendingApplicationFlush();
    }
  };

  updateJobEventApplicationEntities = (idPlane, incremental) => {
    if (!this.viewer || this.viewer.isDestroyed()) return;

    const entities = this.jobEventEntitiesByPlaneId[idPlane] || [];

    if (incremental) {
      this.schedulePendingApplicationFlush();
      return;
    }

    const rectangles = [];
    const labels = [];
    Object.keys(this.realtimeExecutionsByKey || {}).forEach(key => {
      const execution = this.realtimeExecutionsByKey[key];
      if (!execution || execution.consolidated ||
        toNumber(execution.idPlane, 0) !== toNumber(idPlane, 0)) return;

      const executionPoints = [];
      for (let i = 0; i < execution.segments.length; i++) {
        const segment = execution.segments[i] || [];
        for (let j = 0; j < segment.length; j++) executionPoints.push(segment[j]);
        for (let j = 1; j < segment.length; j++) {
          const start = segment[j - 1];
          const end = segment[j];
          const width = toNumber(end.swathWidth, 0) || toNumber(start.swathWidth, 0) || 30;
          const positions = this.getApplicationRectanglePositions(start, end, width);
          if (positions.length) rectangles.push(positions);
        }
      }

      if (executionPoints.length && execution.jobTitle) {
        let latitude = 0;
        let longitude = 0;
        for (let i = 0; i < executionPoints.length; i++) {
          latitude += toNumber(executionPoints[i].lat, 0);
          longitude += toNumber(executionPoints[i].lng, 0);
        }
        labels.push({
          lat: latitude / executionPoints.length,
          lng: longitude / executionPoints.length,
          text: execution.jobTitle
        });
      }
    });

    while (entities.length) {
      const entity = entities.pop();
      if (entity && this.viewer.entities.contains(entity)) this.viewer.entities.remove(entity);
    }

    for (let i = 0; i < rectangles.length; i++) {
      entities.push(this.viewer.entities.add({
        name: this.props.res.APLICACAO_TEMPO_REAL,
        polygon: {
          hierarchy: new Cesium.PolygonHierarchy(rectangles[i]),
          perPositionHeight: false,
          height: 0,
          heightReference: Cesium.HeightReference.CLAMP_TO_GROUND,
          material: Cesium.Color.fromCssColorString('#32a852').withAlpha(0.9),
          outline: false
        }
      }));
    }

    for (let i = 0; i < labels.length; i++) {
      entities.push(this.viewer.entities.add({
        name: labels[i].text,
        position: Cesium.Cartesian3.fromDegrees(labels[i].lng, labels[i].lat, 18),
        label: {
          text: labels[i].text,
          font: '14px sans-serif',
          fillColor: Cesium.Color.WHITE,
          outlineColor: Cesium.Color.BLACK,
          outlineWidth: 3,
          style: Cesium.LabelStyle.FILL_AND_OUTLINE,
          verticalOrigin: Cesium.VerticalOrigin.BOTTOM,
          pixelOffset: new Cesium.Cartesian2(0, -10),
          disableDepthTestDistance: Number.POSITIVE_INFINITY
        }
      }));
    }


    this.pendingApplicationRectanglesByPlaneId[idPlane] = [];
    this.jobEventEntitiesByPlaneId[idPlane] = entities;
  };

  schedulePlanePositionRefresh = () => {
    if (this.planePositionTimer) clearTimeout(this.planePositionTimer);

    const now = Date.now();
    if (!this.nextPlanePositionRefreshAt) {
      this.nextPlanePositionRefreshAt = now + planePositionRefreshIntervalMs;
    }

    while (this.nextPlanePositionRefreshAt <= now) {
      this.nextPlanePositionRefreshAt += planePositionRefreshIntervalMs;
    }

    const delay = Math.max(0, this.nextPlanePositionRefreshAt - now);
    this.planePositionTimer = setTimeout(this.handlePlanePositionsRefresh, delay);
  };

  handlePlanePositionsRefresh = () => {
    this.nextPlanePositionRefreshAt += planePositionRefreshIntervalMs;

    if (!this.mounted || this.planePositionRequestInProgress) {
      this.schedulePlanePositionRefresh();
      return;
    }

    const planes = Array.isArray(this.state.planes) ? this.state.planes : [];
    if (!planes.length) {
      this.schedulePlanePositionRefresh();
      return;
    }

    this.planePositionRequestInProgress = true;

    this.loadPlanePositions(planes)
      .then(
        (positionsByPlane) => {
          if (!this.mounted || !this.viewer || this.viewer.isDestroyed()) return;

          this.planePositionsById = Object.assign({}, this.planePositionsById, positionsByPlane);
          this.updatePlaneEntities(this.planePositionsById);
        },
        () => { }
      )
      .then(() => {
        this.planePositionRequestInProgress = false;
        if (this.mounted) this.schedulePlanePositionRefresh();
      });
  };

  updatePlaneEntities = (positionsByPlane) => {
    const planeFocusItems = (this.state.planeFocusItems || []).map((focusItem) => {
      const idPlane = this.getPlaneId(focusItem.plane);
      const rawPosition = positionsByPlane[idPlane] || focusItem.rawPosition;
      const currentPoint = this.getValidPosition(rawPosition) || focusItem.point;

      this.updatePlaneEntity(idPlane, focusItem.plane, currentPoint, rawPosition);

      return Object.assign({}, focusItem, {
        point: currentPoint,
        rawPosition: rawPosition
      });
    });

    this.setState({
      planeFocusItems: planeFocusItems,
      positionsByPlane: Object.assign({}, positionsByPlane)
    });

    if (this.viewer && !this.viewer.isDestroyed()) {
      this.viewer.scene.requestRender();
    }
  };

  shouldTeleportHeartbeat = (idPlane, rawPosition, targetPosition) => {
    if (this.playbackSourceByPlaneId[idPlane] === 'activity') return false;

    const motion = this.aircraftMotionByPlaneId[idPlane];
    if (!motion || !motion.currentPosition) return false;

    const distance = Cesium.Cartesian3.distance(motion.currentPosition, targetPosition);
    if (distance < heartbeatTeleportDistanceMeters) return false;

    const currentSpeed = toNumber(rawPosition && rawPosition.speed, 0);
    return currentSpeed <= heartbeatStoppedSpeedKmh;
  };

  isPlaneGrounded = (idPlane, rawPosition) => {
    const sourceType = this.playbackSourceByPlaneId[idPlane] || (rawPosition && rawPosition.source === 'job_event' ? 'activity' : 'heartbeat');
    if (sourceType === 'activity' || (rawPosition && rawPosition.source === 'job_event')) return false;

    const speed = toNumber(rawPosition && rawPosition.speed, 0);
    return speed <= heartbeatStoppedSpeedKmh;
  };

  getPlaneDisplayAltitude = (idPlane, model, rawPosition) => {
    if (this.isPlaneGrounded(idPlane, rawPosition)) {
      return Math.max(0, toNumber(model && model.modelAltitudeOffsetMeters, 0));
    }

    const liveAltitude = toNumber(
      rawPosition && (rawPosition.alt !== undefined ? rawPosition.alt : rawPosition.altitude),
      0
    );
    return Math.max(0, liveAltitude) + Math.max(0, toNumber(model && model.modelAltitudeOffsetMeters, 0));
  };

  applyPlaneHeightReference = (entity, idPlane, rawPosition) => {
    if (!entity) return;
    const heightReference = this.isPlaneGrounded(idPlane, rawPosition)
      ? Cesium.HeightReference.RELATIVE_TO_GROUND
      : Cesium.HeightReference.NONE;

    if (entity.model) entity.model.heightReference = heightReference;
    if (entity.label) entity.label.heightReference = heightReference;
  };

  updatePlaneEntity = (idPlane, plane, point, rawPosition) => {
    const entity = this.aircraftEntitiesByPlaneId[idPlane];
    if (!entity || !point) return;
    const displayModel = this.getAircraftModel(plane);
    if (entity.model) entity.model.scale = this.getAircraftDisplayScaleProperty(displayModel);

    if (!this.isRealTime3DEnabled()) {
      const model = this.getAircraftModel(plane);
      const heading = this.getHeadingFromPosition(rawPosition) + model.modelHeadingOffsetDeg;
      const displayPoint = this.isPlaneGrounded(idPlane, rawPosition)
        ? this.getParkedDisplayPoint('plane:' + idPlane, point)
        : point;
      const targetPosition = Cesium.Cartesian3.fromDegrees(
        displayPoint.lng,
        displayPoint.lat,
        this.getPlaneDisplayAltitude(idPlane, model, rawPosition)
      );

      this.applyPlaneHeightReference(entity, idPlane, rawPosition);
      this.setPlaneEntityTransform(entity, model, targetPosition, heading, idPlane, 0);
      this.aircraftMotionByPlaneId[idPlane] = {
        currentPosition: Cesium.Cartesian3.clone(targetPosition),
        currentHeading: this.normalizeHeadingDegrees(heading),
        targetPosition: Cesium.Cartesian3.clone(targetPosition),
        targetHeading: this.normalizeHeadingDegrees(heading),
        lastSampleKey: this.getMotionSampleKey(targetPosition, heading, this.getPlanePositionTime(rawPosition)),
        lastSourceTime: this.getPlanePositionTime(rawPosition) || Date.now()
      };
      this.setPlanePropellerFlying(idPlane, false, false);
      this.updateFollowCamera(idPlane, targetPosition);
      return;
    }

    const source = rawPosition && rawPosition.source;
    if (source !== 'job_event' && this.playbackSourceByPlaneId[idPlane] === 'activity') return;

    const model = this.getAircraftModel(plane);
    let heading = this.getHeadingFromPosition(rawPosition) + model.modelHeadingOffsetDeg;
    const displayPoint = this.isPlaneGrounded(idPlane, rawPosition)
      ? this.getParkedDisplayPoint('plane:' + idPlane, point)
      : point;
    const targetPosition = Cesium.Cartesian3.fromDegrees(
      displayPoint.lng,
      displayPoint.lat,
      this.getPlaneDisplayAltitude(idPlane, model, rawPosition)
    );
    this.applyPlaneHeightReference(entity, idPlane, rawPosition);

    if (source !== 'job_event') {
      const now = Date.now();
      const movementEligibility = this.getHeartbeatMovementEligibility(rawPosition, now);
      const previousMovementSample = this.heartbeatLastMovementSampleByPlaneId[idPlane] || null;
      const previousMovementTime = toNumber(previousMovementSample && previousMovementSample.sourceTime, 0);
      const sourceTime = movementEligibility.sourceTime;
      const sampleGapMs = previousMovementTime > 0 && sourceTime > previousMovementTime
        ? sourceTime - previousMovementTime
        : null;

      let movementAction = 'ACCEPT_FOR_PLAYBACK';
      let movementReason = 'RECENT_CONSECUTIVE';

      if (!movementEligibility.eligible) {
        movementAction = 'POSITION_ONLY';
        movementReason = !movementEligibility.hasValidPosition
          ? 'INVALID_POSITION'
          : !movementEligibility.sameDay
            ? 'STALE_DAY'
            : movementEligibility.ageMs !== null && movementEligibility.ageMs > heartbeatMovementFreshWindowMs
              ? 'STALE_TIME'
              : 'INVALID_TIME';
      } else if (!previousMovementSample) {
        movementAction = 'ANCHOR_ONLY';
        movementReason = 'FIRST_RECENT_HEARTBEAT';
      } else if (sourceTime <= previousMovementTime) {
        movementAction = 'IGNORE_MOVEMENT';
        movementReason = sourceTime === previousMovementTime ? 'DUPLICATE_SOURCE_TIME' : 'OUT_OF_ORDER_SOURCE_TIME';
      } else if (sampleGapMs > heartbeatMovementMaxSampleGapMs) {
        movementAction = 'ANCHOR_ONLY';
        movementReason = 'RECENT_GAP_TOO_LARGE';
      }


      if (!movementEligibility.eligible) {
        delete this.heartbeatLastMovementSampleByPlaneId[idPlane];
        this.setHeartbeatPositionOnly(
          idPlane,
          entity,
          model,
          targetPosition,
          heading,
          sourceTime,
          movementReason
        );
        return;
      }

      if (movementAction === 'IGNORE_MOVEMENT') {
        return;
      }

      if (movementAction === 'ANCHOR_ONLY') {
        this.heartbeatLastMovementSampleByPlaneId[idPlane] = {
          sourceTime: sourceTime,
          position: Cesium.Cartesian3.clone(targetPosition),
          point: movementEligibility.point
        };
        this.setHeartbeatPositionOnly(
          idPlane,
          entity,
          model,
          targetPosition,
          heading,
          sourceTime,
          movementReason
        );
        return;
      }

      this.heartbeatLastMovementSampleByPlaneId[idPlane] = {
        sourceTime: sourceTime,
        position: Cesium.Cartesian3.clone(targetPosition),
        point: movementEligibility.point
      };

    }

    const currentMotion = this.aircraftMotionByPlaneId[idPlane];
    if (source !== 'job_event' && currentMotion && currentMotion.currentPosition &&
      Cesium.Cartesian3.distance(currentMotion.currentPosition, targetPosition) >= realtimePlaybackMinimumMovementMeters) {
      const rawHeading = this.getHeadingFromPosition(rawPosition);
      const movementBearing = this.getHeadingBetweenPositions(
        currentMotion.currentPosition,
        targetPosition,
        rawHeading
      );
      heading = movementBearing + model.modelHeadingOffsetDeg;

    }

    if (source !== 'job_event' && this.shouldTeleportHeartbeat(idPlane, rawPosition, targetPosition)) {

      if (this.aircraftAnimationFrameByPlaneId[idPlane]) {
        cancelAnimationFrame(this.aircraftAnimationFrameByPlaneId[idPlane]);
        delete this.aircraftAnimationFrameByPlaneId[idPlane];
      }

      this.aircraftPlaybackQueueByPlaneId[idPlane] = [];
      delete this.playbackStartedByPlaneId[idPlane];
      delete this.playbackTimelineOffsetByPlaneId[idPlane];
      delete this.visualPlaybackSpeedByPlaneId[idPlane];

      this.playbackSourceByPlaneId[idPlane] = 'heartbeat';
      this.setPlaneEntityTransform(entity, model, targetPosition, heading, idPlane, 0);
      this.aircraftMotionByPlaneId[idPlane] = {
        currentPosition: Cesium.Cartesian3.clone(targetPosition),
        currentHeading: this.normalizeHeadingDegrees(heading),
        targetPosition: Cesium.Cartesian3.clone(targetPosition),
        targetHeading: this.normalizeHeadingDegrees(heading),
        lastSampleKey: this.getMotionSampleKey(targetPosition, heading, this.getPlanePositionTime(rawPosition)),
        lastSourceTime: this.getPlanePositionTime(rawPosition) || Date.now()
      };
      this.updateFollowCamera(idPlane, targetPosition, true);
      return;
    }

    this.animatePlaneEntity(
      idPlane,
      entity,
      model,
      targetPosition,
      heading,
      this.getPlanePositionTime(rawPosition),
      source === 'job_event' ? 'activity' : 'heartbeat'
    );
  };

  normalizeHeadingDegrees = (heading) => {
    let value = toNumber(heading, 0) % 360;
    if (value < 0) value += 360;
    return value;
  };

  interpolateHeadingDegrees = (fromHeading, toHeading, progress) => {
    const from = this.normalizeHeadingDegrees(fromHeading);
    const to = this.normalizeHeadingDegrees(toHeading);
    const difference = ((to - from + 540) % 360) - 180;
    return from + difference * progress;
  };

  getHeadingBetweenPositions = (fromPosition, toPosition, fallbackHeading) => {
    if (!fromPosition || !toPosition) return this.normalizeHeadingDegrees(fallbackHeading);
    const from = Cesium.Cartographic.fromCartesian(fromPosition);
    const to = Cesium.Cartographic.fromCartesian(toPosition);
    const deltaLongitude = to.longitude - from.longitude;
    const y = Math.sin(deltaLongitude) * Math.cos(to.latitude);
    const x = Math.cos(from.latitude) * Math.sin(to.latitude) -
      Math.sin(from.latitude) * Math.cos(to.latitude) * Math.cos(deltaLongitude);
    if (Math.abs(x) < 1e-10 && Math.abs(y) < 1e-10) {
      return this.normalizeHeadingDegrees(fallbackHeading);
    }
    return this.normalizeHeadingDegrees(Cesium.Math.toDegrees(Math.atan2(y, x)));
  };

  getPropellerAxis = (axis) => {
    if (axis === 'x') return new Cesium.Cartesian3(1, 0, 0);
    if (axis === 'z') return new Cesium.Cartesian3(0, 0, 1);
    return new Cesium.Cartesian3(0, 1, 0);
  };

  setPlanePropellerFlying = (idPlane, flying, useIdleDelay) => {
    if (idPlane <= 0) return;

    const isFlying = !!this.aircraftPropellerFlyingByPlaneId[idPlane];
    if (flying && isFlying && !this.aircraftPropellerIdleTimerByPlaneId[idPlane]) return;
    if (!flying && !useIdleDelay && !isFlying && !this.aircraftPropellerIdleTimerByPlaneId[idPlane]) return;

    if (flying) {
      if (this.aircraftPropellerIdleTimerByPlaneId[idPlane]) {
        clearTimeout(this.aircraftPropellerIdleTimerByPlaneId[idPlane]);
        delete this.aircraftPropellerIdleTimerByPlaneId[idPlane];
      }
      this.aircraftPropellerFlyingByPlaneId[idPlane] = true;
      return;
    }

    if (useIdleDelay) {
      if (this.aircraftPropellerIdleTimerByPlaneId[idPlane]) return;
      this.aircraftPropellerIdleTimerByPlaneId[idPlane] = setTimeout(() => {
        delete this.aircraftPropellerIdleTimerByPlaneId[idPlane];
        this.aircraftPropellerFlyingByPlaneId[idPlane] = false;
      }, propellerIdleStopDelayMs);
      return;
    }

    if (this.aircraftPropellerIdleTimerByPlaneId[idPlane]) {
      clearTimeout(this.aircraftPropellerIdleTimerByPlaneId[idPlane]);
      delete this.aircraftPropellerIdleTimerByPlaneId[idPlane];
    }
    this.aircraftPropellerFlyingByPlaneId[idPlane] = false;
  };

  getPropellerRotationAngle = (idPlane, groupKey, rpm, spinSign) => {
    if (!this.aircraftPropellerRuntimeByPlaneId[idPlane]) {
      this.aircraftPropellerRuntimeByPlaneId[idPlane] = {};
    }

    const runtimes = this.aircraftPropellerRuntimeByPlaneId[idPlane];
    const now = performance.now();
    const runtime = runtimes[groupKey] || { angle: 0, updatedAt: now, evaluatedAt: -1 };

    if (runtime.evaluatedAt < 0 || now - runtime.evaluatedAt >= 4) {
      if (this.aircraftPropellerFlyingByPlaneId[idPlane]) {
        const elapsedSeconds = Math.max(0, Math.min(0.1, (now - runtime.updatedAt) / 1000));
        runtime.angle = (runtime.angle + elapsedSeconds * rpm * Math.PI * 2 / 60 * spinSign) % (Math.PI * 2);
      }
      runtime.updatedAt = now;
      runtime.evaluatedAt = now;
    }
    runtimes[groupKey] = runtime;
    return runtime.angle;
  };

  createPropellerNodeTransformations = (model, idPlane) => {
    const groups = Array.isArray(model && model.propellerGroups) ? model.propellerGroups : [];
    if ((!groups.length && model?.id !== 'thrush-510') || idPlane <= 0) return undefined;

    const transforms = {};
    for (let i = 0; i < groups.length; i++) {
      const group = groups[i];
      const axis = this.getPropellerAxis(group.axis);
      const tiltAxis = group.tiltAxis ? this.getPropellerAxis(group.tiltAxis) : null;
      const tiltDeg = toNumber(group.tiltDeg, 0);
      const rpm = Math.max(0, toNumber(group.rpm, 0));
      const spinSign = toNumber(group.spinSign, 1) < 0 ? -1 : 1;
      const groupKey = String(group.id || i);
      const pivot = Array.isArray(group.pivot) && group.pivot.length >= 3
        ? Cesium.Cartesian3.fromArray(group.pivot)
        : null;
      const translationOffset = Array.isArray(group.translationOffset) && group.translationOffset.length >= 3
        ? Cesium.Cartesian3.fromArray(group.translationOffset)
        : Cesium.Cartesian3.ZERO;
      const groupScale = Array.isArray(group.scale) && group.scale.length >= 3
        ? Cesium.Cartesian3.fromArray(group.scale)
        : null;
      const nodes = Array.isArray(group.nodes) ? group.nodes : [];

      for (let j = 0; j < nodes.length; j++) {
        const nodeName = nodes[j];
        const spinScratch = new Cesium.Quaternion();
        const tiltScratch = new Cesium.Quaternion();
        const rotationScratch = new Cesium.Quaternion();
        const matrixScratch = new Cesium.Matrix3();
        const rotatedPivotScratch = new Cesium.Cartesian3();

        const resolveRotation = (result) => {
          const angle = this.getPropellerRotationAngle(idPlane, groupKey, rpm, spinSign);
          Cesium.Quaternion.fromAxisAngle(axis, angle, spinScratch);

          if (!tiltAxis || Math.abs(tiltDeg) < 1e-9) {
            return Cesium.Quaternion.clone(spinScratch, result || rotationScratch);
          }

          Cesium.Quaternion.fromAxisAngle(
            tiltAxis,
            Cesium.Math.toRadians(tiltDeg),
            tiltScratch
          );

          return Cesium.Quaternion.multiply(
            spinScratch,
            tiltScratch,
            result || rotationScratch
          );
        };

        transforms[nodeName] = new Cesium.NodeTransformationProperty({
          rotation: new Cesium.CallbackProperty((time, result) => {
            return resolveRotation(result);
          }, false),
          translation: new Cesium.CallbackProperty((time, result) => {
            const out = result || new Cesium.Cartesian3();
            if (!pivot) return Cesium.Cartesian3.clone(translationOffset, out);

            resolveRotation(rotationScratch);
            Cesium.Matrix3.fromQuaternion(rotationScratch, matrixScratch);
            Cesium.Matrix3.multiplyByVector(matrixScratch, pivot, rotatedPivotScratch);
            out.x = pivot.x - rotatedPivotScratch.x + translationOffset.x;
            out.y = pivot.y - rotatedPivotScratch.y + translationOffset.y;
            out.z = pivot.z - rotatedPivotScratch.z + translationOffset.z;
            return out;
          }, false),
          scale: groupScale
            ? new Cesium.ConstantProperty(Cesium.Cartesian3.clone(groupScale))
            : undefined
        });
      }
    }

    if (model?.id === 'thrush-510') {
      transforms.thrush510_barra_agricola_airtractor502 = new Cesium.NodeTransformationProperty({
        scale: new Cesium.ConstantProperty(new Cesium.Cartesian3(1, 1, 1.16))
      });
    }

    return Object.keys(transforms).length ? transforms : undefined;
  };

  applyPropellerAnimation = (entity, model, idPlane) => {
    if (!entity || !entity.model || idPlane <= 0) return;
    const transformations = this.createPropellerNodeTransformations(model, idPlane);
    if (transformations) {
      entity.model.nodeTransformations = transformations;
      this.setPlanePropellerFlying(idPlane, false, false);
    }
  };

  getTurnBankTargetDeg = (currentHeading, targetHeading) => {
    const headingDelta = ((
      this.normalizeHeadingDegrees(targetHeading) -
      this.normalizeHeadingDegrees(currentHeading) + 540
    ) % 360) - 180;

    if (Math.abs(headingDelta) < 0.5) return 0;

    return Math.max(
      -realtimeTurnBankMaxDeg,
      Math.min(realtimeTurnBankMaxDeg, -headingDelta * realtimeTurnBankFactor)
    );
  };

  setPlaneEntityTransform = (entity, model, position, heading, idPlane, requestedBankDeg) => {
    const normalizedHeading = this.normalizeHeadingDegrees(heading);
    const now = performance.now();
    const runtime = this.aircraftBankRuntimeByPlaneId[idPlane] || {
      heading: normalizedHeading,
      updatedAt: now,
      bankDeg: 0
    };
    const elapsedSeconds = Math.max(0.016, Math.min(0.25, (now - runtime.updatedAt) / 1000));
    const headingDelta = ((normalizedHeading - runtime.heading + 540) % 360) - 180;
    const automaticBankDeg = Math.max(
      -realtimeTurnBankMaxDeg,
      Math.min(realtimeTurnBankMaxDeg, -(headingDelta / elapsedSeconds) * realtimeTurnBankFactor)
    );
    const targetBankDegBase = Number.isFinite(requestedBankDeg)
      ? Math.max(-realtimeTurnBankMaxDeg, Math.min(realtimeTurnBankMaxDeg, requestedBankDeg))
      : automaticBankDeg;
    const targetBankDeg = Math.max(
      -realtimeTurnBankMaxDeg,
      Math.min(
        realtimeTurnBankMaxDeg,
        targetBankDegBase > 0 ? targetBankDegBase * 1.5 : targetBankDegBase
      )
    );
    const smoothing = 1 - Math.exp(-elapsedSeconds / realtimeTurnBankSmoothingSeconds);
    const bankDeg = runtime.bankDeg + (targetBankDeg - runtime.bankDeg) * smoothing;

    this.aircraftBankRuntimeByPlaneId[idPlane] = {
      heading: normalizedHeading,
      updatedAt: now,
      bankDeg: bankDeg
    };

    const flightHeadingDeg = this.normalizeHeadingDegrees(
      normalizedHeading - toNumber(model.modelHeadingOffsetDeg, 0)
    );
    this.updateFlightCompassHeading(idPlane, flightHeadingDeg);
    this.aircraftFlightHpr.heading = Cesium.Math.toRadians(flightHeadingDeg);
    this.aircraftFlightHpr.pitch = 0;
    this.aircraftFlightHpr.roll = 0;
    Cesium.Transforms.headingPitchRollQuaternion(
      position,
      this.aircraftFlightHpr,
      undefined,
      undefined,
      this.aircraftBaseOrientation
    );

    this.aircraftModelHpr.heading = Cesium.Math.toRadians(toNumber(model.modelHeadingOffsetDeg, 0));
    this.aircraftModelHpr.pitch = Cesium.Math.toRadians(toNumber(model.modelPitchOffsetDeg, 0));
    this.aircraftModelHpr.roll = Cesium.Math.toRadians(toNumber(model.modelRollOffsetDeg, 0));
    Cesium.Quaternion.fromHeadingPitchRoll(this.aircraftModelHpr, this.aircraftModelOrientation);
    Cesium.Quaternion.multiply(
      this.aircraftBaseOrientation,
      this.aircraftModelOrientation,
      this.aircraftLevelOrientation
    );

    Cesium.Transforms.eastNorthUpToFixedFrame(position, undefined, this.aircraftEnuTransform);
    Cesium.Matrix4.getMatrix3(this.aircraftEnuTransform, this.aircraftEnuRotation);
    const headingRad = Cesium.Math.toRadians(flightHeadingDeg);
    this.aircraftForwardEnu.x = Math.sin(headingRad);
    this.aircraftForwardEnu.y = Math.cos(headingRad);
    this.aircraftForwardEnu.z = 0;
    Cesium.Matrix3.multiplyByVector(
      this.aircraftEnuRotation,
      this.aircraftForwardEnu,
      this.aircraftForwardWorld
    );
    Cesium.Cartesian3.normalize(this.aircraftForwardWorld, this.aircraftForwardWorld);

    Cesium.Quaternion.fromAxisAngle(
      this.aircraftForwardWorld,
      Cesium.Math.toRadians(-bankDeg),
      this.aircraftBankOrientation
    );

    entity.position = position;
    Cesium.Quaternion.multiply(
      this.aircraftBankOrientation,
      this.aircraftLevelOrientation,
      this.aircraftFinalOrientation
    );
    const planeOrientation = this.aircraftOrientationByPlaneId[idPlane] || new Cesium.Quaternion();
    Cesium.Quaternion.clone(this.aircraftFinalOrientation, planeOrientation);
    this.aircraftOrientationByPlaneId[idPlane] = planeOrientation;
    entity.orientation = planeOrientation;
  };

  getMotionSampleKey = (position, heading, sourceTime) => {
    if (!position) return '';
    return [
      Math.round(position.x * 100) / 100,
      Math.round(position.y * 100) / 100,
      Math.round(position.z * 100) / 100,
      Math.round(this.normalizeHeadingDegrees(heading) * 10) / 10,
      toNumber(sourceTime, 0)
    ].join(':');
  };

  enqueuePlaneMotionSample = (idPlane, entity, model, targetPosition, targetHeading, sourceTime, replayActivity, playbackSource) => {
    if (!idPlane || !entity || !model || !targetPosition) return;

    let normalizedHeading = this.normalizeHeadingDegrees(targetHeading);
    const sourceType = playbackSource || (replayActivity ? 'activity' : 'heartbeat');
    if (sourceType === 'heartbeat' && this.playbackSourceByPlaneId[idPlane] === 'activity') return;
    if (!this.playbackSourceByPlaneId[idPlane]) this.playbackSourceByPlaneId[idPlane] = sourceType;
    const queue = this.aircraftPlaybackQueueByPlaneId[idPlane] || [];
    const sampleTime = toNumber(sourceTime, 0) || Date.now();
    const key = this.getMotionSampleKey(targetPosition, normalizedHeading, sampleTime);

    const lastQueued = queue.length ? queue[queue.length - 1] : null;
    const currentMotion = this.aircraftMotionByPlaneId[idPlane];
    if ((lastQueued && lastQueued.key === key) || (currentMotion && currentMotion.lastSampleKey === key)) return;
    if (!replayActivity && lastQueued && sampleTime <= toNumber(lastQueued.sourceTime, 0)) return;

    const consumedSourceTime = toNumber(currentMotion && currentMotion.lastSourceTime, 0);
    if (this.playbackStartedByPlaneId[idPlane] && consumedSourceTime > 0 && sampleTime <= consumedSourceTime) {
      if (replayActivity) this.appendJobEventActivities(idPlane, [replayActivity]);
      return;
    }

    const referencePosition = lastQueued && lastQueued.position
      ? lastQueued.position
      : currentMotion && currentMotion.targetPosition
        ? currentMotion.targetPosition
        : currentMotion && currentMotion.currentPosition;
    const referenceTime = lastQueued
      ? toNumber(lastQueued.sourceTime, 0)
      : consumedSourceTime;
    const referenceDistanceMeters = referencePosition
      ? Cesium.Cartesian3.distance(referencePosition, targetPosition)
      : 0;

    let heartbeatIntervalMs = 0;
    let heartbeatAlignmentDurationMs = 0;
    let heartbeatTravelDurationMs = 0;
    if (sourceType === 'heartbeat' && referencePosition && referenceDistanceMeters >= realtimePlaybackMinimumMovementMeters) {
      const visualHeading = currentMotion && Number.isFinite(currentMotion.currentHeading)
        ? currentMotion.currentHeading
        : normalizedHeading;
      const movementBearing = this.getHeadingBetweenPositions(
        referencePosition,
        targetPosition,
        normalizedHeading - toNumber(model && model.modelHeadingOffsetDeg, 0)
      );
      normalizedHeading = this.normalizeHeadingDegrees(
        movementBearing + toNumber(model && model.modelHeadingOffsetDeg, 0)
      );
      heartbeatAlignmentDurationMs = this.getHeartbeatAlignmentDurationMs(visualHeading, normalizedHeading);

      const rawIntervalMs = referenceTime > 0 && sampleTime > referenceTime
        ? sampleTime - referenceTime
        : heartbeatTravelDurationFallbackMs;
      heartbeatIntervalMs = rawIntervalMs >= 1000 && rawIntervalMs <= 15000
        ? rawIntervalMs
        : heartbeatTravelDurationFallbackMs;
      heartbeatTravelDurationMs = Math.max(
        heartbeatTravelDurationMinMs,
        Math.min(
          heartbeatTravelDurationMaxMs,
          heartbeatIntervalMs - heartbeatAlignmentDurationMs
        )
      );

    }

    const isRepeatedPosition = referencePosition &&
      referenceDistanceMeters < realtimePlaybackMinimumMovementMeters;

    if (isRepeatedPosition) {
      if (replayActivity) {
        if (lastQueued) {
          if (!Array.isArray(lastQueued.replayActivities)) lastQueued.replayActivities = [];
          lastQueued.replayActivities.push(replayActivity);
        } else {
          if (!this.pendingReplayActivitiesByPlaneId[idPlane]) this.pendingReplayActivitiesByPlaneId[idPlane] = [];
          this.pendingReplayActivitiesByPlaneId[idPlane].push(replayActivity);
        }
      }
      return;
    }

    if (!this.playbackTimelineOffsetByPlaneId[idPlane]) {
      this.playbackTimelineOffsetByPlaneId[idPlane] = sourceType === 'heartbeat'
        ? Date.now() + heartbeatPlaybackBufferMs - sampleTime
        : realtimePlaybackBufferMs;
    }

    const pendingReplayActivities = this.pendingReplayActivitiesByPlaneId[idPlane] || [];
    const replayActivities = pendingReplayActivities.slice();
    if (Array.isArray(replayActivity)) replayActivities.push(...replayActivity);
    else if (replayActivity) replayActivities.push(replayActivity);
    this.pendingReplayActivitiesByPlaneId[idPlane] = [];
    const scheduledAt = sampleTime + this.playbackTimelineOffsetByPlaneId[idPlane];

    if (sourceType === 'heartbeat' && queue.length) {
      queue.splice(0, queue.length);
    }

    queue.push({
      key: key,
      position: Cesium.Cartesian3.clone(targetPosition),
      heading: normalizedHeading,
      sourceTime: sampleTime,
      scheduledAt: sourceType === 'heartbeat' ? Date.now() : scheduledAt,
      replayActivities: replayActivities,
      heartbeatIntervalMs: heartbeatIntervalMs,
      heartbeatAlignmentDurationMs: heartbeatAlignmentDurationMs,
      heartbeatTravelDurationMs: heartbeatTravelDurationMs,
      heartbeatDistanceMeters: referenceDistanceMeters,
      heartbeatQueuedAt: sourceType === 'heartbeat' ? Date.now() : 0,
      heartbeatAlignmentStartedAt: 0,
      heartbeatAlignmentStartHeading: null,
      heartbeatAlignmentComplete: sourceType !== 'heartbeat' || heartbeatAlignmentDurationMs <= 0
    });

    queue.sort((a, b) => a.sourceTime - b.sourceTime);
    if (queue.length > 5000) queue.splice(0, queue.length - 5000);
    this.aircraftPlaybackQueueByPlaneId[idPlane] = queue;
    this.startPlanePlayback(idPlane, entity, model);
  };

  updateRealtimePlaybackTelemetry = (idPlane, activity) => {
    if (!activity) return;

    const previousActivity = this.jobEventLastPositionByPlaneId[idPlane];
    const previousSpeed = toNumber(this.smoothedTelemetrySpeedByPlaneId[idPlane], toNumber(previousActivity && previousActivity.speed, 0));
    const currentSpeed = toNumber(activity.speed, previousSpeed);
    const smoothedSpeed = previousSpeed > 0 && currentSpeed <= 0
      ? previousSpeed
      : previousSpeed > 0
        ? previousSpeed * 0.75 + currentSpeed * 0.25
        : currentSpeed;
    const telemetryActivity = Object.assign({}, activity, { speed: smoothedSpeed });
    this.smoothedTelemetrySpeedByPlaneId[idPlane] = smoothedSpeed;

    this.jobEventLastPositionByPlaneId[idPlane] = telemetryActivity;
    this.planePositionsById[idPlane] = telemetryActivity;

    const planeFocusItems = (this.state.planeFocusItems || []).map((item) => {
      if (this.getPlaneId(item.plane) !== toNumber(idPlane, 0)) return item;
      return Object.assign({}, item, {
        point: this.getValidPosition(telemetryActivity) || item.point,
        rawPosition: telemetryActivity
      });
    });

    this.setState({
      planeFocusItems: planeFocusItems,
      positionsByPlane: Object.assign({}, this.state.positionsByPlane, { [idPlane]: telemetryActivity })
    });
  };

  enqueueJobEventMotionSamples = (plane, idPlane, activities) => {
    const entity = this.aircraftEntitiesByPlaneId[idPlane];
    if (!entity || !Array.isArray(activities) || !activities.length) return;

    if (!this.activityOrderIdsByPlaneId[idPlane]) {
      this.activityOrderIdsByPlaneId[idPlane] = new Set();
    }

    const activityOrderIds = this.activityOrderIdsByPlaneId[idPlane];
    const receivedOrderIds = new Set();
    const normalizedItems = activities
      .map((rawActivity, originalIndex) => ({
        rawActivity: rawActivity,
        activity: this.normalizeJobEventActivity(rawActivity),
        createdTime: this.getJobEventCreatedTime(rawActivity),
        elapsedTime: toNumber(rawActivity && rawActivity.elapsetime, null),
        orderId: toNumber(rawActivity && rawActivity.order_id, 0),
        originalIndex: originalIndex
      }))
      .filter((item) => {
        if (!item.activity || item.createdTime <= 0 || !this.getValidPosition(item.activity)) {
          return false;
        }

        if (item.orderId <= 0) return true;

        if (activityOrderIds.has(item.orderId) || receivedOrderIds.has(item.orderId)) {
          return false;
        }

        receivedOrderIds.add(item.orderId);
        return true;
      })
      .sort((a, b) => {
        if (a.orderId > 0 && b.orderId > 0 && a.orderId !== b.orderId) {
          return a.orderId - b.orderId;
        }

        if (a.createdTime !== b.createdTime) return a.createdTime - b.createdTime;
        return a.originalIndex - b.originalIndex;
      });

    if (!normalizedItems.length) {

      return;
    }

    let timingState = this.activityTimingStateByPlaneId[idPlane] || null;
    for (let i = 0; i < normalizedItems.length; i++) {
      const item = normalizedItems[i];
      let effectiveSourceTime = item.createdTime;

      if (timingState) {
        const elapsedDeltaSeconds = item.elapsedTime !== null && timingState.elapsedTime !== null
          ? item.elapsedTime - timingState.elapsedTime
          : 0;
        const orderDelta = item.orderId > 0 && timingState.orderId > 0
          ? item.orderId - timingState.orderId
          : 0;

        let deltaMs = activitySampleIntervalMs;
        if (elapsedDeltaSeconds > 0 && elapsedDeltaSeconds <= 30) {
          deltaMs = Math.max(activitySampleIntervalMs, Math.round(elapsedDeltaSeconds * 1000));
        } else if (orderDelta > 0 && orderDelta <= 150) {
          deltaMs = orderDelta * activitySampleIntervalMs;
        } else if (item.createdTime > timingState.createdTime) {
          deltaMs = Math.max(activitySampleIntervalMs, item.createdTime - timingState.createdTime);
        }

        effectiveSourceTime = timingState.effectiveSourceTime + deltaMs;

        if (item.createdTime > effectiveSourceTime + 2000) {
          effectiveSourceTime = item.createdTime;
        }
      }

      item.sourceTime = effectiveSourceTime;
      timingState = {
        orderId: item.orderId,
        elapsedTime: item.elapsedTime,
        createdTime: item.createdTime,
        effectiveSourceTime: effectiveSourceTime
      };
    }
    this.activityTimingStateByPlaneId[idPlane] = timingState;
    receivedOrderIds.forEach(orderId => activityOrderIds.add(orderId));

    const switchingFromHeartbeat = this.playbackSourceByPlaneId[idPlane] === 'heartbeat';
    const queue = this.aircraftPlaybackQueueByPlaneId[idPlane] || [];
    const firstActivityPlayback = this.playbackSourceByPlaneId[idPlane] !== 'activity';

    if (switchingFromHeartbeat) {
      if (this.aircraftAnimationFrameByPlaneId[idPlane]) {
        cancelAnimationFrame(this.aircraftAnimationFrameByPlaneId[idPlane]);
        delete this.aircraftAnimationFrameByPlaneId[idPlane];
      }
      this.aircraftPlaybackQueueByPlaneId[idPlane] = [];
      delete this.playbackStartedByPlaneId[idPlane];
      delete this.playbackTimelineOffsetByPlaneId[idPlane];
      delete this.visualPlaybackSpeedByPlaneId[idPlane];
    }

    this.playbackSourceByPlaneId[idPlane] = 'activity';
    this.activityLastResponseAtByPlaneId[idPlane] = Date.now();

    let playbackItems = normalizedItems;
    if (firstActivityPlayback || !queue.length) {
      const latestTime = normalizedItems[normalizedItems.length - 1].sourceTime;
      const cutoff = latestTime - realtimePlaybackBufferMs;
      let firstIndex = normalizedItems.findIndex(item => item.sourceTime >= cutoff);
      if (firstIndex < 0) firstIndex = normalizedItems.length - 1;
      if (firstIndex > 0) firstIndex -= 1;
      playbackItems = normalizedItems.slice(firstIndex);

      const currentMotion = this.aircraftMotionByPlaneId[idPlane];
      if (switchingFromHeartbeat && currentMotion && currentMotion.currentPosition && playbackItems.length) {
        let closestIndex = 0;
        let closestDistance = Number.POSITIVE_INFINITY;
        const model = this.getAircraftModel(plane);
        for (let i = 0; i < playbackItems.length; i++) {
          const point = this.getValidPosition(playbackItems[i].activity);
          const position = Cesium.Cartesian3.fromDegrees(
            point.lng,
            point.lat,
            Math.max(0, toNumber(playbackItems[i].activity.altitude, 0)) + model.modelAltitudeOffsetMeters
          );
          const distance = Cesium.Cartesian3.distance(currentMotion.currentPosition, position);
          if (distance < closestDistance) {
            closestDistance = distance;
            closestIndex = i;
          }
        }
        if (closestDistance <= 800) playbackItems = playbackItems.slice(closestIndex);
      }
    }

    if (!playbackItems.length) return;

    if (switchingFromHeartbeat) {
      const firstPoint = this.getValidPosition(playbackItems[0].activity);
      const currentMotion = this.aircraftMotionByPlaneId[idPlane];
      if (firstPoint && currentMotion && currentMotion.currentPosition) {
        const model = this.getAircraftModel(plane);
        const firstPosition = Cesium.Cartesian3.fromDegrees(
          firstPoint.lng,
          firstPoint.lat,
          Math.max(0, toNumber(playbackItems[0].activity.altitude, 0)) + model.modelAltitudeOffsetMeters
        );
        const firstHeading = this.getHeadingFromPosition(playbackItems[0].activity) + model.modelHeadingOffsetDeg;
        this.setPlaneEntityTransform(entity, model, firstPosition, firstHeading, idPlane, 0);
        this.aircraftMotionByPlaneId[idPlane] = {
          currentPosition: Cesium.Cartesian3.clone(firstPosition),
          currentHeading: this.normalizeHeadingDegrees(firstHeading),
          targetPosition: Cesium.Cartesian3.clone(firstPosition),
          targetHeading: this.normalizeHeadingDegrees(firstHeading),
          lastSourceTime: playbackItems[0].sourceTime
        };
      }
    }

    if (!this.playbackTimelineOffsetByPlaneId[idPlane]) {
      this.playbackTimelineOffsetByPlaneId[idPlane] = Date.now() - playbackItems[0].sourceTime;
    }

    const model = this.getAircraftModel(plane);
    for (let i = 0; i < playbackItems.length; i++) {
      const item = playbackItems[i];
      const activity = item.activity;
      const point = this.getValidPosition(activity);
      const altitude = toNumber(activity.altitude, 0);
      const position = Cesium.Cartesian3.fromDegrees(
        point.lng,
        point.lat,
        Math.max(0, altitude) + model.modelAltitudeOffsetMeters
      );
      const heading = this.getHeadingFromPosition(activity) + model.modelHeadingOffsetDeg;
      this.enqueuePlaneMotionSample(
        idPlane,
        entity,
        model,
        position,
        heading,
        item.sourceTime,
        [item.rawActivity],
        'activity'
      );
    }

  };

  animatePlaneEntity = (idPlane, entity, model, targetPosition, targetHeading, sourceTime, playbackSource) => {
    const previousMotion = this.aircraftMotionByPlaneId[idPlane];
    const normalizedTargetHeading = this.normalizeHeadingDegrees(targetHeading);

    const sourceType = playbackSource || 'heartbeat';
    if (!previousMotion || !previousMotion.currentPosition) {
      this.playbackSourceByPlaneId[idPlane] = sourceType;
      this.setPlaneEntityTransform(entity, model, targetPosition, normalizedTargetHeading, idPlane);
      this.aircraftMotionByPlaneId[idPlane] = {
        currentPosition: Cesium.Cartesian3.clone(targetPosition),
        currentHeading: normalizedTargetHeading,
        targetPosition: Cesium.Cartesian3.clone(targetPosition),
        targetHeading: normalizedTargetHeading,
        lastSampleKey: this.getMotionSampleKey(targetPosition, normalizedTargetHeading, sourceTime),
        lastSourceTime: toNumber(sourceTime, 0) || Date.now()
      };
      return;
    }

    this.enqueuePlaneMotionSample(
      idPlane,
      entity,
      model,
      targetPosition,
      normalizedTargetHeading,
      sourceTime || Date.now(),
      null,
      sourceType
    );
  };

  startPlanePlayback = (idPlane, entity, model) => {
    if (this.aircraftAnimationFrameByPlaneId[idPlane]) return;

    let activeSample = null;
    let lastFrameAt = 0;
    let visualSpeedMs = toNumber(this.visualPlaybackSpeedByPlaneId[idPlane], 0);
    let lastDirection = null;
    let frameTimeDebtSeconds = 0;
    const interpolatedPosition = new Cesium.Cartesian3();
    const directionScratch = new Cesium.Cartesian3();

    const applyReachedSample = (sample) => {
      if (!sample) return;
      this.activityLastProgressAtByPlaneId[idPlane] = Date.now();

      const replayActivities = Array.isArray(sample.replayActivities) ? sample.replayActivities : [];
      if (replayActivities.length) {
        this.appendJobEventActivities(idPlane, replayActivities);
        const lastReplayActivity = replayActivities[replayActivities.length - 1];
        const replayPosition = this.normalizeJobEventActivity(lastReplayActivity);
        if (replayPosition) this.updateRealtimePlaybackTelemetry(idPlane, replayPosition);
      }
    };

    const calculateLookAheadSpeed = (motion, firstSample, queue) => {
      if (!motion || !motion.currentPosition || !firstSample) {
        return Math.max(realtimePlaybackVisualSpeedMinMs, visualSpeedMs || realtimePlaybackVisualSpeedMinMs);
      }

      if (this.playbackSourceByPlaneId[idPlane] === 'heartbeat' && toNumber(firstSample.heartbeatTravelDurationMs, 0) > 0) {
        const heartbeatDistance = Cesium.Cartesian3.distance(motion.currentPosition, firstSample.position);
        const heartbeatDurationSeconds = Math.max(0.1, firstSample.heartbeatTravelDurationMs / 1000);
        return Math.max(
          realtimePlaybackVisualSpeedMinMs,
          Math.min(realtimePlaybackVisualSpeedMaxMs, heartbeatDistance / heartbeatDurationSeconds)
        );
      }

      let previousPosition = motion.currentPosition;
      let previousTime = toNumber(motion.lastSourceTime, firstSample.sourceTime);
      let totalDistance = 0;
      let totalTimeMs = 0;
      const samples = [firstSample].concat((queue || []).slice(0, 19));

      for (let i = 0; i < samples.length; i++) {
        const sample = samples[i];
        const distance = Cesium.Cartesian3.distance(previousPosition, sample.position);
        const deltaMs = Math.max(20, sample.sourceTime - previousTime);

        totalDistance += distance;
        totalTimeMs += deltaMs;
        previousPosition = sample.position;
        previousTime = sample.sourceTime;

        if (totalTimeMs >= 3000) break;
      }

      if (totalDistance <= 0 || totalTimeMs <= 0) {
        return Math.max(realtimePlaybackVisualSpeedMinMs, visualSpeedMs || realtimePlaybackVisualSpeedMinMs);
      }

      return Math.max(
        realtimePlaybackVisualSpeedMinMs,
        Math.min(realtimePlaybackVisualSpeedMaxMs, totalDistance / (totalTimeMs / 1000))
      );
    };

    const animate = (now) => {
      if (!this.mounted || !this.viewer || this.viewer.isDestroyed() || !this.viewer.entities.contains(entity)) {
        delete this.aircraftAnimationFrameByPlaneId[idPlane];
        return;
      }

      const queue = this.aircraftPlaybackQueueByPlaneId[idPlane] || [];
      let motion = this.aircraftMotionByPlaneId[idPlane];

      if (!lastFrameAt) lastFrameAt = now;
      const rawFrameDeltaSeconds = Math.max(0, (now - lastFrameAt) / 1000);
      lastFrameAt = now;

      frameTimeDebtSeconds = Math.min(0.5, frameTimeDebtSeconds + rawFrameDeltaSeconds);
      const deltaSeconds = Math.min(0.08, frameTimeDebtSeconds);
      frameTimeDebtSeconds = Math.max(0, frameTimeDebtSeconds - deltaSeconds);

      if (!this.playbackStartedByPlaneId[idPlane] && queue.length && motion && motion.currentPosition) {
        const firstSample = queue[0];
        const lastSample = queue[queue.length - 1];
        const sourceIsActivity = this.playbackSourceByPlaneId[idPlane] === 'activity';
        const startupBufferedMs = sourceIsActivity
          ? Math.max(0, toNumber(lastSample.sourceTime, 0) - toNumber(firstSample.sourceTime, 0))
          : 0;
        const waitingForActivityBuffer = sourceIsActivity &&
          !this.playbackFinalizingByPlaneId[idPlane] &&
          startupBufferedMs < realtimePlaybackStartupBufferMs;

        if (Date.now() < firstSample.scheduledAt || waitingForActivityBuffer) {
          if (sourceIsActivity) {
            const previousQueueLog = this.activityQueueLogByPlaneId[idPlane] || {};
            if (!previousQueueLog.startupLoggedAt || now - previousQueueLog.startupLoggedAt >= activityQueueLogIntervalMs) {

              this.activityQueueLogByPlaneId[idPlane] = Object.assign({}, previousQueueLog, {
                startupLoggedAt: now
              });
            }
          }
          this.aircraftAnimationFrameByPlaneId[idPlane] = requestAnimationFrame(animate);
          return;
        }

        this.playbackStartedByPlaneId[idPlane] = true;

        this.setPlanePropellerFlying(idPlane, true, false);
      }

      if (this.playbackStartedByPlaneId[idPlane] && motion && motion.currentPosition) {
        if (!activeSample && queue.length) activeSample = queue.shift();
        this.activityActiveSampleByPlaneId[idPlane] = !!activeSample;

        if (activeSample) {
          const sourceIsHeartbeat = this.playbackSourceByPlaneId[idPlane] === 'heartbeat';

          if (sourceIsHeartbeat && !activeSample.heartbeatAlignmentComplete) {
            if (!activeSample.heartbeatAlignmentStartedAt) {
              activeSample.heartbeatAlignmentStartedAt = now;
              activeSample.heartbeatAlignmentStartHeading = motion.currentHeading;
            }

            const alignmentDurationMs = Math.max(1, toNumber(activeSample.heartbeatAlignmentDurationMs, 0));
            const alignmentElapsedMs = Math.max(0, now - activeSample.heartbeatAlignmentStartedAt);
            const alignmentProgress = Math.min(1, alignmentElapsedMs / alignmentDurationMs);
            const alignmentHeading = this.interpolateHeadingDegrees(
              activeSample.heartbeatAlignmentStartHeading,
              activeSample.heading,
              alignmentProgress
            );

            this.setPlaneEntityTransform(entity, model, motion.currentPosition, alignmentHeading, idPlane, 0);
            this.updateFollowCamera(idPlane, motion.currentPosition);
            this.aircraftMotionByPlaneId[idPlane] = Object.assign({}, motion, {
              currentHeading: alignmentHeading,
              targetHeading: activeSample.heading
            });
            motion = this.aircraftMotionByPlaneId[idPlane];

            if (alignmentProgress < 1) {
              this.aircraftAnimationFrameByPlaneId[idPlane] = requestAnimationFrame(animate);
              return;
            }

            activeSample.heartbeatAlignmentComplete = true;
          }

          const targetSpeedMs = calculateLookAheadSpeed(motion, activeSample, queue);
          const speedAlpha = 1 - Math.exp(-deltaSeconds / 0.65);
          visualSpeedMs = visualSpeedMs > 0
            ? visualSpeedMs + (targetSpeedMs - visualSpeedMs) * speedAlpha
            : targetSpeedMs;
          visualSpeedMs = Math.max(
            realtimePlaybackVisualSpeedMinMs,
            Math.min(realtimePlaybackVisualSpeedMaxMs, visualSpeedMs)
          );
          const finalizing = !!this.playbackFinalizingByPlaneId[idPlane];
          const lastQueuedSample = queue.length ? queue[queue.length - 1] : activeSample;
          const bufferedAheadMs = sourceIsHeartbeat
            ? 0
            : Math.max(0, toNumber(lastQueuedSample.sourceTime, 0) - toNumber(activeSample.sourceTime, 0));

          const playbackDelayMs = sourceIsHeartbeat
            ? 0
            : Math.max(0, Date.now() - toNumber(activeSample.sourceTime, Date.now()));
          const recoveryDelayMs = Math.max(bufferedAheadMs, playbackDelayMs);

          let requestedQueueSpeedFactor = 1;
          if (finalizing) {
            requestedQueueSpeedFactor = realtimePlaybackMaximumSpeedFactor;
          } else if (!sourceIsHeartbeat) {
            const recoveryDelaySeconds = recoveryDelayMs / 1000;
            if (recoveryDelaySeconds >= 25) requestedQueueSpeedFactor = 3.5;
            else if (recoveryDelaySeconds >= 20) requestedQueueSpeedFactor = 2.5;
            else if (recoveryDelaySeconds >= 15) requestedQueueSpeedFactor = 1.75;
            else if (recoveryDelaySeconds >= 12) requestedQueueSpeedFactor = 1.3;
            else if (recoveryDelaySeconds >= 10) requestedQueueSpeedFactor = 1.08;
            else if (bufferedAheadMs < 4 * 1000) requestedQueueSpeedFactor = 0.35;
            else if (bufferedAheadMs < 7 * 1000) requestedQueueSpeedFactor = 0.55;
            else if (bufferedAheadMs < 9 * 1000) requestedQueueSpeedFactor = 0.75;
            else if (bufferedAheadMs < realtimePlaybackBufferMs) requestedQueueSpeedFactor = 0.9;
          }

          requestedQueueSpeedFactor = Math.max(
            realtimePlaybackMinimumSpeedFactor,
            Math.min(realtimePlaybackMaximumSpeedFactor, requestedQueueSpeedFactor)
          );

          const previousQueueSpeedFactor = toNumber(
            this.queueSpeedFactorByPlaneId[idPlane],
            requestedQueueSpeedFactor
          );
          const queueSpeedSmoothingAlpha = 1 - Math.exp(-deltaSeconds / 0.2);
          const queueSpeedFactor = previousQueueSpeedFactor +
            (requestedQueueSpeedFactor - previousQueueSpeedFactor) * queueSpeedSmoothingAlpha;
          this.queueSpeedFactorByPlaneId[idPlane] = queueSpeedFactor;

          if (!sourceIsHeartbeat) {
            const queueStatus = bufferedAheadMs <= 0
              ? 'EMPTY'
              : bufferedAheadMs < realtimePlaybackLowWaterMs
                ? 'LOW'
                : bufferedAheadMs > realtimePlaybackHighWaterMs
                  ? 'HIGH'
                  : 'HEALTHY';
            const previousQueueLog = this.activityQueueLogByPlaneId[idPlane] || {};
            const shouldLogQueue = !previousQueueLog.loggedAt ||
              now - previousQueueLog.loggedAt >= activityQueueLogIntervalMs ||
              previousQueueLog.status !== queueStatus;

            if (shouldLogQueue) {

              this.activityQueueLogByPlaneId[idPlane] = {
                loggedAt: now,
                status: queueStatus
              };
            }
          }

          const effectiveVisualSpeedMs = visualSpeedMs * queueSpeedFactor;
          this.visualPlaybackSpeedByPlaneId[idPlane] = visualSpeedMs;

          if (sourceIsHeartbeat && !activeSample.heartbeatMovementStartedAt) {
            activeSample.heartbeatMovementStartedAt = now;
          }

          let distanceToTravel = effectiveVisualSpeedMs * deltaSeconds;
          let safety = 0;

          while (activeSample && distanceToTravel >= 0 && safety < 12) {
            safety += 1;
            motion = this.aircraftMotionByPlaneId[idPlane];

            const distanceToTarget = Cesium.Cartesian3.distance(motion.currentPosition, activeSample.position);

            if (distanceToTarget <= Math.max(0.01, distanceToTravel)) {
              if (distanceToTarget > 0.01) {
                Cesium.Cartesian3.subtract(activeSample.position, motion.currentPosition, directionScratch);
                lastDirection = Cesium.Cartesian3.normalize(directionScratch, new Cesium.Cartesian3());
              }

              const nextSample = queue.length ? queue[0] : null;
              const reachedBankDeg = nextSample
                ? this.getTurnBankTargetDeg(activeSample.heading, nextSample.heading)
                : 0;
              this.setPlaneEntityTransform(
                entity,
                model,
                activeSample.position,
                activeSample.heading,
                idPlane,
                reachedBankDeg
              );
              const reachedTelemetry = this.jobEventLastPositionByPlaneId[idPlane] || {};
              this.updateRealtimeSprayMist(
                idPlane,
                model,
                activeSample.position,
                activeSample.heading,
                toNumber(reachedTelemetry.active, reachedTelemetry.spray ? 1 : 0) > 0,
                reachedTelemetry.swathwidth || reachedTelemetry.swathWidth
              );
              this.updateFollowCamera(idPlane, activeSample.position);
              distanceToTravel = Math.max(0, distanceToTravel - distanceToTarget);

              this.aircraftMotionByPlaneId[idPlane] = {
                currentPosition: Cesium.Cartesian3.clone(activeSample.position),
                currentHeading: activeSample.heading,
                targetPosition: Cesium.Cartesian3.clone(activeSample.position),
                targetHeading: activeSample.heading,
                lastSampleKey: activeSample.key,
                lastSourceTime: activeSample.sourceTime
              };

              applyReachedSample(activeSample);
              activeSample = queue.length ? queue.shift() : null;
              continue;
            }

            if (distanceToTravel > 0) {
              const progress = Math.min(1, distanceToTravel / distanceToTarget);
              Cesium.Cartesian3.lerp(motion.currentPosition, activeSample.position, progress, interpolatedPosition);
              const heading = this.interpolateHeadingDegrees(motion.currentHeading, activeSample.heading, progress);

              Cesium.Cartesian3.subtract(activeSample.position, motion.currentPosition, directionScratch);
              lastDirection = Cesium.Cartesian3.normalize(directionScratch, new Cesium.Cartesian3());

              const turnBankDeg = this.getTurnBankTargetDeg(motion.currentHeading, activeSample.heading);
              this.setPlaneEntityTransform(
                entity,
                model,
                interpolatedPosition,
                heading,
                idPlane,
                turnBankDeg
              );
              const currentTelemetry = this.jobEventLastPositionByPlaneId[idPlane] || {};
              this.updateRealtimeSprayMist(
                idPlane,
                model,
                interpolatedPosition,
                heading,
                toNumber(currentTelemetry.active, currentTelemetry.spray ? 1 : 0) > 0,
                currentTelemetry.swathwidth || currentTelemetry.swathWidth
              );
              this.updateFollowCamera(idPlane, interpolatedPosition);
              this.aircraftMotionByPlaneId[idPlane] = {
                currentPosition: Cesium.Cartesian3.clone(interpolatedPosition),
                currentHeading: heading,
                targetPosition: Cesium.Cartesian3.clone(activeSample.position),
                targetHeading: activeSample.heading,
                lastSampleKey: activeSample.key,
                lastSourceTime: motion.lastSourceTime
              };

            }

            distanceToTravel = 0;
          }

        } else if (!queue.length) {
          this.activityActiveSampleByPlaneId[idPlane] = false;
          if (this.playbackSourceByPlaneId[idPlane] === 'activity') {
            const previousQueueLog = this.activityQueueLogByPlaneId[idPlane] || {};
            if (previousQueueLog.status !== 'EMPTY') {

              this.activityQueueLogByPlaneId[idPlane] = { loggedAt: now, status: 'EMPTY' };
            }
          }
          const idleTelemetry = this.jobEventLastPositionByPlaneId[idPlane] || {};
          this.updateRealtimeSprayMist(idPlane, model, motion.currentPosition, motion.currentHeading, false, idleTelemetry.swathwidth || idleTelemetry.swathWidth);

        }
      }

      if (this.playbackStartedByPlaneId[idPlane] && !activeSample && !queue.length) {
        if (this.playbackFinalizingByPlaneId[idPlane]) this.finalizeConsolidatedExecutionsForPlane(idPlane);
        this.setPlanePropellerFlying(idPlane, false, true);
      } else if (this.playbackStartedByPlaneId[idPlane]) {
        this.setPlanePropellerFlying(idPlane, true, false);
      }

      this.activityActiveSampleByPlaneId[idPlane] = !!activeSample;
      this.aircraftAnimationFrameByPlaneId[idPlane] = requestAnimationFrame(animate);
    };

    this.aircraftAnimationFrameByPlaneId[idPlane] = requestAnimationFrame(animate);
  };

  clearRealtimeSprayMist = (idPlane) => {
    const runtime = this.sprayMistRuntimeByPlaneId[idPlane];
    if (!runtime) return;
    const entities = Array.isArray(runtime.entities) ? runtime.entities : [];
    for (let i = 0; i < entities.length; i++) {
      if (this.viewer && !this.viewer.isDestroyed() && entities[i]) {
        this.viewer.entities.remove(entities[i]);
      }
    }
    delete this.sprayMistRuntimeByPlaneId[idPlane];
  };

  ensureRealtimeSprayMist = (idPlane) => {
    if (!this.viewer || this.viewer.isDestroyed()) return null;
    if (this.sprayMistRuntimeByPlaneId[idPlane]) return this.sprayMistRuntimeByPlaneId[idPlane];

    const trails = Array.from({ length: realtimeSprayMistLineCount }, () => []);
    const entities = [];
    for (let i = 0; i < realtimeSprayMistLineCount; i++) {
      const center = (realtimeSprayMistLineCount - 1) / 2;
      const centerWeight = 1 - Math.abs(i - center) / Math.max(1, center);
      const color = Cesium.Color.WHITE.withAlpha(0.28 + centerWeight * 0.3);
      const material = new Cesium.PolylineGlowMaterialProperty({
        glowPower: 0.45,
        color: color
      });
      const entity = this.viewer.entities.add({
        name: 'Névoa de aplicação em tempo real',
        show: false,
        polyline: {
          positions: new Cesium.CallbackProperty(() => trails[i].length >= 2 ? trails[i] : [], false),
          width: 2.2 + centerWeight * 1.8,
          material: material,
          arcType: Cesium.ArcType.NONE,
          clampToGround: false,
          shadows: Cesium.ShadowMode.DISABLED
        }
      });
      entities.push(entity);
    }

    const runtime = {
      trails: trails,
      entities: entities,
      lastUpdateAt: 0,
      lastCenterPosition: null,
      active: false
    };
    this.sprayMistRuntimeByPlaneId[idPlane] = runtime;
    return runtime;
  };

  updateRealtimeSprayMist = (idPlane, model, position, heading, spraying, swathWidth) => {
    const runtime = this.ensureRealtimeSprayMist(idPlane);
    if (!runtime || !position) return;

    if (!spraying) {
      if (runtime.active) {
        runtime.active = false;
        runtime.lastCenterPosition = null;
        for (let i = 0; i < runtime.trails.length; i++) runtime.trails[i] = [];
        for (let i = 0; i < runtime.entities.length; i++) runtime.entities[i].show = false;
      }
      return;
    }

    const now = performance.now();
    if (now - runtime.lastUpdateAt < realtimeSprayMistUpdateIntervalMs) return;
    if (runtime.lastCenterPosition && Cesium.Cartesian3.distance(runtime.lastCenterPosition, position) < realtimeSprayMistTrailSpacingMeters) return;
    runtime.lastUpdateAt = now;
    runtime.lastCenterPosition = Cesium.Cartesian3.clone(position);
    runtime.active = true;

    const width = Math.max(0.1, toNumber(swathWidth, 24));
    const cartographic = Cesium.Cartographic.fromCartesian(position);
    const baseHeight = cartographic.height + toNumber(model && model.sprayMistVerticalOffsetMeters, -1.4);
    let centerPoint = {
      lat: Cesium.Math.toDegrees(cartographic.latitude),
      lng: Cesium.Math.toDegrees(cartographic.longitude)
    };
    const aircraftBearing = this.normalizeHeadingDegrees(heading - toNumber(model && model.modelHeadingOffsetDeg, 0));
    const backOffset = toNumber(model && model.sprayMistBackOffsetMeters, 0);
    if (backOffset) centerPoint = this.getOffsetLatLng(centerPoint, backOffset, aircraftBearing + 180);
    const sideBearing = this.normalizeHeadingDegrees(aircraftBearing + 90);

    for (let i = 0; i < realtimeSprayMistLineCount; i++) {
      const normalized = realtimeSprayMistLineCount <= 1 ? 0 : (i / (realtimeSprayMistLineCount - 1)) * 2 - 1;
      const lateralPoint = this.getOffsetLatLng(centerPoint, normalized * width * 0.48, sideBearing);
      const mistPosition = Cesium.Cartesian3.fromDegrees(lateralPoint.lng, lateralPoint.lat, baseHeight);
      const trail = runtime.trails[i];
      trail.push(mistPosition);
      if (trail.length > realtimeSprayMistMaxTrailPoints) trail.splice(0, trail.length - realtimeSprayMistMaxTrailPoints);
      runtime.entities[i].show = trail.length >= 2;
    }
  };

  getPlaneDgpsId = (plane) => {
    if (!plane) return 0;

    const candidates = [plane.id_dgps, plane.idDgps, plane.idDGPS];
    for (let i = 0; i < candidates.length; i++) {
      const id = toNumber(candidates[i], 0);
      if (id > 0) return id;
    }

    return 0;
  };

  hasPlaneDgps = (plane) => {
    return this.getPlaneDgpsId(plane) > 0;
  };

  handlePlanePositionRequest = (plane) => {
    if (!plane) return Promise.resolve(null);

    const idPlane = this.getPlaneId(plane);
    const idDgps = this.getPlaneDgpsId(plane);
    const cachedPosition = this.planePositionsById[idPlane] || null;

    if (idDgps <= 0) {
      return Promise.resolve(cachedPosition || (this.getValidPosition(plane) ? plane : null));
    }

    const apiUrl = `${Config.server}/plane_position/${idDgps}`;
    const options = {
      method: 'GET',
      headers: {
        'Authorization': localStorage.getItem('access-token-jwt')
      }
    };

    return fetch(apiUrl, options)
      .then((res) => {
        if (res.status !== 200) return null;
        return res.json();
      })
      .then((result) => {
        const currentPosition = Array.isArray(result)
          ? (result.length > 0 ? result[0] : null)
          : result;
        const currentPoint = this.getValidPosition(currentPosition);
        if (currentPoint) {
          this.heartbeatLastResponseAtByPlaneId[idPlane] = Date.now();
          return currentPosition;
        }

        return cachedPosition || (this.getValidPosition(plane) ? plane : null);
      })
      .catch(() => cachedPosition || (this.getValidPosition(plane) ? plane : null));
  };

  getAircraftCloseRangeScaleFactor = () => {
    const rangeMeters = Math.max(0, toNumber(this.state.cameraRangeMeters, aircraftCloseRangeScaleStartMeters));
    if (rangeMeters >= aircraftCloseRangeScaleStartMeters) return 1;
    if (rangeMeters <= aircraftCloseRangeScaleMinimumMeters) return aircraftCloseRangeMinimumScaleFactor;

    const ratio = (rangeMeters - aircraftCloseRangeScaleMinimumMeters) /
      (aircraftCloseRangeScaleStartMeters - aircraftCloseRangeScaleMinimumMeters);
    return aircraftCloseRangeMinimumScaleFactor + (1 - aircraftCloseRangeMinimumScaleFactor) * ratio;
  };

  getAircraftDisplayScaleProperty = (model) => {
    return new Cesium.CallbackProperty(() => {
      return Math.max(0.0001, toNumber(model && model.scale, 1) * this.getAircraftCloseRangeScaleFactor());
    }, false);
  };

  getAircraftDisplayMinimumPixelSizeProperty = (model) => {
    return new Cesium.CallbackProperty(() => {
      const baseMinimumPixelSize = Math.max(0, toNumber(model && model.minimumPixelSize, 0));
      if (baseMinimumPixelSize <= 0) return 0;
      const scaleFactor = this.getAircraftCloseRangeScaleFactor();
      return Math.max(3, baseMinimumPixelSize * Math.max(0.2, scaleFactor));
    }, false);
  };

  getParkedDisplayPoint = (key, fallbackPoint) => {
    const point = this.parkedDisplayPointByKey && this.parkedDisplayPointByKey[key];
    return point || fallbackPoint;
  };

  rebuildParkedDisplayPoints = (planes, positionsByPlane, jobs, areasByJob, weatherStations) => {
    const parkedItems = [];
    const planeById = this.buildPlaneById(planes);
    const drawnPlanes = {};

    for (let i = 0; i < planes.length; i++) {
      const plane = planeById[this.getPlaneId(planes[i])] || planes[i];
      const idPlane = this.getPlaneId(plane);
      if (idPlane <= 0 || drawnPlanes[idPlane]) continue;
      drawnPlanes[idPlane] = true;

      const rawPosition = positionsByPlane && positionsByPlane[idPlane]
        ? positionsByPlane[idPlane]
        : (this.planePositionsById[idPlane] || plane);
      if (!this.isPlaneGrounded(idPlane, rawPosition)) continue;
      const point = this.getValidPosition(rawPosition) || this.getPlaneFallbackPosition(plane, jobs, areasByJob);
      if (!point) continue;
      parkedItems.push({ key: 'plane:' + idPlane, point: point });
    }

    const safeWeatherStations = Array.isArray(weatherStations) ? weatherStations : [];
    for (let i = 0; i < safeWeatherStations.length; i++) {
      const station = safeWeatherStations[i];
      const stationId = this.getWeatherStationId(station);
      const point = this.getWeatherStationPoint(station);
      if (!stationId || !point) continue;
      parkedItems.push({ key: 'station:' + stationId, point: point });
    }

    const clusters = [];
    const assigned = {};
    for (let i = 0; i < parkedItems.length; i++) {
      if (assigned[i]) continue;
      const cluster = [parkedItems[i]];
      assigned[i] = true;
      let expanded = true;
      while (expanded) {
        expanded = false;
        for (let j = 0; j < parkedItems.length; j++) {
          if (assigned[j]) continue;
          const touchesCluster = cluster.some(item =>
            this.getDistanceBetweenPointsMeters(item.point, parkedItems[j].point) <= parkedEntityOverlapThresholdMeters
          );
          if (touchesCluster) {
            assigned[j] = true;
            cluster.push(parkedItems[j]);
            expanded = true;
          }
        }
      }
      clusters.push(cluster);
    }

    const displayPoints = {};
    for (let i = 0; i < clusters.length; i++) {
      const cluster = clusters[i];
      if (cluster.length <= 1) {
        displayPoints[cluster[0].key] = cluster[0].point;
        continue;
      }

      const center = cluster.reduce((acc, item) => ({
        lat: acc.lat + item.point.lat / cluster.length,
        lng: acc.lng + item.point.lng / cluster.length
      }), { lat: 0, lng: 0 });

      const ordered = cluster.slice().sort((a, b) => String(a.key).localeCompare(String(b.key)));
      for (let j = 0; j < ordered.length; j++) {
        const ringIndex = Math.floor(j / parkedEntityItemsPerRing);
        const itemIndex = j % parkedEntityItemsPerRing;
        const itemsInRing = Math.min(parkedEntityItemsPerRing, ordered.length - ringIndex * parkedEntityItemsPerRing);
        const radiusMeters = parkedEntityFirstRingRadiusMeters + ringIndex * parkedEntityRingStepMeters;
        const bearingDeg = (360 / Math.max(1, itemsInRing)) * itemIndex + ringIndex * 22.5;
        displayPoints[ordered[j].key] = this.getOffsetLatLng(center, radiusMeters, bearingDeg);
      }
    }

    this.parkedDisplayPointByKey = displayPoints;
  };

  drawOnlineMap = (jobs, planes, areasByJob, positionsByPlane, weatherStations) => {
    if (!this.viewer || this.viewer.isDestroyed()) return { focusItems: [], planeFocusItems: [], stationFocusItems: [] };
    this.rebuildParkedDisplayPoints(planes, positionsByPlane, jobs, areasByJob, weatherStations);
    const aircraftEntities = Object.keys(this.aircraftEntitiesByPlaneId || {})
      .map((idPlane) => this.aircraftEntitiesByPlaneId[idPlane])
      .filter(Boolean);
    const aircraftEntitySet = new Set(aircraftEntities);
    const weatherStationEntities = Object.keys(this.weatherStationEntitiesById || {}).map((id) => this.weatherStationEntitiesById[id]).filter(Boolean);
    const weatherStationCoverageEntities = Object.keys(this.weatherStationCoverageEntitiesById || {})
      .map((id) => this.weatherStationCoverageEntitiesById[id])
      .filter(Boolean);
    const weatherStationWindEntities = [];
    Object.keys(this.weatherStationWindEntitiesById || {}).forEach((id) => {
      const entities = Array.isArray(this.weatherStationWindEntitiesById[id]) ? this.weatherStationWindEntitiesById[id] : [];
      for (let i = 0; i < entities.length; i++) {
        if (entities[i]) weatherStationWindEntities.push(entities[i]);
      }
    });
    const weatherStationEntitySet = new Set(weatherStationEntities.concat(weatherStationCoverageEntities, weatherStationWindEntities));
    const sprayMistEntitySet = new Set();
    Object.keys(this.sprayMistRuntimeByPlaneId || {}).forEach((idPlane) => {
      const runtime = this.sprayMistRuntimeByPlaneId[idPlane];
      const entities = runtime && Array.isArray(runtime.entities) ? runtime.entities : [];
      for (let i = 0; i < entities.length; i++) sprayMistEntitySet.add(entities[i]);
    });
    const currentEntities = this.viewer.entities.values.slice();

    for (let i = 0; i < currentEntities.length; i++) {
      if (!aircraftEntitySet.has(currentEntities[i]) && !weatherStationEntitySet.has(currentEntities[i]) && !sprayMistEntitySet.has(currentEntities[i])) {
        this.viewer.entities.remove(currentEntities[i]);
      }
    }

    this.aircraftEntities = aircraftEntities;
    this.applicationGeometryKeys = {};
    this.jobEventEntitiesByPlaneId = {};
    this.jobVisualEntitiesByKey = {};
    this.jobSelectionOutlineEntitiesByKey = {};

    let bounds = [];
    const focusItems = [];
    const planeFocusItems = [];
    const stationFocusItems = [];
    const planeById = this.buildPlaneById(planes);
    const planesByJob = {};
    const planeJobsById = {};

    for (let i = 0; i < jobs.length; i++) {
      const job = jobs[i];
      const areas = areasByJob[job.id] || [];
      const entitiesBeforeJob = new Set(this.viewer.entities.values.slice());
      const areaPoints = this.drawJobAreas(job, areas);
      const jobVisualKey = this.getJobSelectionKey({ type: 'job', job: job });
      this.jobVisualEntitiesByKey[jobVisualKey] = this.viewer.entities.values.filter((entity) => !entitiesBeforeJob.has(entity));
      bounds = bounds.concat(areaPoints);

      const servicePoint = this.getJobServicePosition(job, areas);
      if (!servicePoint) continue;

      const jobPlanes = this.getJobPlanes(job, planeById);
      planesByJob[job.id] = jobPlanes;

      for (let j = 0; j < jobPlanes.length; j++) {
        const idPlane = toNumber(jobPlanes[j].id, 0);
        if (idPlane <= 0) continue;
        if (!planeJobsById[idPlane]) planeJobsById[idPlane] = [];
        planeJobsById[idPlane].push(job);
      }

      const planeNames = jobPlanes.map((plane) => this.getPlaneDisplayName(plane)).filter(Boolean);
      const focusLabel = this.getJobFocusLabel(job, planeNames);
      const focusBounds = areaPoints.length ? areaPoints.concat([servicePoint]) : [servicePoint];

      focusItems.push({
        type: 'job',
        job: job,
        point: servicePoint,
        bounds: focusBounds,
        label: focusLabel,
        planeNames: planeNames,
        applicationReport: areas.applicationReport || null,
        telemetryReport: areas.telemetryReport || null,
        climateSummary: areas.climateSummary || null
      });
    }

    if (this.isRealTime3DEnabled()) {
      const realtimeFocusItems = this.getRealtimeExecutionFocusItems(planes);
      for (let i = 0; i < realtimeFocusItems.length; i++) {
        focusItems.push(realtimeFocusItems[i]);
        bounds = bounds.concat(realtimeFocusItems[i].bounds || []);
      }
    }

    const drawnPlanes = {};
    for (let i = 0; i < planes.length; i++) {
      const plane = planeById[this.getPlaneId(planes[i])] || planes[i];
      const idPlane = this.getPlaneId(plane);
      if (idPlane <= 0 || drawnPlanes[idPlane]) continue;

      const rawPosition = positionsByPlane && positionsByPlane[idPlane]
        ? positionsByPlane[idPlane]
        : (this.planePositionsById[idPlane] || plane);
      const currentPoint = this.getValidPosition(rawPosition) || this.getPlaneFallbackPosition(plane, jobs, areasByJob);
      if (!currentPoint) continue;

      drawnPlanes[idPlane] = true;
      bounds.push(currentPoint);
      this.drawPlane(plane, currentPoint, rawPosition, null);

      const planeJobs = planeJobsById[idPlane] || [];
      let planeBounds = [currentPoint];
      for (let j = 0; j < planeJobs.length; j++) {
        const areas = areasByJob[planeJobs[j].id] || [];
        for (let k = 0; k < areas.length; k++) {
          planeBounds = planeBounds.concat(this.parseAreaPoints(areas[k]));
        }
      }

      planeFocusItems.push({
        type: 'plane',
        plane: plane,
        point: currentPoint,
        bounds: planeBounds,
        label: this.getPlaneDisplayName(plane),
        jobs: planeJobs,
        rawPosition: rawPosition
      });
    }


    const safeWeatherStations = Array.isArray(weatherStations) ? weatherStations : [];
    const activeStationIds = {};
    for (let i = 0; i < safeWeatherStations.length; i++) {
      const station = safeWeatherStations[i];
      const point = this.getWeatherStationPoint(station);
      if (!point) continue;
      const stationId = this.getWeatherStationId(station);
      if (!stationId) continue;
      activeStationIds[stationId] = true;
      bounds.push(point);
      this.drawWeatherStation(station, point);
      stationFocusItems.push({
        type: 'station',
        station: station,
        point: point,
        bounds: [point],
        label: this.getWeatherStationName(station)
      });
    }

    Object.keys(this.weatherStationEntitiesById || {}).forEach((id) => {
      if (activeStationIds[id]) return;
      const entity = this.weatherStationEntitiesById[id];
      if (entity && this.viewer.entities.contains(entity)) this.viewer.entities.remove(entity);
      delete this.weatherStationEntitiesById[id];
      this.removeWeatherStationCoverageVisuals(id);
    });

    const orderedPlaneFocusItems = this.applyInitialPlaneFocusOrder(planeFocusItems);

    const safeIndex = this.getSafeJobIndex(this.state.currentJobIndex, focusItems.length);
    const safePlaneIndex = this.getSafeJobIndex(this.state.currentPlaneIndex, orderedPlaneFocusItems.length);
    const safeStationIndex = this.getSafeJobIndex(this.state.currentStationIndex, stationFocusItems.length);

    if (!this.initialCameraFocusApplied) {
      if (focusItems[safeIndex]) {
        this.flyToJobFocus(focusItems[safeIndex]);
      } else if (orderedPlaneFocusItems[safePlaneIndex]) {
        this.flyToJobFocus(orderedPlaneFocusItems[safePlaneIndex]);
      } else if (stationFocusItems[safeStationIndex]) {
        this.flyToJobFocus(stationFocusItems[safeStationIndex]);
      } else {
        this.flyToPoints(bounds);
      }
      this.initialCameraFocusApplied = true;
    }

    this.viewer.scene.requestRender();
    return { focusItems: focusItems, planeFocusItems: orderedPlaneFocusItems, stationFocusItems: stationFocusItems };
  };


  getWeatherStationId = (station) => {
    if (!station) return 0;
    return toNumber(station.id_source !== undefined ? station.id_source : (station.source_id !== undefined ? station.source_id : station.id), 0);
  };

  getWeatherStationName = (station) => {
    const res = this.props.res || {};
    if (!station) return res.ESTACAO;
    return station.source_name || station.name || ((res.ESTACAO) + ' ' + this.getWeatherStationId(station));
  };

  getWeatherStationPoint = (station) => {
    if (!station) return null;
    const lat = toNumber(station.latAverage !== undefined ? station.latAverage : station.lat, null);
    const lng = toNumber(station.lngAverage !== undefined ? station.lngAverage : station.lng, null);
    if (!this.isValidLatLng(lat, lng)) return null;
    return { lat: lat, lng: lng };
  };

  getWeatherStationCameraTarget = (focusItem) => {
    if (!focusItem || !focusItem.point) return null;

    const station = focusItem.station || {};
    const stationId = this.getWeatherStationId(station);
    const entity = this.weatherStationEntitiesById[stationId];
    if (entity && entity.position) {
      const entityPosition = entity.position.getValue
        ? entity.position.getValue(Cesium.JulianDate.now())
        : entity.position;
      if (entityPosition) return entityPosition;
    }

    return Cesium.Cartesian3.fromDegrees(
      focusItem.point.lng,
      focusItem.point.lat,
      weatherStationVehicleAltitudeOffsetMeters
    );
  };

  getWeatherStationOffsetPosition = (point, bearingDeg, distanceMeters, altitudeMeters = 2) => {
    if (!point) return null;
    const earthRadiusMeters = 6378137;
    const latitude = Cesium.Math.toRadians(toNumber(point.lat, 0));
    const longitude = Cesium.Math.toRadians(toNumber(point.lng, 0));
    const bearing = Cesium.Math.toRadians(toNumber(bearingDeg, 0));
    const angularDistance = Math.max(0, toNumber(distanceMeters, 0)) / earthRadiusMeters;

    const destinationLatitude = Math.asin(
      Math.sin(latitude) * Math.cos(angularDistance) +
      Math.cos(latitude) * Math.sin(angularDistance) * Math.cos(bearing)
    );
    const destinationLongitude = longitude + Math.atan2(
      Math.sin(bearing) * Math.sin(angularDistance) * Math.cos(latitude),
      Math.cos(angularDistance) - Math.sin(latitude) * Math.sin(destinationLatitude)
    );

    return Cesium.Cartesian3.fromRadians(
      destinationLongitude,
      destinationLatitude,
      Math.max(0, toNumber(altitudeMeters, 0))
    );
  };

  buildWeatherStationCoverageOutlinePositions = (point) => {
    const positions = [];
    const segmentCount = 96;
    for (let i = 0; i <= segmentCount; i++) {
      const bearing = (i / segmentCount) * 360;
      const position = this.getWeatherStationOffsetPosition(point, bearing, weatherStationCoverageRadiusMeters, 1);
      if (position) positions.push(position);
    }
    return positions;
  };

  getWeatherStationWindBearing = (station) => {
    const direction = this.getOptionalNumber(station && station.wind_direction);
    if (direction === null || direction === -99) return null;
    return (direction + 180 + 360) % 360;
  };

  removeWeatherStationCoverageVisuals = (stationId) => {
    if (!this.viewer || this.viewer.isDestroyed()) return;

    const coverageEntity = this.weatherStationCoverageEntitiesById[stationId];
    if (coverageEntity && this.viewer.entities.contains(coverageEntity)) {
      this.viewer.entities.remove(coverageEntity);
    }
    delete this.weatherStationCoverageEntitiesById[stationId];

    const windEntities = Array.isArray(this.weatherStationWindEntitiesById[stationId])
      ? this.weatherStationWindEntitiesById[stationId]
      : [];
    for (let i = 0; i < windEntities.length; i++) {
      if (windEntities[i] && this.viewer.entities.contains(windEntities[i])) {
        this.viewer.entities.remove(windEntities[i]);
      }
    }
    delete this.weatherStationWindEntitiesById[stationId];
    delete this.weatherStationVisualStateById[stationId];
  };

  ensureWeatherStationCoverageVisuals = (station, point) => {
    if (!this.viewer || this.viewer.isDestroyed() || !point) return;
    const stationId = this.getWeatherStationId(station);
    if (!stationId) return;

    const stationOnline = station && station.online === true;
    const windBearing = stationOnline ? this.getWeatherStationWindBearing(station) : null;
    const windSpeedMetersPerSecond = this.getOptionalNumber(station && station.wind_speed);
    const coverageColorCss = stationOnline
      ? (this.getWeatherDeltaTConditionColor(station && station.delta_t) || '#f28c28')
      : '#f28c28';
    const coverageColor = Cesium.Color.fromCssColorString(coverageColorCss);
    this.weatherStationVisualStateById[stationId] = {
      point: { lat: point.lat, lng: point.lng },
      windBearing: windBearing,
      coverageColorCss: coverageColorCss,
      windSpeedMetersPerSecond: stationOnline && windSpeedMetersPerSecond !== null && windSpeedMetersPerSecond !== -99
        ? Math.max(0, windSpeedMetersPerSecond)
        : 0
    };

    let coverageEntity = this.weatherStationCoverageEntitiesById[stationId];
    const stationPosition = Cesium.Cartesian3.fromDegrees(point.lng, point.lat, 0);
    const outlinePositions = this.buildWeatherStationCoverageOutlinePositions(point);

    if (!coverageEntity || !this.viewer.entities.contains(coverageEntity)) {
      coverageEntity = this.viewer.entities.add({
        name: this.getWeatherStationName(station) + ' - 5 km',
        position: stationPosition,
        ellipse: {
          semiMajorAxis: weatherStationCoverageRadiusMeters,
          semiMinorAxis: weatherStationCoverageRadiusMeters,
          material: coverageColor.withAlpha(this.state.weatherStationCoverageOpacity),
          heightReference: Cesium.HeightReference.CLAMP_TO_GROUND,
          zIndex: 0
        },
        polyline: {
          positions: outlinePositions,
          width: weatherStationCoverageOutlineWidth,
          material: coverageColor.withAlpha(weatherStationCoverageOutlineAlpha),
          clampToGround: true
        }
      });
      this.weatherStationCoverageEntitiesById[stationId] = coverageEntity;
    } else {
      coverageEntity.position = stationPosition;
      if (coverageEntity.ellipse) coverageEntity.ellipse.material = coverageColor.withAlpha(this.state.weatherStationCoverageOpacity);
      if (coverageEntity.polyline) {
        coverageEntity.polyline.positions = outlinePositions;
        coverageEntity.polyline.material = coverageColor.withAlpha(weatherStationCoverageOutlineAlpha);
      }
    }

    let windEntities = this.weatherStationWindEntitiesById[stationId];
    if (!Array.isArray(windEntities) || windEntities.length !== weatherStationWindArrowCount) {
      const oldEntities = Array.isArray(windEntities) ? windEntities : [];
      for (let i = 0; i < oldEntities.length; i++) {
        if (oldEntities[i] && this.viewer.entities.contains(oldEntities[i])) this.viewer.entities.remove(oldEntities[i]);
      }

      windEntities = [];
      for (let arrowIndex = 0; arrowIndex < weatherStationWindArrowCount; arrowIndex++) {
        const trackIndex = Math.floor(arrowIndex / weatherStationWindArrowsPerTrack);
        const arrowInTrack = arrowIndex % weatherStationWindArrowsPerTrack;
        const trackRatio = weatherStationWindTrackCount <= 1
          ? 0
          : ((trackIndex / (weatherStationWindTrackCount - 1)) * 2) - 1;
        const crossTrackMeters = trackRatio * weatherStationCoverageRadiusMeters * weatherStationWindCrossTrackFactor;
        const halfChordMeters = Math.sqrt(Math.max(0,
          weatherStationCoverageRadiusMeters * weatherStationCoverageRadiusMeters -
          crossTrackMeters * crossTrackMeters
        )) * 0.96;

        const getArrowCartesianPosition = () => {
          const visualState = this.weatherStationVisualStateById[stationId];
          if (!visualState || visualState.windBearing === null) return undefined;

          const windSpeedKmh = Math.max(0, toNumber(visualState.windSpeedMetersPerSecond, 0) * 3.6);
          const animationDurationMs = Math.max(
            weatherStationWindAnimationDurationMinMs,
            Math.min(
              weatherStationWindAnimationDurationMaxMs,
              32000 / Math.sqrt(Math.max(0.5, windSpeedKmh))
            )
          );
          const animationPhase = windSpeedKmh <= 0.05
            ? 0
            : (Date.now() % animationDurationMs) / animationDurationMs;
          const trackPhase = trackIndex % 2 === 0 ? 0 : 0.5 / weatherStationWindArrowsPerTrack;
          const phase = (animationPhase + (arrowInTrack / weatherStationWindArrowsPerTrack) + trackPhase) % 1;
          const alongTrackMeters = -halfChordMeters + phase * (halfChordMeters * 2);
          const radialDistanceMeters = Math.sqrt(
            alongTrackMeters * alongTrackMeters + crossTrackMeters * crossTrackMeters
          );
          const bearingOffsetDeg = Cesium.Math.toDegrees(Math.atan2(crossTrackMeters, alongTrackMeters));
          const bearingDeg = (visualState.windBearing + bearingOffsetDeg + 360) % 360;

          return this.getWeatherStationOffsetPosition(
            visualState.point,
            bearingDeg,
            radialDistanceMeters,
            8
          );
        };

        const arrowPosition = new Cesium.CallbackProperty(() => getArrowCartesianPosition(), false);

        const arrowRotation = new Cesium.CallbackProperty(() => {
          const visualState = this.weatherStationVisualStateById[stationId];
          if (!visualState || visualState.windBearing === null || !this.viewer || this.viewer.isDestroyed()) return 0;

          const currentPosition = getArrowCartesianPosition();
          if (!currentPosition) return Cesium.Math.toRadians(-visualState.windBearing);

          const cartographic = Cesium.Cartographic.fromCartesian(currentPosition);
          if (!cartographic) return Cesium.Math.toRadians(-visualState.windBearing);

          const currentPoint = {
            lat: Cesium.Math.toDegrees(cartographic.latitude),
            lng: Cesium.Math.toDegrees(cartographic.longitude)
          };
          const targetPosition = this.getWeatherStationOffsetPosition(
            currentPoint,
            visualState.windBearing,
            250,
            8
          );

          const scene = this.viewer.scene;
          const currentScreen = scene.cartesianToCanvasCoordinates(currentPosition, new Cesium.Cartesian2());
          const targetScreen = scene.cartesianToCanvasCoordinates(targetPosition, new Cesium.Cartesian2());
          if (!currentScreen || !targetScreen) return Cesium.Math.toRadians(-visualState.windBearing);

          const deltaX = targetScreen.x - currentScreen.x;
          const deltaY = targetScreen.y - currentScreen.y;
          if (Math.abs(deltaX) < 0.01 && Math.abs(deltaY) < 0.01) {
            return Cesium.Math.toRadians(-visualState.windBearing);
          }

          return -Math.atan2(deltaX, -deltaY);
        }, false);

        windEntities.push(this.viewer.entities.add({
          name: this.getWeatherStationName(station) + ' - vento',
          show: windBearing !== null,
          position: arrowPosition,
          billboard: {
            image: weatherStationWindArrowImage,
            width: 30,
            height: 30,
            rotation: arrowRotation,
            alignedAxis: Cesium.Cartesian3.ZERO,
            horizontalOrigin: Cesium.HorizontalOrigin.CENTER,
            verticalOrigin: Cesium.VerticalOrigin.CENTER,
            disableDepthTestDistance: Number.POSITIVE_INFINITY,
            heightReference: Cesium.HeightReference.CLAMP_TO_GROUND
          }
        }));
      }
      this.weatherStationWindEntitiesById[stationId] = windEntities;
    }

    for (let i = 0; i < windEntities.length; i++) {
      if (windEntities[i]) windEntities[i].show = windBearing !== null;
    }
  };

  updateWeatherStationCoverageVisibility = () => {
    const filteredStations = this.getFilteredStationFocusItems();
    const selectedFocusItem = filteredStations.length ? this.getSelectedStationFocusItem() : null;
    const selectedStationId = selectedFocusItem
      ? String(this.getWeatherStationId(selectedFocusItem.station) || '')
      : '';
    const hasVisibleStation = filteredStations.length > 0 && selectedStationId !== '';

    Object.keys(this.weatherStationCoverageEntitiesById || {}).forEach((stationId) => {
      const entity = this.weatherStationCoverageEntitiesById[stationId];
      if (!entity) return;
      entity.show = hasVisibleStation && String(stationId) === selectedStationId;
    });

    Object.keys(this.weatherStationWindEntitiesById || {}).forEach((stationId) => {
      const visualState = this.weatherStationVisualStateById[stationId];
      const stationSelected = hasVisibleStation && String(stationId) === selectedStationId;
      const shouldShowWind = stationSelected && !!visualState && visualState.windBearing !== null;
      const windEntities = Array.isArray(this.weatherStationWindEntitiesById[stationId])
        ? this.weatherStationWindEntitiesById[stationId]
        : [];
      for (let i = 0; i < windEntities.length; i++) {
        if (windEntities[i]) windEntities[i].show = shouldShowWind;
      }
    });
  };

  drawWeatherStation = (station, point) => {
    if (!this.props.hasWeatherStations || !this.viewer || this.viewer.isDestroyed() || !point) return null;
    const stationId = this.getWeatherStationId(station);
    if (!stationId) return null;
    const labelText = this.getWeatherStationName(station);
    const displayPoint = this.getParkedDisplayPoint('station:' + stationId, point);
    const position = Cesium.Cartesian3.fromDegrees(
      displayPoint.lng,
      displayPoint.lat,
      weatherStationVehicleAltitudeOffsetMeters
    );
    const heading = toNumber(station.heading, 0) + weatherStationVehicleHeadingOffsetDeg;
    const hpr = new Cesium.HeadingPitchRoll(
      Cesium.Math.toRadians(heading),
      Cesium.Math.toRadians(weatherStationVehiclePitchOffsetDeg),
      Cesium.Math.toRadians(weatherStationVehicleRollOffsetDeg)
    );
    this.ensureWeatherStationCoverageVisuals(station, point);
    let entity = this.weatherStationEntitiesById[stationId];

    if (entity && this.viewer.entities.contains(entity)) {
      entity._stationSelectionId = stationId;
      entity.name = labelText;
      entity.position = position;
      entity.orientation = Cesium.Transforms.headingPitchRollQuaternion(position, hpr);
      if (entity.model) entity.model.heightReference = Cesium.HeightReference.CLAMP_TO_GROUND;
      if (entity.label) {
        entity.label.text = labelText;
        entity.label.heightReference = Cesium.HeightReference.CLAMP_TO_GROUND;
      }
      return entity;
    }

    entity = this.viewer.entities.add({
      name: labelText,
      position: position,
      orientation: Cesium.Transforms.headingPitchRollQuaternion(position, hpr),
      model: {
        uri: weatherStationVehicleModel,
        scale: weatherStationVehicleScale,
        minimumPixelSize: weatherStationVehicleMinimumPixelSize,
        maximumScale: weatherStationVehicleMaximumScale,
        heightReference: Cesium.HeightReference.CLAMP_TO_GROUND
      },
      label: {
        text: labelText,
        font: '14px sans-serif',
        fillColor: Cesium.Color.WHITE,
        outlineColor: Cesium.Color.BLACK,
        outlineWidth: 3,
        style: Cesium.LabelStyle.FILL_AND_OUTLINE,
        verticalOrigin: Cesium.VerticalOrigin.BOTTOM,
        pixelOffset: new Cesium.Cartesian2(0, -30),
        heightReference: Cesium.HeightReference.CLAMP_TO_GROUND
      }
    });
    entity._stationSelectionId = stationId;
    this.weatherStationEntitiesById[stationId] = entity;
    return entity;
  };

  buildPlaneById = (planes) => {
    const map = {};
    const catalog = this.getPlaneCatalog();
    const allPlanes = (Array.isArray(planes) ? planes : []).concat(catalog);

    for (let i = 0; i < allPlanes.length; i++) {
      const item = allPlanes[i];
      const id = this.getPlaneId(item);
      if (id <= 0) continue;

      if (!map[id]) {
        map[id] = item;
      } else {
        map[id] = Object.assign({}, map[id], item);
      }
    }

    return map;
  };

  getJobPlanes = (job, planeById) => {
    const ret = [];
    const added = {};
    const lists = ['list_exe', 'list_plan', 'list_analise'];

    for (let l = 0; l < lists.length; l++) {
      const arr = Array.isArray(job[lists[l]]) ? job[lists[l]] : [];
      for (let i = 0; i < arr.length; i++) {
        const item = arr[i] || {};
        const idPlane = toNumber(item.id_plane || item.idPlane || item.plane_id, 0);
        if (idPlane <= 0 || added[idPlane]) continue;

        const planeFromList = planeById[idPlane] || this.findPlaneById(idPlane) || {};
        const merged = Object.assign({}, item, planeFromList, {
          id: idPlane,
          name: planeFromList.name || item.plane_name || item.plane || (this.props.res.AERONAVE + ' ' + idPlane),
          plane_name: planeFromList.name || item.plane_name || item.plane || '',
          job_plane_name: item.plane_name || item.plane || ''
        });

        if (!this.hasPlaneDgps(merged)) continue;

        added[idPlane] = true;
        ret.push(merged);
      }
    }

    return ret;
  };

  getPlaneDisplayName = (plane) => {
    if (!plane) return '';
    return plane.name || (this.getPlaneId(plane) ? (this.props.res.AERONAVE + ' ' + this.getPlaneId(plane)) : '');
  };

  getJobDisplayName = (job) => {
    if (!job) return '';
    return job.title || job.name || (this.props.res.TRABALHO + ' ' + job.id);
  };

  getJobFocusLabel = (job, planeNames) => {
    const jobName = this.getJobDisplayName(job);
    const planeText = planeNames && planeNames.length ? planeNames.join(', ') : this.props.res.AERONAVE_NAO_INFORMADA;
    return jobName + ' - ' + planeText;
  };

  getSafeJobIndex = (index, total) => {
    if (!total || total <= 0) return 0;
    const parsed = toNumber(index, 0);
    if (parsed < 0) return 0;
    if (parsed >= total) return total - 1;
    return parsed;
  };

  getJobServicePosition = (job, areas) => {
    let points = [];
    for (let i = 0; i < areas.length; i++) {
      points = points.concat(this.parseAreaPoints(areas[i]));
    }

    const center = this.getCenterFromPoints(points);
    if (center) return center;

    return this.getJobFallbackPosition(job);
  };

  getJobFallbackPosition = (job) => {
    const candidates = [];
    const addCandidates = (item) => {
      if (!item) return;
      candidates.push({ lat: item.center_lat, lng: item.center_lng });
      candidates.push({ lat: item.hangar_lat, lng: item.hangar_lng });
      candidates.push({ lat: item.lat, lng: item.lng });
      candidates.push({ lat: item.latitude, lng: item.longitude });
    };

    addCandidates(job);

    const lists = ['list_exe', 'list_plan', 'list_analise'];
    for (let i = 0; i < lists.length; i++) {
      const arr = Array.isArray(job[lists[i]]) ? job[lists[i]] : [];
      for (let j = 0; j < arr.length; j++) addCandidates(arr[j]);
    }

    for (let k = 0; k < candidates.length; k++) {
      const lat = toNumber(candidates[k].lat, null);
      const lng = toNumber(candidates[k].lng, null);
      if (this.isValidLatLng(lat, lng)) return { lat: lat, lng: lng };
    }

    return null;
  };

  getJobSelectionKeyFromJob = (job) => this.getJobSelectionKey({ type: 'job', job: job });

  tagJobMapEntity = (entity, job) => {
    if (!entity || !job) return entity;
    const key = this.getJobSelectionKeyFromJob(job);
    if (key) entity._jobSelectionKey = key;
    return entity;
  };

  addJobSelectionOutline = (points, job) => {
    if (!this.viewer || !job || !Array.isArray(points) || points.length < 3) return null;
    const positions = [];
    for (let i = 0; i < points.length; i++) {
      if (!this.isValidLatLng(points[i].lat, points[i].lng)) continue;
      positions.push(Cesium.Cartesian3.fromDegrees(points[i].lng, points[i].lat, 1));
    }
    if (positions.length < 3) return null;
    positions.push(Cesium.Cartesian3.clone(positions[0]));

    const key = this.getJobSelectionKeyFromJob(job);
    if (!key) return null;
    const color = Cesium.Color.fromCssColorString('#ff2d2d');
    const entity = this.viewer.entities.add({
      polyline: {
        positions: positions,
        width: 5,
        material: color,
        depthFailMaterial: color,
        clampToGround: true
      },
      show: false
    });
    entity._jobSelectionKey = key;
    entity._jobSelectionHighlight = true;
    if (!this.jobSelectionOutlineEntitiesByKey[key]) this.jobSelectionOutlineEntitiesByKey[key] = [];
    this.jobSelectionOutlineEntitiesByKey[key].push(entity);
    return entity;
  };

  updateSelectedJobHighlight = () => {
    if (!this.viewer || this.viewer.isDestroyed()) return;
    const focusItems = this.getFilteredJobFocusItems();
    const index = this.getSafeJobIndex(this.state.currentJobIndex, focusItems.length);
    const selected = this.state.cameraFocusType === 'job' ? focusItems[index] : null;
    const selectedKey = selected ? this.getJobSelectionKey(selected) : '';

    Object.keys(this.jobSelectionOutlineEntitiesByKey || {}).forEach((key) => {
      const show = !!selectedKey && key === selectedKey;
      const entities = this.jobSelectionOutlineEntitiesByKey[key] || [];
      for (let i = 0; i < entities.length; i++) {
        if (entities[i]) entities[i].show = show;
      }
    });
    this.viewer.scene.requestRender();
  };

  selectPickedJob = (keys) => {
    if (!Array.isArray(keys) || !keys.length) return false;
    const focusItems = this.getFilteredJobFocusItems();
    if (!focusItems.length) return false;

    const currentIndex = this.getSafeJobIndex(this.state.currentJobIndex, focusItems.length);
    const currentKey = focusItems[currentIndex] ? this.getJobSelectionKey(focusItems[currentIndex]) : '';
    const currentPickIndex = keys.indexOf(currentKey);
    let targetKey = keys[currentPickIndex >= 0 && keys.length > 1 ? (currentPickIndex + 1) % keys.length : 0];
    let targetIndex = focusItems.findIndex((item) => this.getJobSelectionKey(item) === targetKey);

    if (targetIndex < 0) {
      targetKey = keys.find((key) => focusItems.some((item) => this.getJobSelectionKey(item) === key));
      targetIndex = targetKey ? focusItems.findIndex((item) => this.getJobSelectionKey(item) === targetKey) : -1;
    }
    if (targetIndex < 0) return false;

    this.cameraFollowEnabled = false;
    this.cameraFollowPlaneId = 0;
    this.setState(
      { currentJobIndex: targetIndex, cameraFocusType: 'job', freeMode: false, desktopExecutedStationVisible: false, desktopStationManuallyHidden: false },
      this.updateSelectedJobHighlight
    );
    return true;
  };

  selectPickedPlane = (planeIds) => {
    if (!Array.isArray(planeIds) || !planeIds.length) return false;
    const focusItems = this.getFilteredPlaneFocusItems();
    if (!focusItems.length) return false;

    const currentIndex = this.getSafeJobIndex(this.state.currentPlaneIndex, focusItems.length);
    const currentId = focusItems[currentIndex] && focusItems[currentIndex].plane
      ? this.getPlaneId(focusItems[currentIndex].plane)
      : 0;
    const currentPickIndex = planeIds.indexOf(currentId);
    const orderedIds = currentPickIndex >= 0 && planeIds.length > 1
      ? planeIds.slice(currentPickIndex + 1).concat(planeIds.slice(0, currentPickIndex + 1))
      : planeIds;

    let targetIndex = -1;
    for (let i = 0; i < orderedIds.length && targetIndex < 0; i++) {
      targetIndex = focusItems.findIndex((item) =>
        item && item.plane && this.getPlaneId(item.plane) === orderedIds[i]
      );
    }
    if (targetIndex < 0) return false;

    this.cameraFollowEnabled = false;
    this.cameraFollowPlaneId = 0;
    this.setState(
      { currentPlaneIndex: targetIndex, cameraFocusType: 'plane', freeMode: false },
      this.updateSelectedJobHighlight
    );
    return true;
  };

  selectPickedStation = (stationIds) => {
    if (!Array.isArray(stationIds) || !stationIds.length) return false;
    const focusItems = this.getFilteredStationFocusItems();
    if (!focusItems.length) return false;

    const currentIndex = this.getSafeJobIndex(this.state.currentStationIndex, focusItems.length);
    const currentId = focusItems[currentIndex] && focusItems[currentIndex].station
      ? this.getWeatherStationId(focusItems[currentIndex].station)
      : 0;
    const currentPickIndex = stationIds.indexOf(currentId);
    const orderedIds = currentPickIndex >= 0 && stationIds.length > 1
      ? stationIds.slice(currentPickIndex + 1).concat(stationIds.slice(0, currentPickIndex + 1))
      : stationIds;

    let targetIndex = -1;
    for (let i = 0; i < orderedIds.length && targetIndex < 0; i++) {
      targetIndex = focusItems.findIndex((item) =>
        item && item.station && this.getWeatherStationId(item.station) === orderedIds[i]
      );
    }
    if (targetIndex < 0) return false;

    const nextState = { currentStationIndex: targetIndex, freeMode: false };
    if (this.state.mobileCompact) nextState.cameraFocusType = 'station';

    this.cameraFollowEnabled = false;
    this.cameraFollowPlaneId = 0;
    this.setState(nextState, () => {
      this.updateWeatherStationCoverageVisibility();
      this.updateSelectedJobHighlight();
    });
    return true;
  };

  handleMapEntityClick = (position) => {
    if (!position || !this.viewer || this.viewer.isDestroyed()) return;
    const picked = this.viewer.scene.drillPick(position, 30) || [];
    const planeIds = [];
    const stationIds = [];
    const jobKeys = [];

    for (let i = 0; i < picked.length; i++) {
      const entity = picked[i] && picked[i].id;
      if (!entity) continue;

      const planeId = toNumber(entity._planeSelectionId, 0);
      if (planeId > 0 && planeIds.indexOf(planeId) < 0) planeIds.push(planeId);

      const stationId = toNumber(entity._stationSelectionId, 0);
      if (stationId > 0 && stationIds.indexOf(stationId) < 0) stationIds.push(stationId);

      const jobKey = entity._jobSelectionKey;
      if (jobKey && jobKeys.indexOf(jobKey) < 0) jobKeys.push(jobKey);
    }

    if (planeIds.length && this.selectPickedPlane(planeIds)) return;
    if (stationIds.length && this.selectPickedStation(stationIds)) return;
    if (jobKeys.length) this.selectPickedJob(jobKeys);
  };

  drawJobAreas = (job, areas) => {
    let allPoints = [];
    const safeAreas = Array.isArray(areas) ? areas : [];
    const planningAreas = Array.isArray(safeAreas.planningAreas) && safeAreas.planningAreas.length
      ? safeAreas.planningAreas
      : safeAreas;
    const executionAreas = Array.isArray(safeAreas.executionAreas) ? safeAreas.executionAreas : [];
    const applicationReport = safeAreas.applicationReport || null;
    const planningJob = safeAreas.planningJob || null;
    const hasExecution = this.hasJobExecution(job);

    this.drawSavedPlanningRoute(job, planningJob);

    if (hasExecution) {
      const boundaryPoints = this.drawExecutedJobBoundaryPolygons(job, planningAreas, applicationReport);
      if (boundaryPoints.length) {
        allPoints = allPoints.concat(boundaryPoints);
      }
    }

    const reportPoints = this.drawApplicationReport(job, applicationReport);
    let applicationPoints = reportPoints;

    if (!applicationPoints.length && executionAreas.length) {
      applicationPoints = this.drawJobApplicationAreas(job, executionAreas);
    }

    const hasApplication = applicationPoints.length > 0;
    if (hasApplication) {
      allPoints = allPoints.concat(applicationPoints);
    }

    if (!hasExecution) {
      for (let i = 0; i < planningAreas.length; i++) {
        const area = planningAreas[i];
        const points = this.parseAreaPoints(area);
        if (!points.length) continue;
        allPoints = allPoints.concat(points);

        if (points.length >= 3) this.addJobSelectionOutline(points, job);

        if (hasApplication) {
          continue;
        }

        if (points.length < 3) {
          this.addPointLabel(points[0], area.name || job.title || (this.props.res.TRABALHO + ' ' + job.id));
          continue;
        }

        const degrees = [];
        for (let j = 0; j < points.length; j++) {
          degrees.push(points[j].lng, points[j].lat);
        }

        const entity = this.viewer.entities.add({
          name: area.name || job.title || (this.props.res.TRABALHO + ' ' + job.id),
          polygon: {
            hierarchy: Cesium.Cartesian3.fromDegreesArray(degrees),
            material: Cesium.Color.fromCssColorString('#0a7eb4').withAlpha(0.42),
            outline: true,
            outlineColor: Cesium.Color.fromCssColorString('#0a7eb4'),
            perPositionHeight: false,
            heightReference: Cesium.HeightReference.CLAMP_TO_GROUND,
            zIndex: 10
          }
        });
        this.tagJobMapEntity(entity, job);
        this.addJobPolygonLabel(points, job);
      }
    }

    return allPoints;
  };

  getSavedPlanningLineSet = (poly) => {
    if (!poly) return null;

    if (Array.isArray(poly.lines)) {
      const selectedLine = toNumber(poly.selected_line, 0);
      if (poly.lines[selectedLine]) return poly.lines[selectedLine];
      if (poly.lines.length) return poly.lines[poly.lines.length - 1];
    }

    if (poly.lines && Array.isArray(poly.lines.itens)) {
      const items = poly.lines.itens;
      const lineSets = items.length && Array.isArray(items[0]) ? items[0] : items;
      if (!lineSets.length) return null;

      const info = poly.info || {};
      const selectedLine = toNumber(info.selected_line, lineSets.length - 1);
      return lineSets[selectedLine] || lineSets[lineSets.length - 1] || null;
    }

    return null;
  };

  collectSavedPlanningLineCoordinates = (value, output, depth) => {
    if (depth > 8 || value === null || value === undefined) return;

    if (typeof value === 'string') {
      const text = value.trim();
      if (!text) return;
      try {
        this.collectSavedPlanningLineCoordinates(JSON.parse(text), output, depth + 1);
      } catch (e) { }
      return;
    }

    if (Array.isArray(value)) {
      if (value.length >= 2 && typeof value[0] === 'number' && typeof value[1] === 'number') return;
      for (let i = 0; i < value.length; i++) {
        this.collectSavedPlanningLineCoordinates(value[i], output, depth + 1);
      }
      return;
    }

    if (typeof value !== 'object') return;

    if (value.type === 'LineString' && Array.isArray(value.coordinates)) {
      output.push(value.coordinates);
      return;
    }

    if (value.type === 'MultiLineString' && Array.isArray(value.coordinates)) {
      for (let i = 0; i < value.coordinates.length; i++) {
        if (Array.isArray(value.coordinates[i])) output.push(value.coordinates[i]);
      }
      return;
    }

    if (value.geometry) this.collectSavedPlanningLineCoordinates(value.geometry, output, depth + 1);
    if (Array.isArray(value.features)) this.collectSavedPlanningLineCoordinates(value.features, output, depth + 1);
  };

  normalizeSavedPlanningABPoint = (point) => {
    if (!Array.isArray(point) || point.length < 2) return null;
    const lng = toNumber(point[0], null);
    const lat = toNumber(point[1], null);
    if (!this.isValidLatLng(lat, lng)) return null;
    return { lat: lat, lng: lng };
  };

  getSavedPlanningAB = (poly, lineSet) => {
    if (!poly || !lineSet || !lineSet.ABs) return null;

    const info = poly.info || poly;
    const abs = lineSet.ABs;
    const selectedAB = toNumber(info.selected_AB, 0);
    const selectedABBack = toNumber(info.selected_AB_back, 0);
    const selectedOut = toNumber(info.selected_Out, -1);
    const strategy = String(info.estrategia || '').toUpperCase();
    const specialStrategy = selectedOut === 4 || selectedOut === 5 || selectedOut === 7 ||
      strategy === 'REVERSERACETRACK' || strategy === 'EXPAND' || strategy === 'SQUEEZE';
    let coords = null;

    if (specialStrategy) {
      if (abs.centerAB && Array.isArray(abs.centerAB.c)) {
        coords = selectedAB !== 0 ? abs.centerAB.c : [abs.centerAB.c[1], abs.centerAB.c[0]];
      } else if (selectedABBack === 0 || selectedAB === 1) {
        coords = abs.centerAB1 && abs.centerAB1.c;
      } else if (abs.centerAB2 && Array.isArray(abs.centerAB2.c)) {
        coords = [abs.centerAB2.c[1], abs.centerAB2.c[0]];
      }
    } else if (selectedABBack === 0) {
      if (selectedAB === 0) {
        coords = abs.startAB && abs.startAB.c;
      } else if (abs.startAB && Array.isArray(abs.startAB.c)) {
        coords = [abs.startAB.c[1], abs.startAB.c[0]];
      }
    } else if (selectedAB === 0) {
      coords = abs.endAB && abs.endAB.c;
    } else if (abs.endAB && Array.isArray(abs.endAB.c)) {
      coords = [abs.endAB.c[1], abs.endAB.c[0]];
    }

    if (!Array.isArray(coords) || coords.length < 2) return null;

    const a = this.normalizeSavedPlanningABPoint(coords[0]);
    const b = this.normalizeSavedPlanningABPoint(coords[1]);
    return a && b ? { a: a, b: b } : null;
  };

  addSavedPlanningLine = (coordinates, name) => {
    if (!this.viewer || !Array.isArray(coordinates) || coordinates.length < 2) return;

    const degrees = [];
    for (let i = 0; i < coordinates.length; i++) {
      const point = this.normalizeSavedPlanningABPoint(coordinates[i]);
      if (!point) continue;
      degrees.push(point.lng, point.lat);
    }

    if (degrees.length < 4) return;

    this.viewer.entities.add({
      name: name,
      polyline: {
        positions: Cesium.Cartesian3.fromDegreesArray(degrees),
        width: 3,
        material: Cesium.Color.fromCssColorString('#ee5151').withAlpha(0.95),
        clampToGround: true,
        zIndex: 3
      }
    });
  };

  addSavedPlanningAB = (ab, name) => {
    if (!this.viewer || !ab || !ab.a || !ab.b) return;

    this.viewer.entities.add({
      name: name + ' AB',
      polyline: {
        positions: Cesium.Cartesian3.fromDegreesArray([
          ab.a.lng, ab.a.lat,
          ab.b.lng, ab.b.lat
        ]),
        width: 5,
        material: Cesium.Color.fromCssColorString('#f5c749'),
        clampToGround: true,
        zIndex: 4
      }
    });

    const addMarker = (point, image, label) => {
      this.viewer.entities.add({
        name: name + ' ' + label,
        position: Cesium.Cartesian3.fromDegrees(point.lng, point.lat, 0),
        billboard: {
          image: image,
          width: 40,
          height: 40,
          verticalOrigin: Cesium.VerticalOrigin.BOTTOM,
          heightReference: Cesium.HeightReference.CLAMP_TO_GROUND,
          disableDepthTestDistance: Number.POSITIVE_INFINITY
        }
      });
    };

    addMarker(ab.a, iconA, 'A');
    addMarker(ab.b, iconB, 'B');
  };

  getSavedPlanningPolygons = (planningData) => {
    if (!planningData) return [];

    if (planningData.state && Array.isArray(planningData.state.polygons)) {
      return planningData.state.polygons;
    }

    if (planningData.job && Array.isArray(planningData.job.polys)) {
      return planningData.job.polys;
    }

    if (Array.isArray(planningData.polygons)) return planningData.polygons;
    if (Array.isArray(planningData.polys)) return planningData.polys;

    return [];
  };

  drawSavedPlanningRoute = (job, planningData) => {
    if (!this.viewer || !planningData) return;

    const polygons = this.getSavedPlanningPolygons(planningData);
    if (!polygons.length) return;

    for (let i = 0; i < polygons.length; i++) {
      const poly = polygons[i];
      if (!poly) continue;

      const info = poly.info || poly;
      const type = String(info.type || 'N').toUpperCase();
      if (type === 'X' || type === 'XL' || type === 'T' || type === 'F') continue;

      const lineSet = this.getSavedPlanningLineSet(poly);
      if (!lineSet) continue;

      const name = info.name || job.title || (this.props.res.TRABALHO + ' ' + job.id);
      const lines = Array.isArray(lineSet.lines) ? lineSet.lines : [];

      for (let j = 0; j < lines.length; j++) {
        const coordinates = [];
        this.collectSavedPlanningLineCoordinates(lines[j] && lines[j].linhas, coordinates, 0);
        for (let k = 0; k < coordinates.length; k++) {
          this.addSavedPlanningLine(coordinates[k], name);
        }
      }

      const ab = this.getSavedPlanningAB(poly, lineSet);
      if (ab) this.addSavedPlanningAB(ab, name);
    }
  };

  drawExecutedJobBoundaryPolygons = (job, planningAreas, applicationReport) => {
    const allPoints = [];
    const reportPolygons = applicationReport && Array.isArray(applicationReport.polygons)
      ? applicationReport.polygons
      : [];
    const drawnKeys = {};

    for (let i = 0; i < reportPolygons.length; i++) {
      const item = reportPolygons[i];
      if (!item || String(item.type || '').toUpperCase() === 'T') continue;

      const geometries = [];
      const source = item.poly || item.polygon || item.geojson || null;
      this.collectApplicationGeometries(source, geometries, 0);

      for (let j = 0; j < geometries.length; j++) {
        const geometry = geometries[j];
        if (!geometry || geometry.type !== 'polygon' || !Array.isArray(geometry.points) || geometry.points.length < 3) continue;

        const key = geometry.points.map((point) =>
          point.lat.toFixed(7) + ',' + point.lng.toFixed(7)
        ).join('|');
        if (!key || drawnKeys[key]) continue;
        drawnKeys[key] = true;

        const name = item.name || item.title || job.title || (this.props.res.TRABALHO + ' ' + job.id);
        if (this.addExecutedJobBoundaryPolygon(geometry.points, name, job)) {
          allPoints.push(...geometry.points);
        }
      }
    }

    if (allPoints.length) return allPoints;

    const safePlanningAreas = Array.isArray(planningAreas) ? planningAreas : [];
    for (let i = 0; i < safePlanningAreas.length; i++) {
      const area = safePlanningAreas[i];
      const points = this.parseAreaPoints(area);
      if (points.length < 3) continue;

      const name = area.name || job.title || (this.props.res.TRABALHO + ' ' + job.id);
      if (this.addExecutedJobBoundaryPolygon(points, name, job)) {
        allPoints.push(...points);
      }
    }

    return allPoints;
  };

  addExecutedJobBoundaryPolygon = (points, name, job) => {
    if (!this.viewer || !Array.isArray(points) || points.length < 3) return null;

    const degrees = [];
    for (let i = 0; i < points.length; i++) {
      if (!this.isValidLatLng(points[i].lat, points[i].lng)) continue;
      degrees.push(points[i].lng, points[i].lat);
    }

    if (degrees.length < 6) return null;

    const entity = this.viewer.entities.add({
      name: name,
      polygon: {
        hierarchy: Cesium.Cartesian3.fromDegreesArray(degrees),
        material: Cesium.Color.fromCssColorString('#0a7eb4').withAlpha(0.38),
        outline: true,
        outlineColor: Cesium.Color.fromCssColorString('#0a7eb4'),
        perPositionHeight: false,
        heightReference: Cesium.HeightReference.CLAMP_TO_GROUND,
        zIndex: 10
      }
    });
    this.tagJobMapEntity(entity, job);
    this.addJobSelectionOutline(points, job);
    this.addJobPolygonLabel(points, job);
    return entity;
  };

  drawApplicationReport = (job, report) => {
    let allPoints = [];
    const applicationItems = this.getApplicationReportItems(report);


    if (!applicationItems.length) {
      return allPoints;
    }

    for (let i = 0; i < applicationItems.length; i++) {
      const item = applicationItems[i];
      if (!item) continue;

      const geometries = [];
      const reportGeometry = item.poly || item.polygon || item.geojson || item;

      this.collectApplicationGeometries(reportGeometry, geometries, 0);


      for (let j = 0; j < geometries.length; j++) {
        const geometry = geometries[j];
        if (!geometry || !Array.isArray(geometry.points) || geometry.points.length < 2) continue;

        const name = item.title || item.name || job.title || (this.props.res.TRABALHO + ' ' + job.id);

        if (geometry.type === 'line' && geometry.points.length >= 2) {
          const width = this.getApplicationWidth(item, job);
          const strip = this.buildApplicationStripFromLine(geometry.points, width);
          if (strip && strip.length >= 3 && !this.hasApplicationGeometryDrawn(strip)) {
            this.addApplicationPolygon(strip, name, job);
            allPoints = allPoints.concat(strip);
          }
        } else if (geometry.points.length >= 3 && !this.hasApplicationGeometryDrawn(geometry.points)) {
          this.addApplicationPolygon(geometry.points, name, job);
          allPoints = allPoints.concat(geometry.points);
        }
      }
    }

    return allPoints;
  };

  getApplicationReportItems = (report) => {
    const items = [];
    const visited = [];

    const collect = (value, depth) => {
      if (depth > 7 || value === null || value === undefined) return;

      if (typeof value === 'string') {
        const text = value.trim();
        if (!text) return;
        try {
          collect(JSON.parse(text), depth + 1);
        } catch (e) { }
        return;
      }

      if (typeof value !== 'object') return;
      if (visited.indexOf(value) >= 0) return;
      visited.push(value);

      const applicationKeys = [
        'aplicacao', 'aplicacoes', 'application', 'applications', 'app',
        'spray', 'sprays', 'faixas', 'strips', 'swaths'
      ];

      for (let i = 0; i < applicationKeys.length; i++) {
        const current = value[applicationKeys[i]];
        if (!Array.isArray(current) || !current.length) continue;

        for (let j = 0; j < current.length; j++) {
          if (current[j]) items.push(current[j]);
        }
      }

      const nestedKeys = ['apl', 'result', 'resultado', 'output', 'data', 'report', 'relatorio', 'quality', 'analysis', 'execution'];
      for (let i = 0; i < nestedKeys.length; i++) {
        if (value[nestedKeys[i]] !== undefined) collect(value[nestedKeys[i]], depth + 1);
      }
    };

    collect(report, 0);
    return items;
  };

  drawJobApplicationAreas = (job, areas) => {
    let allPoints = [];
    if (!Array.isArray(areas) || !areas.length) return allPoints;

    for (let i = 0; i < areas.length; i++) {
      const area = areas[i];
      const geometries = this.getApplicationGeometriesFromArea(area);

      for (let j = 0; j < geometries.length; j++) {
        const geometry = geometries[j];
        if (!geometry || !Array.isArray(geometry.points) || geometry.points.length < 2) continue;

        if (geometry.type === 'line' && geometry.points.length >= 2) {
          const width = this.getApplicationWidth(area, job);
          const strip = this.buildApplicationStripFromLine(geometry.points, width);
          if (strip && strip.length >= 3) {
            this.addApplicationPolygon(strip, area.name || job.title || (this.props.res.TRABALHO + ' ' + job.id), job);
            allPoints = allPoints.concat(strip);
          }
        } else if (geometry.points.length >= 3) {
          this.addApplicationPolygon(geometry.points, area.name || job.title || (this.props.res.TRABALHO + ' ' + job.id), job);
          allPoints = allPoints.concat(geometry.points);
        }
      }
    }

    return allPoints;
  };

  hasApplicationGeometryDrawn = (points) => {
    if (!Array.isArray(points) || !points.length) return true;

    const key = points.map((point) => {
      const lat = toNumber(point && point.lat, null);
      const lng = toNumber(point && point.lng, null);

      if (!this.isValidLatLng(lat, lng)) return '';
      return lat.toFixed(7) + ',' + lng.toFixed(7);
    }).filter(Boolean).join('|');

    if (!key) return true;

    if (!this.applicationGeometryKeys) {
      this.applicationGeometryKeys = {};
    }

    if (this.applicationGeometryKeys[key]) {
      return true;
    }

    this.applicationGeometryKeys[key] = true;
    return false;
  };

  addApplicationPolygon = (points, name, job) => {
    if (!this.viewer || !Array.isArray(points) || points.length < 3) return null;

    const degrees = [];
    for (let i = 0; i < points.length; i++) {
      if (!this.isValidLatLng(points[i].lat, points[i].lng)) continue;
      degrees.push(points[i].lng, points[i].lat);
    }

    if (degrees.length < 6) return null;

    const entity = this.viewer.entities.add({
      name: name,
      polygon: {
        hierarchy: Cesium.Cartesian3.fromDegreesArray(degrees),
        material: Cesium.Color.fromCssColorString('#32a852').withAlpha(0.9),
        outline: true,
        outlineColor: Cesium.Color.fromCssColorString('#32a852'),
        perPositionHeight: false,
        heightReference: Cesium.HeightReference.CLAMP_TO_GROUND,
        zIndex: 11
      }
    });
    return this.tagJobMapEntity(entity, job);
  };

  getApplicationWidth = (area, job) => {
    const candidates = [
      area && area.faixa,
      area && area.width,
      area && area.largura,
      area && area.setLargura,
      job && job.faixa,
      job && job.width,
      job && job.largura
    ];

    for (let i = 0; i < candidates.length; i++) {
      const value = toNumber(candidates[i], null);
      if (value && value > 0) return value;
    }

    return 30;
  };

  getApplicationGeometriesFromArea = (area) => {
    const ret = [];
    if (!area) return ret;

    const fields = [
      area.application,
      area.applications,
      area.app,
      area.aplicacao,
      area.aplicacoes,
      area.spray,
      area.spray_points,
      area.sprayPoints,
      area.points_spray,
      area.pointsSpray,
      area.path,
      area.paths,
      area.track,
      area.tracks,
      area.geojson,
      area.output,
      area.result
    ];

    for (let i = 0; i < fields.length; i++) {
      this.collectApplicationGeometries(fields[i], ret, 0);
    }

    return ret;
  };

  collectApplicationGeometries = (value, ret, depth) => {
    if (depth > 5 || value === null || value === undefined) return;

    if (typeof value === 'string') {
      const text = value.trim();
      if (!text) return;
      try {
        this.collectApplicationGeometries(JSON.parse(text), ret, depth + 1);
      } catch (e) { }
      return;
    }

    if (Array.isArray(value)) {
      const points = this.parseLatLngList(value);
      if (points.length >= 2) {
        ret.push({ type: 'line', points: points });
        return;
      }

      for (let i = 0; i < value.length; i++) {
        this.collectApplicationGeometries(value[i], ret, depth + 1);
      }
      return;
    }

    if (typeof value !== 'object') return;

    if (value.type && String(value.type).toLowerCase() === 'featurecollection' && Array.isArray(value.features)) {
      for (let i = 0; i < value.features.length; i++) {
        this.collectApplicationGeometries(value.features[i], ret, depth + 1);
      }
    }

    if (value.type && value.geometry) {
      this.collectApplicationGeometries(value.geometry, ret, depth + 1);
    }

    if (value.type && value.coordinates) {
      this.collectGeoJsonApplication(value, ret, depth + 1);
    }

    if (value.left && value.right) {
      const left = this.parseLatLngList(value.left.points || value.left);
      const right = this.parseLatLngList(value.right.points || value.right);
      if (left.length >= 2 && right.length >= 2) {
        ret.push({ type: 'polygon', points: left.concat(right.slice().reverse()) });
      }
    }

    const keys = ['spray', 'spray_points', 'sprayPoints', 'points_spray', 'pointsSpray', 'application', 'applications', 'app', 'aplicacao', 'aplicacoes', 'poly', 'polygon', 'polygons', 'geojson', 'path', 'paths', 'track', 'tracks', 'line', 'lines', 'polyline', 'items', 'data', 'geometry'];
    for (let i = 0; i < keys.length; i++) {
      if (value[keys[i]] !== undefined) {
        this.collectApplicationGeometries(value[keys[i]], ret, depth + 1);
      }
    }
  };

  collectGeoJsonApplication = (geojson, ret, depth) => {
    if (!geojson || !geojson.type || !geojson.coordinates) return;

    const type = String(geojson.type).toLowerCase();
    const coordinates = geojson.coordinates;

    if (type === 'polygon') {
      for (let i = 0; i < coordinates.length; i++) {
        const points = this.parseGeoJsonLatLngList(coordinates[i]);
        if (points.length >= 3) ret.push({ type: 'polygon', points: points });
      }
    } else if (type === 'multipolygon') {
      for (let i = 0; i < coordinates.length; i++) {
        this.collectGeoJsonApplication({ type: 'Polygon', coordinates: coordinates[i] }, ret, depth + 1);
      }
    } else if (type === 'linestring') {
      const points = this.parseGeoJsonLatLngList(coordinates);
      if (points.length >= 2) ret.push({ type: 'line', points: points });
    } else if (type === 'multilinestring') {
      for (let i = 0; i < coordinates.length; i++) {
        const points = this.parseGeoJsonLatLngList(coordinates[i]);
        if (points.length >= 2) ret.push({ type: 'line', points: points });
      }
    }
  };

  parseGeoJsonLatLngList = (value) => {
    if (!Array.isArray(value)) return [];
    const points = [];
    for (let i = 0; i < value.length; i++) {
      const raw = value[i];
      if (!Array.isArray(raw) || raw.length < 2) continue;
      const lng = toNumber(raw[0], null);
      const lat = toNumber(raw[1], null);
      if (this.isValidLatLng(lat, lng)) points.push({ lat: lat, lng: lng });
    }
    return points;
  };

  parseLatLngList = (value) => {
    if (!Array.isArray(value)) return [];

    const points = [];
    for (let i = 0; i < value.length; i++) {
      const parsed = this.normalizeLatLngPair(value[i]);
      if (parsed) points.push(parsed);
    }

    return points;
  };

  buildApplicationStripFromLine = (line, widthMeters) => {
    if (!Array.isArray(line) || line.length < 2) return null;

    const safeWidth = Number.isFinite(widthMeters) && widthMeters > 0 ? widthMeters : 30;
    const projection = this.getLocalProjection(line[0]);
    const leftPoints = [];
    const rightPoints = [];
    const halfWidth = safeWidth / 2;

    for (let i = 0; i < line.length; i++) {
      const point = line[i];
      const previousPoint = line[i - 1] || point;
      const nextPoint = line[i + 1] || point;
      const previousXY = projection.toXY(previousPoint);
      const nextXY = projection.toXY(nextPoint);
      let dx = nextXY.x - previousXY.x;
      let dy = nextXY.y - previousXY.y;

      if (Math.abs(dx) < 0.0001 && Math.abs(dy) < 0.0001 && i > 0) {
        const currentXY = projection.toXY(point);
        const lastXY = projection.toXY(line[i - 1]);
        dx = currentXY.x - lastXY.x;
        dy = currentXY.y - lastXY.y;
      }

      const length = Math.sqrt((dx * dx) + (dy * dy));
      if (!Number.isFinite(length) || length <= 0) continue;

      const normal = { x: -(dy / length), y: dx / length };
      const xy = projection.toXY(point);
      const left = projection.toLatLng({ x: xy.x + (normal.x * halfWidth), y: xy.y + (normal.y * halfWidth) });
      const right = projection.toLatLng({ x: xy.x - (normal.x * halfWidth), y: xy.y - (normal.y * halfWidth) });

      leftPoints.push(left);
      rightPoints.push(right);
    }

    if (leftPoints.length < 2 || rightPoints.length < 2) return null;
    return leftPoints.concat(rightPoints.reverse());
  };

  getLocalProjection = (referencePoint) => {
    const earthRadius = 6378137;
    const refLatRad = Cesium.Math.toRadians(referencePoint.lat);
    const refLngRad = Cesium.Math.toRadians(referencePoint.lng);
    const cosLat = Math.cos(refLatRad) || 1;

    return {
      toXY: (point) => ({
        x: (Cesium.Math.toRadians(point.lng) - refLngRad) * earthRadius * cosLat,
        y: (Cesium.Math.toRadians(point.lat) - refLatRad) * earthRadius
      }),
      toLatLng: (xy) => ({
        lat: Cesium.Math.toDegrees((xy.y / earthRadius) + refLatRad),
        lng: Cesium.Math.toDegrees((xy.x / (earthRadius * cosLat)) + refLngRad)
      })
    };
  };

  addJobPolygonLabel = (points, job) => {
    const center = this.getCenterFromPoints(points);
    if (!center) return;

    this.addPointLabel(
      center,
      job.title || (this.props.res.TRABALHO + ' ' + job.id),
      false
    );
  };

  addPointLabel = (point, text, showPoint = true) => {
    const entity = {
      name: text,
      position: Cesium.Cartesian3.fromDegrees(point.lng, point.lat, 18),
      label: {
        text: text,
        font: '14px sans-serif',
        fillColor: Cesium.Color.WHITE,
        outlineColor: Cesium.Color.BLACK,
        outlineWidth: 3,
        style: Cesium.LabelStyle.FILL_AND_OUTLINE,
        verticalOrigin: Cesium.VerticalOrigin.CENTER,
        pixelOffset: new Cesium.Cartesian2(0, 0),
        disableDepthTestDistance: Number.POSITIVE_INFINITY
      }
    };

    if (showPoint) {
      entity.point = {
        pixelSize: 10,
        color: Cesium.Color.fromCssColorString('#1f8f47'),
        outlineColor: Cesium.Color.WHITE,
        outlineWidth: 2,
        disableDepthTestDistance: Number.POSITIVE_INFINITY
      };
      entity.label.verticalOrigin = Cesium.VerticalOrigin.BOTTOM;
      entity.label.pixelOffset = new Cesium.Cartesian2(0, -20);
    }

    this.viewer.entities.add(entity);
  };

  parseAreaPoints = (area) => {
    if (!area) return [];

    if (area.radius > 0 && area.point) {
      try {
        const point = Array.isArray(area.point) ? area.point : JSON.parse(area.point);
        const parsed = this.normalizeLatLngPair(point);
        return parsed ? [parsed] : [];
      } catch (e) { }
    }

    if (!area.points) return [];

    try {
      const list = Array.isArray(area.points) ? area.points : JSON.parse(area.points);
      const points = [];
      for (let i = 0; i < list.length; i++) {
        const parsed = this.normalizeLatLngPair(list[i]);
        if (parsed) points.push(parsed);
      }
      return points;
    } catch (e) {
      return [];
    }
  };

  normalizeLatLngPair = (raw) => {
    if (!raw) return null;

    let lat = null;
    let lng = null;

    if (Array.isArray(raw) && raw.length >= 2) {
      const a = toNumber(raw[0], null);
      const b = toNumber(raw[1], null);
      if (this.isValidLatLng(a, b)) {
        lat = a;
        lng = b;
      } else if (this.isValidLatLng(b, a)) {
        lat = b;
        lng = a;
      }
    } else if (raw.lat !== undefined && raw.lng !== undefined) {
      lat = toNumber(raw.lat, null);
      lng = toNumber(raw.lng, null);
    } else if (raw.latitude !== undefined && raw.longitude !== undefined) {
      lat = toNumber(raw.latitude, null);
      lng = toNumber(raw.longitude, null);
    }

    if (!this.isValidLatLng(lat, lng)) return null;
    return { lat: lat, lng: lng };
  };

  drawPlane = (plane, point, rawPosition, job) => {
    const model = this.getAircraftModel(plane);
    const heading = this.getHeadingFromPosition(rawPosition) + model.modelHeadingOffsetDeg;
    const displayName = this.getPlaneDisplayName(plane) || model.label;
    const labelText = job ? this.getJobFocusLabel(job, [displayName]) : displayName;
    const idPlane = this.getPlaneId(plane);
    const displayPoint = this.isPlaneGrounded(idPlane, rawPosition)
      ? this.getParkedDisplayPoint('plane:' + idPlane, point)
      : point;
    const position = Cesium.Cartesian3.fromDegrees(
      displayPoint.lng,
      displayPoint.lat,
      this.getPlaneDisplayAltitude(idPlane, model, rawPosition)
    );
    const hpr = new Cesium.HeadingPitchRoll(
      Cesium.Math.toRadians(heading),
      Cesium.Math.toRadians(model.modelPitchOffsetDeg),
      Cesium.Math.toRadians(model.modelRollOffsetDeg)
    );

    let entity = idPlane > 0 ? this.aircraftEntitiesByPlaneId[idPlane] : null;

    if (entity && this.viewer.entities.contains(entity)) {
      entity._planeSelectionId = idPlane;
      entity.name = labelText;
      if (entity.model) {
        entity.model.scale = this.getAircraftDisplayScaleProperty(model);
        entity.model.minimumPixelSize = this.getAircraftDisplayMinimumPixelSizeProperty(model);
      }

      const playbackIsControllingPosition = idPlane > 0 && (
        !!this.playbackStartedByPlaneId[idPlane] ||
        !!this.aircraftAnimationFrameByPlaneId[idPlane]
      );

      if (!playbackIsControllingPosition) {
        this.applyPlaneHeightReference(entity, idPlane, rawPosition);
        this.setPlaneEntityTransform(entity, model, position, heading, idPlane);
        this.aircraftMotionByPlaneId[idPlane] = {
          currentPosition: Cesium.Cartesian3.clone(position),
          currentHeading: this.normalizeHeadingDegrees(heading),
          targetPosition: Cesium.Cartesian3.clone(position),
          targetHeading: this.normalizeHeadingDegrees(heading)
        };
      } else {
      }

      if (entity.label) {
        entity.label.text = labelText;
      }

      if (!entity.baseReplay3DPropellerConfigured) {
        this.applyPropellerAnimation(entity, model, idPlane);
        entity.baseReplay3DPropellerConfigured = true;
      }
      return entity;
    }

    entity = this.viewer.entities.add({
      name: labelText,
      position: position,
      orientation: Cesium.Transforms.headingPitchRollQuaternion(position, hpr),
      model: {
        uri: model.url,
        scale: this.getAircraftDisplayScaleProperty(model),
        minimumPixelSize: this.getAircraftDisplayMinimumPixelSizeProperty(model),
        heightReference: this.isPlaneGrounded(idPlane, rawPosition)
          ? Cesium.HeightReference.RELATIVE_TO_GROUND
          : Cesium.HeightReference.NONE
      },
      label: {
        text: labelText,
        font: '14px sans-serif',
        fillColor: Cesium.Color.WHITE,
        outlineColor: Cesium.Color.BLACK,
        outlineWidth: 3,
        style: Cesium.LabelStyle.FILL_AND_OUTLINE,
        verticalOrigin: Cesium.VerticalOrigin.BOTTOM,
        pixelOffset: new Cesium.Cartesian2(0, -34),
        heightReference: this.isPlaneGrounded(idPlane, rawPosition)
          ? Cesium.HeightReference.RELATIVE_TO_GROUND
          : Cesium.HeightReference.NONE
      }
    });

    this.applyPropellerAnimation(entity, model, idPlane);
    entity.baseReplay3DPropellerConfigured = true;
    if (idPlane > 0) {
      entity._planeSelectionId = idPlane;
      this.aircraftEntitiesByPlaneId[idPlane] = entity;
      this.aircraftMotionByPlaneId[idPlane] = {
        currentPosition: Cesium.Cartesian3.clone(position),
        currentHeading: this.normalizeHeadingDegrees(heading),
        targetPosition: Cesium.Cartesian3.clone(position),
        targetHeading: this.normalizeHeadingDegrees(heading)
      };
    }
    if (this.aircraftEntities.indexOf(entity) < 0) this.aircraftEntities.push(entity);
    return entity;
  };

  getHeadingFromPosition = (position) => {
    if (!position) return 0;
    return toNumber(position.angle !== undefined ? position.angle : position.heading, 0);
  };

  getAircraftModelById = (modelId) => {
    for (let i = 0; i < aircraftModels.length; i++) {
      if (aircraftModels[i].id === modelId) return aircraftModels[i];
    }

    return null;
  };

  normalizeReplay3DModelId = (value) => {
    const text = this.normalizeReplay3DText(value).replace(/_/g, '-');

    if (
      text === 'emb200' ||
      text === 'air-tractor-at-502b' ||
      text === 'air-tractor-at-802' ||
      text === 'cessna-188' ||
      text === 'emb-203' ||
      text === 'thrush-510'
    ) {
      return text;
    }

    return '';
  };

  getAircraftModel = (plane) => {
    const model3D = this.normalizeReplay3DModelId(plane && plane.model3D);
    const selectedModel = this.getAircraftModelById(model3D);
    if (selectedModel) return selectedModel;

    const brand = this.normalizeReplay3DText(plane && plane.aircraftbrand);
    const model = this.normalizeReplay3DText(plane && plane.aircraftmodel);

    if (brand === 'thrush') return this.getAircraftModelById('thrush-510');
    if (brand === 'embraer' && model === 'emb-203') return this.getAircraftModelById('emb-203');

    const aircraftText = this.buildAircraftSearchText(plane);
    if (this.isCessnaReplay3DBrand(aircraftText)) return this.getAircraftModelById('cessna-188');
    if (this.isAirTractor802Replay3DModel(aircraftText)) return this.getAircraftModelById('air-tractor-at-802');
    if (this.isAirTractorReplay3DBrand(aircraftText) || this.isAirTractor502Replay3DModel(aircraftText)) {
      return this.getAircraftModelById('air-tractor-at-502b');
    }

    return this.getAircraftModelById('emb200');
  };

  buildAircraftSearchText = (plane) => {
    if (!plane) return '';

    const values = [];
    const add = (value) => {
      if (value === null || value === undefined) return;
      if (typeof value === 'object') {
        values.push(value.name);
        values.push(value.label);
        values.push(value.title);
        values.push(value.brand);
        values.push(value.model);
        values.push(value.aircraftBrand);
        values.push(value.aircraftModel);
        return;
      }
      values.push(value);
    };

    add(plane.name);
    add(plane.plane_name);
    add(plane.job_plane_name);
    add(plane.plane);
    add(plane.prefix);
    add(plane.model);
    add(plane.model_name);
    add(plane.aircraftmodel);
    add(plane.aircraftModel);
    add(plane.aircraft_model);
    add(plane.aircraft_model_name);
    add(plane.plane_model);
    add(plane.brand);
    add(plane.brand_name);
    add(plane.aircraftbrand);
    add(plane.aircraftBrand);
    add(plane.aircraft_brand);
    add(plane.aircraft_brand_name);
    add(plane.manufacturer);
    add(plane.manufacturer_name);
    add(plane.aircraft);
    add(plane.aircraft_type);

    return this.normalizeReplay3DText(values.join(' '));
  };

  normalizeReplay3DText = (value) => {
    let text = String(value || '').trim().toLowerCase();
    if (text.normalize) text = text.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
    return text;
  };

  compactReplay3DText = (value) => {
    return this.normalizeReplay3DText(value).replace(/[\s_-]+/g, '');
  };

  isAirTractorReplay3DBrand = (value) => {
    const compactText = this.compactReplay3DText(value);
    return compactText.indexOf('airtractor') >= 0 || compactText.indexOf('airtractorat502') >= 0;
  };

  isAirTractor502Replay3DModel = (value) => {
    const compactText = this.compactReplay3DText(value);
    return compactText.indexOf('at502') >= 0 || compactText.indexOf('at502b') >= 0 || compactText.indexOf('502b') >= 0;
  };

  isAirTractor802Replay3DModel = (value) => {
    const compactText = this.compactReplay3DText(value);
    return compactText.indexOf('at802') >= 0 || compactText.indexOf('at802a') >= 0 || compactText.indexOf('airtractorat802') >= 0 || compactText.indexOf('802a') >= 0;
  };

  isCessnaReplay3DBrand = (value) => {
    const compactText = this.compactReplay3DText(value);
    return compactText.indexOf('cessna') >= 0 || compactText.indexOf('cessna188') >= 0;
  };

  getValidPosition = (position) => {
    if (!position) return null;
    const candidates = [
      { lat: position.lat, lng: position.lng },
      { lat: position.latitude, lng: position.longitude },
      { lat: position.y, lng: position.x }
    ];

    for (let i = 0; i < candidates.length; i++) {
      const lat = toNumber(candidates[i].lat, null);
      const lng = toNumber(candidates[i].lng, null);
      if (this.isValidLatLng(lat, lng)) return { lat: lat, lng: lng };
    }
    return null;
  };

  getPlaneFallbackPosition = (plane, jobs, areasByJob) => {
    for (let i = 0; i < jobs.length; i++) {
      const listExe = Array.isArray(jobs[i].list_exe) ? jobs[i].list_exe : [];
      for (let j = 0; j < listExe.length; j++) {
        if (toNumber(listExe[j].id_plane, 0) === toNumber(plane.id, 0)) {
          const areas = areasByJob[jobs[i].id] || [];
          let points = [];
          for (let k = 0; k < areas.length; k++) points = points.concat(this.parseAreaPoints(areas[k]));
          const center = this.getCenterFromPoints(points);
          if (center) return center;
        }
      }
    }
    return this.getDefaultPlace();
  };

  getDefaultPlace = () => {
    if (Config.place && Config.place.length >= 2) {
      const lat = toNumber(Config.place[0], null);
      const lng = toNumber(Config.place[1], null);
      if (this.isValidLatLng(lat, lng)) return { lat: lat, lng: lng };
    }
    return { lat: -30.0346, lng: -51.2177 };
  };

  getCenterFromPoints = (points) => {
    if (!points || !points.length) return null;
    let lat = 0;
    let lng = 0;
    let qtd = 0;
    for (let i = 0; i < points.length; i++) {
      if (this.isValidLatLng(points[i].lat, points[i].lng)) {
        lat += points[i].lat;
        lng += points[i].lng;
        qtd++;
      }
    }
    return qtd ? { lat: lat / qtd, lng: lng / qtd } : null;
  };

  isValidLatLng = (lat, lng) => {
    return lat !== null && lng !== null && !isNaN(lat) && !isNaN(lng) && lat <= 90 && lat >= -90 && lng <= 180 && lng >= -180 && !(lat === 0 && lng === 0);
  };

  flyToSelectedJob = () => {
    this.cameraFocusNavigationDetached = false;
    this.cameraFollowEnabled = false;
    this.cameraFollowPlaneId = 0;
    const controller = this.viewer && !this.viewer.isDestroyed() && this.viewer.scene
      ? this.viewer.scene.screenSpaceCameraController
      : null;
    if (controller) controller.enableZoom = true;
    const focusItems = this.getFilteredJobFocusItems();
    const safeIndex = this.getSafeJobIndex(this.state.currentJobIndex, focusItems.length);
    const focusItem = focusItems[safeIndex];

    this.setState({ freeMode: false, cameraFocusType: 'job' }, () => {
      if (!focusItem) {
        this.flyToPoints(this.getCurrentPoints());
        return;
      }

      this.flyToJobFocus(focusItem);
    });
  };

  flyToJobFocus = (focusItem, duration = 0.7) => {
    if (!focusItem || !focusItem.point || !this.viewer) return;

    const point = focusItem.point;
    const bounds = focusItem.bounds && focusItem.bounds.length ? focusItem.bounds : [point];
    const rangeMeters = this.getFocusCameraRangeMeters(bounds);
    if (Math.abs(rangeMeters - this.state.cameraRangeMeters) >= 1) {
      this.setState({ cameraRangeMeters: rangeMeters });
    }

    const boundsCenter = this.getBoundsCenter(bounds) || point;
    const target = Cesium.Cartesian3.fromDegrees(boundsCenter.lng, boundsCenter.lat, 0);

    this.viewer.camera.flyToBoundingSphere(new Cesium.BoundingSphere(target, 120), {
      offset: new Cesium.HeadingPitchRange(
        Cesium.Math.toRadians(this.state.cameraHeadingDeg),
        Cesium.Math.toRadians(this.state.cameraPitchDeg),
        rangeMeters
      ),
      duration: duration,
      complete: () => this.applyCollapsedMobileFocusOffset(rangeMeters)
    });
  };

  getFocusCameraRangeMeters = (points) => {
    const list = Array.isArray(points) ? points : [];
    let minLat = null;
    let maxLat = null;
    let minLng = null;
    let maxLng = null;

    for (let i = 0; i < list.length; i++) {
      const point = list[i];
      if (!point || !this.isValidLatLng(point.lat, point.lng)) continue;

      minLat = minLat === null ? point.lat : Math.min(minLat, point.lat);
      maxLat = maxLat === null ? point.lat : Math.max(maxLat, point.lat);
      minLng = minLng === null ? point.lng : Math.min(minLng, point.lng);
      maxLng = maxLng === null ? point.lng : Math.max(maxLng, point.lng);
    }

    if (minLat === null || minLng === null) return 1800;

    const diagonal = this.distanceMeters(
      { lat: minLat, lng: minLng },
      { lat: maxLat, lng: maxLng }
    );

    if (!diagonal || diagonal < 250) return 1800;

    // Mantém o enquadramento atual para trabalhos pequenos e afasta
    // progressivamente quando as áreas do mesmo trabalho estão separadas.
    return Math.max(1800, Math.min(cameraRangeMaximumMeters, diagonal * 1.45));
  };

  getBoundsCenter = (points) => {
    const list = Array.isArray(points) ? points : [];
    let minLat = null;
    let maxLat = null;
    let minLng = null;
    let maxLng = null;

    for (let i = 0; i < list.length; i++) {
      const point = list[i];
      if (!point || !this.isValidLatLng(point.lat, point.lng)) continue;

      minLat = minLat === null ? point.lat : Math.min(minLat, point.lat);
      maxLat = maxLat === null ? point.lat : Math.max(maxLat, point.lat);
      minLng = minLng === null ? point.lng : Math.min(minLng, point.lng);
      maxLng = maxLng === null ? point.lng : Math.max(maxLng, point.lng);
    }

    if (minLat === null || minLng === null) return null;
    return {
      lat: (minLat + maxLat) / 2,
      lng: (minLng + maxLng) / 2
    };
  };

  distanceMeters = (a, b) => {
    if (!a || !b) return 0;
    const earthRadius = 6371000;
    const lat1 = Cesium.Math.toRadians(a.lat);
    const lat2 = Cesium.Math.toRadians(b.lat);
    const dLat = Cesium.Math.toRadians(b.lat - a.lat);
    const dLng = Cesium.Math.toRadians(b.lng - a.lng);
    const sinLat = Math.sin(dLat / 2);
    const sinLng = Math.sin(dLng / 2);
    const h = sinLat * sinLat + Math.cos(lat1) * Math.cos(lat2) * sinLng * sinLng;
    return earthRadius * 2 * Math.atan2(Math.sqrt(h), Math.sqrt(1 - h));
  };

  isJobFocusItemRunning = (focusItem) => {
    if (!focusItem) return false;
    if (focusItem.type === 'realtime') return true;
    return !!(focusItem.job && this.hasOpenRealtimeExecutionForJob(focusItem.job));
  };

  isJobFocusItemExecuted = (focusItem) => {
    if (!focusItem || focusItem.type === 'realtime' || this.isJobFocusItemRunning(focusItem)) return false;
    return !!(focusItem.job && this.hasJobExecution(focusItem.job));
  };

  isJobFocusItemPending = (focusItem) => {
    if (!focusItem || this.isJobFocusItemRunning(focusItem)) return false;
    return !this.isJobFocusItemExecuted(focusItem);
  };

  getJobSelectionKey = (focusItem) => {
    if (!focusItem) return '';
    if (focusItem.type === 'realtime') return 'realtime:' + String(focusItem.key || (focusItem.execution && focusItem.execution.key) || '');
    const job = focusItem.job || {};
    return 'job:' + String(job.id !== undefined ? job.id : (job.id_job !== undefined ? job.id_job : (job.guid || focusItem.label || '')));
  };

  isJobFocusItemSelected = (focusItem) => {
    const key = this.getJobSelectionKey(focusItem);
    if (!key || (this.state.hiddenJobFocusKeys || {})[key]) return false;
    const filter = this.state.jobFocusFilter || 'all';
    if (filter === 'running') return this.isJobFocusItemRunning(focusItem);
    if (filter === 'executed') return this.isJobFocusItemExecuted(focusItem);
    if (filter === 'pending') return this.isJobFocusItemPending(focusItem);
    return true;
  };

  isPlaneFocusItemSelected = (item) => {
    if (!item || !item.plane) return false;
    const idPlane = this.getPlaneId(item.plane);
    if (idPlane <= 0 || (this.state.hiddenPlaneIds || {})[idPlane]) return false;
    const filter = this.state.planeFocusFilter || 'all';
    const online = this.isPlaneRealtimeConnected(idPlane);
    if (filter === 'online') return online;
    if (filter === 'offline') return !online;
    return true;
  };

  isStationFocusItemSelected = (item) => {
    if (!item || !item.station) return false;
    const stationId = this.getWeatherStationId(item.station);
    if (!stationId || (this.state.hiddenStationIds || {})[stationId]) return false;
    const filter = this.state.stationFocusFilter || 'all';
    const online = item.station.online === true;
    if (filter === 'online') return online;
    if (filter === 'offline') return !online;
    return true;
  };

  getFilteredJobFocusItems = () => (this.state.focusItems || []).filter(this.isJobFocusItemSelected);
  getFilteredPlaneFocusItems = () => (this.state.planeFocusItems || []).filter(this.isPlaneFocusItemSelected);
  getFilteredStationFocusItems = () => (this.state.stationFocusItems || []).filter(this.isStationFocusItemSelected);

  buildHiddenJobKeysForFilter = (filter) => {
    const hidden = {};
    if (filter === 'all') return hidden;
    (this.state.focusItems || []).forEach((item) => {
      let shouldShow = true;
      if (filter === 'running') shouldShow = this.isJobFocusItemRunning(item);
      if (filter === 'executed') shouldShow = this.isJobFocusItemExecuted(item);
      if (filter === 'pending') shouldShow = this.isJobFocusItemPending(item);
      if (!shouldShow) hidden[this.getJobSelectionKey(item)] = true;
    });
    return hidden;
  };

  buildHiddenPlaneIdsForFilter = (filter) => {
    const hidden = {};
    if (filter === 'all') return hidden;
    (this.state.planeFocusItems || []).forEach((item) => {
      if (!item || !item.plane) return;
      const idPlane = this.getPlaneId(item.plane);
      const online = this.isPlaneRealtimeConnected(idPlane);
      if ((filter === 'online' && !online) || (filter === 'offline' && online)) hidden[idPlane] = true;
    });
    return hidden;
  };

  buildHiddenStationIdsForFilter = (filter) => {
    const hidden = {};
    if (filter === 'all') return hidden;
    (this.state.stationFocusItems || []).forEach((item) => {
      if (!item || !item.station) return;
      const stationId = this.getWeatherStationId(item.station);
      const online = item.station.online === true;
      if ((filter === 'online' && !online) || (filter === 'offline' && online)) hidden[stationId] = true;
    });
    return hidden;
  };

  applyMapItemSelectionVisibility = () => {
    if (!this.viewer || this.viewer.isDestroyed()) return;

    Object.keys(this.jobVisualEntitiesByKey || {}).forEach((key) => {
      const visibleItem = (this.state.focusItems || []).find((item) => this.getJobSelectionKey(item) === key);
      const show = visibleItem ? this.isJobFocusItemSelected(visibleItem) : true;
      const entities = this.jobVisualEntitiesByKey[key] || [];
      entities.forEach((entity) => {
        if (entity && !entity._jobSelectionHighlight) entity.show = show;
      });
    });

    this.updateSelectedJobHighlight();

    (this.state.planeFocusItems || []).forEach((item) => {
      if (!item || !item.plane) return;
      const idPlane = this.getPlaneId(item.plane);
      const entity = this.aircraftEntitiesByPlaneId[idPlane];
      if (entity) entity.show = this.isPlaneFocusItemSelected(item);
    });

    (this.state.stationFocusItems || []).forEach((item) => {
      if (!item || !item.station) return;
      const stationId = this.getWeatherStationId(item.station);
      const entity = this.weatherStationEntitiesById[stationId];
      if (entity) entity.show = this.isStationFocusItemSelected(item);
    });

    this.updateWeatherStationCoverageVisibility();
    this.viewer.scene.requestRender();
  };

  setJobFocusFilter = (filter) => {
    this.setState({
      jobFocusFilter: filter,
      hiddenJobFocusKeys: this.buildHiddenJobKeysForFilter(filter),
      currentJobIndex: 0,
      freeMode: false,
      cameraFocusType: 'job',
      desktopExecutedStationVisible: false,
      desktopStationManuallyHidden: false
    }, () => {
      this.applyMapItemSelectionVisibility();
      if (this.getFilteredJobFocusItems().length) this.flyToSelectedJob();
    });
  };

  setPlaneFocusFilter = (filter) => {
    this.setState({
      planeFocusFilter: filter,
      hiddenPlaneIds: this.buildHiddenPlaneIdsForFilter(filter),
      currentPlaneIndex: 0,
      freeMode: false,
      cameraFocusType: 'plane'
    }, () => {
      this.applyMapItemSelectionVisibility();
      if (this.getFilteredPlaneFocusItems().length) this.flyToSelectedPlane();
    });
  };

  setStationFocusFilter = (filter) => {
    this.setState({
      stationFocusFilter: filter,
      hiddenStationIds: this.buildHiddenStationIdsForFilter(filter),
      currentStationIndex: 0,
      freeMode: false,
      cameraFocusType: 'station'
    }, () => {
      this.applyMapItemSelectionVisibility();
      if (this.getFilteredStationFocusItems().length) this.flyToSelectedStation();
    });
  };

  toggleItemSelector = (type) => {
    this.setState((state) => ({ itemSelectorOpen: state.itemSelectorOpen === type ? null : type }));
  };

  toggleJobItemSelection = (focusItem) => {
    const key = this.getJobSelectionKey(focusItem);
    if (!key) return;
    this.setState((state) => {
      const hidden = Object.assign({}, state.hiddenJobFocusKeys || {});
      if (hidden[key]) delete hidden[key]; else hidden[key] = true;
      return { hiddenJobFocusKeys: hidden, jobFocusFilter: 'custom', currentJobIndex: 0 };
    }, this.applyMapItemSelectionVisibility);
  };

  togglePlaneItemSelection = (focusItem) => {
    if (!focusItem || !focusItem.plane) return;
    const idPlane = this.getPlaneId(focusItem.plane);
    if (idPlane <= 0) return;
    this.setState((state) => {
      const hidden = Object.assign({}, state.hiddenPlaneIds || {});
      if (hidden[idPlane]) delete hidden[idPlane]; else hidden[idPlane] = true;
      return { hiddenPlaneIds: hidden, planeFocusFilter: 'custom', currentPlaneIndex: 0 };
    }, this.applyMapItemSelectionVisibility);
  };

  toggleStationItemSelection = (focusItem) => {
    if (!focusItem || !focusItem.station) return;
    const stationId = this.getWeatherStationId(focusItem.station);
    if (!stationId) return;
    this.setState((state) => {
      const hidden = Object.assign({}, state.hiddenStationIds || {});
      if (hidden[stationId]) delete hidden[stationId]; else hidden[stationId] = true;
      return { hiddenStationIds: hidden, stationFocusFilter: 'custom', currentStationIndex: 0 };
    }, this.applyMapItemSelectionVisibility);
  };

  setAllItemSelection = (type, selected, items) => {
    const safeItems = Array.isArray(items) ? items : [];

    if (type === 'job') {
      const hidden = {};
      if (!selected) {
        safeItems.forEach((item) => {
          const key = this.getJobSelectionKey(item);
          if (key) hidden[key] = true;
        });
      }
      this.setState({
        hiddenJobFocusKeys: hidden,
        jobFocusFilter: selected ? 'all' : 'custom',
        currentJobIndex: 0
      }, this.applyMapItemSelectionVisibility);
      return;
    }

    if (type === 'plane') {
      const hidden = {};
      if (!selected) {
        safeItems.forEach((item) => {
          if (!item || !item.plane) return;
          const idPlane = this.getPlaneId(item.plane);
          if (idPlane > 0) hidden[idPlane] = true;
        });
      }
      this.setState({
        hiddenPlaneIds: hidden,
        planeFocusFilter: selected ? 'all' : 'custom',
        currentPlaneIndex: 0
      }, this.applyMapItemSelectionVisibility);
      return;
    }

    if (type === 'station') {
      const hidden = {};
      if (!selected) {
        safeItems.forEach((item) => {
          if (!item || !item.station) return;
          const stationId = this.getWeatherStationId(item.station);
          if (stationId) hidden[stationId] = true;
        });
      }
      this.setState({
        hiddenStationIds: hidden,
        stationFocusFilter: selected ? 'all' : 'custom',
        currentStationIndex: 0
      }, this.applyMapItemSelectionVisibility);
    }
  };

  renderDesktopItemSelector = (type, items) => {
    if (this.state.mobileCompact || this.state.itemSelectorOpen !== type) return null;
    const safeItems = Array.isArray(items) ? items : [];
    const res = this.props.res || {};
    const selectedCount = safeItems.reduce((count, item) => {
      if (type === 'job') return count + (this.isJobFocusItemSelected(item) ? 1 : 0);
      if (type === 'plane') return count + (this.isPlaneFocusItemSelected(item) ? 1 : 0);
      if (type === 'station') return count + (this.isStationFocusItemSelected(item) ? 1 : 0);
      return count;
    }, 0);
    const allSelected = safeItems.length > 0 && selectedCount === safeItems.length;
    const partiallySelected = selectedCount > 0 && selectedCount < safeItems.length;

    return (
      <div className="desktop-item-selector-popover">
        <label className="desktop-item-selector-row desktop-item-selector-all-row">
          <input
            type="checkbox"
            checked={allSelected}
            ref={(element) => {
              if (element) element.indeterminate = partiallySelected;
            }}
            onChange={(event) => this.setAllItemSelection(type, event.target.checked, safeItems)}
          />
          <span>{res.TODOS}</span>
        </label>
        <div className="desktop-item-selector-list">
          {safeItems.map((item, index) => {
            let key = type + ':' + index;
            let label = '-';
            let checked = true;
            let onChange = null;

            if (type === 'job') {
              key = this.getJobSelectionKey(item);
              label = item && item.label ? item.label : '-';
              checked = this.isJobFocusItemSelected(item);
              onChange = () => this.toggleJobItemSelection(item);
            } else if (type === 'plane') {
              const idPlane = item && item.plane ? this.getPlaneId(item.plane) : index;
              key = 'plane:' + idPlane;
              label = item && item.plane ? this.getPlaneDisplayName(item.plane) : '-';
              checked = this.isPlaneFocusItemSelected(item);
              onChange = () => this.togglePlaneItemSelection(item);
            } else if (type === 'station') {
              const stationId = item && item.station ? this.getWeatherStationId(item.station) : index;
              key = 'station:' + stationId;
              label = item && item.station ? this.getWeatherStationName(item.station) : '-';
              checked = this.isStationFocusItemSelected(item);
              onChange = () => this.toggleStationItemSelection(item);
            }

            return (
              <label className="desktop-item-selector-row" key={key}>
                <input type="checkbox" checked={checked} onChange={onChange} />
                <span title={label}>{label}</span>
              </label>
            );
          })}
          {!safeItems.length ? <div className="desktop-item-selector-empty">-</div> : null}
        </div>
      </div>
    );
  };

  goToNextJob = () => {
    const focusItems = this.getFilteredJobFocusItems();
    if (!focusItems.length) return;
    const nextIndex = (this.state.currentJobIndex + 1) >= focusItems.length ? 0 : this.state.currentJobIndex + 1;
    this.setState({ currentJobIndex: nextIndex, freeMode: false, desktopExecutedStationVisible: false, desktopStationManuallyHidden: false }, () => this.flyToSelectedJob());
  };

  goToPreviousJob = () => {
    const focusItems = this.getFilteredJobFocusItems();
    if (!focusItems.length) return;
    const previousIndex = (this.state.currentJobIndex - 1) < 0 ? focusItems.length - 1 : this.state.currentJobIndex - 1;
    this.setState({ currentJobIndex: previousIndex, freeMode: false, desktopExecutedStationVisible: false, desktopStationManuallyHidden: false }, () => this.flyToSelectedJob());
  };

  getPlaneCameraTarget = (focusItem) => {
    if (!focusItem || !focusItem.point) return null;

    const idPlane = this.getPlaneId(focusItem.plane);
    const entity = this.aircraftEntitiesByPlaneId[idPlane];
    if (entity && entity.position) {
      const entityPosition = entity.position.getValue
        ? entity.position.getValue(Cesium.JulianDate.now())
        : entity.position;
      if (entityPosition) return entityPosition;
    }

    const rawPosition = this.planePositionsById[idPlane] || {};
    const model = this.getAircraftModel(focusItem.plane);
    const altitude = Math.max(0, toNumber(
      rawPosition.alt !== undefined ? rawPosition.alt : rawPosition.altitude,
      0
    )) + model.modelAltitudeOffsetMeters;

    return Cesium.Cartesian3.fromDegrees(focusItem.point.lng, focusItem.point.lat, altitude);
  };

  flyToSelectedPlane = () => {
    this.cameraFocusNavigationDetached = false;
    const planeFocusItems = this.getFilteredPlaneFocusItems();
    const safeIndex = this.getSafeJobIndex(this.state.currentPlaneIndex, planeFocusItems.length);
    const focusItem = planeFocusItems[safeIndex];

    this.cameraFollowEnabled = false;
    this.cameraFollowPlaneId = 0;

    if (!focusItem) {
      this.setState({ freeMode: false, cameraFocusType: 'plane' }, () => {
        this.flyToPoints(this.getCurrentPoints());
      });
      return;
    }

    const idPlane = this.getPlaneId(focusItem.plane);
    const point = focusItem.point;
    if (!point || !this.viewer) return;

    const target = this.getPlaneCameraTarget(focusItem);
    if (!target) return;
    const rangeMeters = Math.max(cameraRangeMinimumMeters, Math.min(cameraRangeMaximumMeters, toNumber(this.planeCameraRangeMeters, 1000)));
    this.cameraFollowPlaneId = idPlane;
    this.cameraFollowRangeMeters = rangeMeters;

    this.setState({ freeMode: false, cameraFocusType: 'plane', cameraRangeMeters: rangeMeters }, () => {
      this.viewer.camera.flyToBoundingSphere(new Cesium.BoundingSphere(target, 120), {
        offset: new Cesium.HeadingPitchRange(
          Cesium.Math.toRadians(this.state.cameraHeadingDeg),
          Cesium.Math.toRadians(this.state.cameraPitchDeg),
          rangeMeters
        ),
        duration: 0.7,
        complete: () => {
          if (!this.mounted || this.state.freeMode || this.state.cameraFocusType !== 'plane') return;
          this.applyCollapsedMobileFocusOffset(rangeMeters);
          this.cameraFollowEnabled = true;
          this.cameraFollowPlaneId = idPlane;
          const controller = this.viewer && !this.viewer.isDestroyed() && this.viewer.scene
            ? this.viewer.scene.screenSpaceCameraController
            : null;
          if (controller) controller.enableZoom = false;
        }
      });
    });
  };

  goToNextPlane = () => {
    const planeFocusItems = this.getFilteredPlaneFocusItems();
    if (!planeFocusItems.length) return;
    const nextIndex = (this.state.currentPlaneIndex + 1) >= planeFocusItems.length ? 0 : this.state.currentPlaneIndex + 1;
    this.setState({ currentPlaneIndex: nextIndex, freeMode: false }, () => this.flyToSelectedPlane());
  };

  goToPreviousPlane = () => {
    const planeFocusItems = this.getFilteredPlaneFocusItems();
    if (!planeFocusItems.length) return;
    const previousIndex = (this.state.currentPlaneIndex - 1) < 0 ? planeFocusItems.length - 1 : this.state.currentPlaneIndex - 1;
    this.setState({ currentPlaneIndex: previousIndex, freeMode: false }, () => this.flyToSelectedPlane());
  };

  toggleFreeMode = () => {
    this.cameraFocusNavigationDetached = false;
    this.cameraFollowEnabled = false;
    this.cameraFollowPlaneId = 0;
    const nextFreeMode = !this.state.freeMode;
    this.setState({ freeMode: nextFreeMode }, () => {
      this.setFreeModeCameraNavigation(nextFreeMode);

      if (!nextFreeMode) {
        if (this.state.cameraFocusType === 'plane') this.flyToSelectedPlane();
        else if (this.state.cameraFocusType === 'station') this.flyToSelectedStation();
        else this.flyToSelectedJob();
      }
    });
  };

  flyToPoints = (points, closer) => {
    if (!this.viewer) return;
    const valid = (points || []).filter((p) => this.isValidLatLng(p.lat, p.lng));
    if (!valid.length) {
      this.flyToDefaultPlace();
      return;
    }

    let minLat = valid[0].lat;
    let maxLat = valid[0].lat;
    let minLng = valid[0].lng;
    let maxLng = valid[0].lng;

    for (let i = 1; i < valid.length; i++) {
      minLat = Math.min(minLat, valid[i].lat);
      maxLat = Math.max(maxLat, valid[i].lat);
      minLng = Math.min(minLng, valid[i].lng);
      maxLng = Math.max(maxLng, valid[i].lng);
    }

    const minDelta = closer ? 0.0018 : 0.004;
    const deltaLat = Math.max(maxLat - minLat, minDelta);
    const deltaLng = Math.max(maxLng - minLng, minDelta);
    const padLat = deltaLat * (closer ? 0.25 : 0.35);
    const padLng = deltaLng * (closer ? 0.25 : 0.35);

    const rectangle = Cesium.Rectangle.fromDegrees(minLng - padLng, minLat - padLat, maxLng + padLng, maxLat + padLat);
    this.viewer.camera.flyTo({
      destination: rectangle,
      orientation: { heading: Cesium.Math.toRadians(0), pitch: Cesium.Math.toRadians(-52), roll: 0 },
      duration: 0.8
    });
  };

  flyToDefaultPlace = () => {
    if (!this.viewer) return;
    const place = this.getDefaultPlace();
    this.viewer.camera.setView({
      destination: Cesium.Cartesian3.fromDegrees(place.lng, place.lat, 2500),
      orientation: { heading: Cesium.Math.toRadians(0), pitch: Cesium.Math.toRadians(-55), roll: 0 }
    });
    this.viewer.scene.requestRender();
  };

  getActiveCameraFocusItem = () => {
    if (this.state.cameraFocusType === 'plane') return this.getSelectedPlaneFocusItem();
    if (this.state.cameraFocusType === 'station') return this.getSelectedStationFocusItem();
    return this.getSelectedFocusItem();
  };

  getCameraFocusPosition = () => {
    if (this.state.cameraFocusType === 'plane') {
      const focusItem = this.getSelectedPlaneFocusItem();
      const idPlane = focusItem ? this.getPlaneId(focusItem.plane) : 0;
      const entity = this.aircraftEntitiesByPlaneId[idPlane];
      if (entity && entity.position) {
        return entity.position.getValue ? entity.position.getValue(Cesium.JulianDate.now()) : entity.position;
      }
    }

    if (this.state.cameraFocusType === 'station') {
      return this.getWeatherStationCameraTarget(this.getSelectedStationFocusItem());
    }

    const focusItem = this.getActiveCameraFocusItem();
    return focusItem && focusItem.point
      ? Cesium.Cartesian3.fromDegrees(focusItem.point.lng, focusItem.point.lat, 0)
      : null;
  };

  syncCameraRangeFromViewer = () => {
    if (!this.viewer || this.viewer.isDestroyed() || this.state.freeMode) return;
    const focusPosition = this.getCameraFocusPosition();
    if (!focusPosition) return;

    const measuredRangeMeters = Cesium.Cartesian3.distance(this.viewer.camera.positionWC, focusPosition);
    const rangeMeters = Math.max(cameraRangeMinimumMeters, Math.min(cameraRangeMaximumMeters, this.normalizeCollapsedMobileCameraRange(measuredRangeMeters)));
    this.cameraFollowRangeMeters = rangeMeters;
    if (this.state.cameraFocusType === 'plane') this.planeCameraRangeMeters = rangeMeters;
    if (Math.abs(rangeMeters - this.state.cameraRangeMeters) >= 1) {
      this.setState({ cameraRangeMeters: rangeMeters });
    }
  };

  applyCameraControls = () => {
    if (!this.viewer || this.viewer.isDestroyed()) return;

    if (this.cameraFocusNavigationDetached && !this.state.freeMode) {
      this.viewer.scene.requestRender();
      return;
    }

    if (this.state.freeMode) {
      this.viewer.camera.setView({
        destination: this.viewer.camera.positionWC,
        orientation: {
          heading: Cesium.Math.toRadians(this.state.cameraHeadingDeg),
          pitch: Cesium.Math.toRadians(this.state.cameraPitchDeg),
          roll: 0
        }
      });
      this.viewer.scene.requestRender();
      return;
    }

    const focusItem = this.getActiveCameraFocusItem();
    if (this.state.cameraFocusType === 'plane') {
      const planeFocusItem = this.getSelectedPlaneFocusItem();
      if (planeFocusItem && planeFocusItem.point) {
        const target = this.getPlaneCameraTarget(planeFocusItem);
        if (!target) return;
        this.viewer.camera.lookAt(
          target,
          new Cesium.HeadingPitchRange(
            Cesium.Math.toRadians(this.state.cameraHeadingDeg),
            Cesium.Math.toRadians(this.state.cameraPitchDeg),
            this.state.cameraRangeMeters
          )
        );
        this.applyCollapsedMobileFocusOffset(this.state.cameraRangeMeters);
        this.cameraFollowRangeMeters = this.state.cameraRangeMeters;
        this.cameraFollowEnabled = true;
        this.cameraFollowPlaneId = this.getPlaneId(planeFocusItem.plane);
      }
    } else if (focusItem && focusItem.point) {
      const target = Cesium.Cartesian3.fromDegrees(focusItem.point.lng, focusItem.point.lat, 0);
      this.viewer.camera.lookAt(
        target,
        new Cesium.HeadingPitchRange(
          Cesium.Math.toRadians(this.state.cameraHeadingDeg),
          Cesium.Math.toRadians(this.state.cameraPitchDeg),
          this.state.cameraRangeMeters
        )
      );
      this.applyCollapsedMobileFocusOffset(this.state.cameraRangeMeters);
    }
    this.viewer.scene.requestRender();
  };

  handleCameraHeadingChange = (event) => {
    this.cameraFocusNavigationDetached = false;
    this.setState({ cameraHeadingDeg: toNumber(event.target.value, 0) }, this.applyCameraControls);
  };

  handleCameraPitchChange = (event) => {
    this.cameraFocusNavigationDetached = false;
    this.setState({ cameraPitchDeg: toNumber(event.target.value, -54) }, this.applyCameraControls);
  };

  handleCameraRangeChange = (event) => {
    this.cameraFocusNavigationDetached = false;
    const cameraRangeMeters = toNumber(event.target.value, 1000);
    this.cameraFollowRangeMeters = cameraRangeMeters;
    if (this.state.cameraFocusType === 'plane') this.planeCameraRangeMeters = cameraRangeMeters;
    this.setState({ cameraRangeMeters: cameraRangeMeters }, this.applyCameraControls);
  };

  handleWeatherStationCoverageOpacityChange = (event) => {
    const percent = Math.max(0, Math.min(30, toNumber(event.target.value, weatherStationCoverageDefaultFillAlpha * 100)));
    const opacity = percent / 100;

    this.setState({ weatherStationCoverageOpacity: opacity }, () => {
      Object.keys(this.weatherStationCoverageEntitiesById || {}).forEach((stationId) => {
        const entity = this.weatherStationCoverageEntitiesById[stationId];
        const visualState = this.weatherStationVisualStateById[stationId] || {};
        const color = Cesium.Color.fromCssColorString(visualState.coverageColorCss || '#f28c28');
        if (entity && entity.ellipse) entity.ellipse.material = color.withAlpha(this.state.weatherStationCoverageOpacity);
      });
      if (this.viewer && !this.viewer.isDestroyed()) this.viewer.scene.requestRender();
    });
  };

  getSelectedFocusItem = () => {
    const focusItems = this.getFilteredJobFocusItems();
    if (!focusItems.length) return null;
    const index = this.getSafeJobIndex(this.state.currentJobIndex, focusItems.length);
    return focusItems[index] || null;
  };

  getSelectedFocusTitle = () => {
    const focusItems = this.getFilteredJobFocusItems();
    const focusItem = this.getSelectedFocusItem();
    if (!focusItem) return this.props.res.NENHUM_TRABALHO_FOCO;
    const total = focusItems.length;
    const currentIndex = this.getSafeJobIndex(this.state.currentJobIndex, total);
    const numberText = total > 1 ? ((currentIndex + 1) + '/' + total + ' - ') : '';
    if (focusItem.type === 'realtime') {
      return numberText + focusItem.label;
    }

    const jobName = this.getJobDisplayName(focusItem.job);
    const hasOpenRealtime = this.hasOpenRealtimeExecutionForJob(focusItem.job);

    return numberText + (hasOpenRealtime
      ? this.props.res.STATUS_EM_EXECUCAO + ' - ' + jobName
      : jobName);
  };

  getSelectedFocusSubtitle = () => {
    const focusItem = this.getSelectedFocusItem();
    if (!focusItem) return this.props.res.NENHUM_TRABALHO_EXECUCAO_DIA;
    if (this.state.freeMode) return this.props.res.MODO_LIVRE_ATIVO;
    const planeText = focusItem.planeNames && focusItem.planeNames.length ? focusItem.planeNames.join(', ') : this.props.res.AERONAVE_NAO_INFORMADA;
    if (focusItem.type === 'realtime' && focusItem.jobTitle) {
      return this.props.res.AERONAVE + ': ' + planeText + ' | ' + this.props.res.TRABALHO + ': ' + focusItem.jobTitle;
    }
    return this.props.res.AERONAVE + ': ' + planeText;
  };

  getSelectedPlaneFocusItem = () => {
    const planeFocusItems = this.getFilteredPlaneFocusItems();
    if (!planeFocusItems.length) return null;
    const index = this.getSafeJobIndex(this.state.currentPlaneIndex, planeFocusItems.length);
    return planeFocusItems[index] || null;
  };

  getSelectedPlaneFocusTitle = () => {
    const planeFocusItems = this.getFilteredPlaneFocusItems();
    const focusItem = this.getSelectedPlaneFocusItem();
    if (!focusItem) return this.props.res.AERONAVE_NAO_INFORMADA;
    const total = planeFocusItems.length;
    const currentIndex = this.getSafeJobIndex(this.state.currentPlaneIndex, total);
    const numberText = total > 1 ? ((currentIndex + 1) + '/' + total + ' - ') : '';
    return numberText + this.getPlaneDisplayName(focusItem.plane);
  };

  getSelectedPlaneFocusSubtitle = () => {
    const focusItem = this.getSelectedPlaneFocusItem();
    if (!focusItem) return this.props.res.NENHUM_TRABALHO_EXECUCAO_DIA;
    if (this.state.freeMode) return this.props.res.MODO_LIVRE_ATIVO;

    const jobs = focusItem.jobs || [];
    if (!jobs.length) return this.props.res.NENHUM_TRABALHO_EXECUCAO_DIA;

    const jobNames = jobs.map((job) => this.getJobDisplayName(job)).filter(Boolean);
    return this.props.res.TRABALHOS + ': ' + jobNames.join(', ');
  };

  formatPlaneTelemetryValue = (value, decimals) => {
    const numberValue = toNumber(value, null);
    if (numberValue === null) return '-';
    return numberValue.toFixed(decimals);
  };

  formatPlanePositionDate = (value) => {
    if (!value) return '-';

    const normalizedValue = typeof value === 'string'
      ? value.trim().replace(' ', 'T')
      : value;
    const date = new Date(normalizedValue);

    if (isNaN(date.getTime())) return '-';

    return date.toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  getOptionalNumber = (value) => {
    if (value === null || value === undefined || value === '') return null;
    const numberValue = Number(value);
    return Number.isFinite(numberValue) ? numberValue : null;
  };

  formatJobTelemetryValue = (value, decimals, suffix) => {
    const numberValue = this.getOptionalNumber(value);
    if (numberValue === null) return '-';
    return Units.formatValue(numberValue, this.props.res) + (suffix ? ' ' + suffix : '');
  };


  formatRealtimeSpeed = (speed, decimals = 2, sourceType = 'activity') => {
    const value = this.getOptionalNumber(speed);
    if (value === null) return '-';

    const sourceUnit = sourceType === 'heartbeat' ? 'km/h' : 'm/s';
    return this.formatJobTelemetryValue(
      Units.convertSpeed(value, sourceUnit, this.props.res),
      decimals,
      Units.getSpeedUnit(this.props.res)
    );
  };

  formatRealtimeLength = (meters, decimals = 2) => {
    const value = this.getOptionalNumber(meters);
    if (value === null) return '-';

    return this.formatJobTelemetryValue(
      Units.convertLength(value, 'm', this.props.res),
      decimals,
      Units.getLengthUnit(this.props.res)
    );
  };

  formatRealtimeFlow = (litersPerHectare, decimals = 2) => {
    const value = this.getOptionalNumber(litersPerHectare);
    if (value === null) return '-';

    return this.formatJobTelemetryValue(
      Units.convertFlow(value, 'L', 'ha', this.props.res),
      decimals,
      Units.getFlowUnit(this.props.res)
    );
  };

  formatReportArea = (hectares, decimals = 2) => {
    const value = this.getOptionalNumber(hectares);
    if (value === null) return '-';

    return this.formatJobTelemetryValue(
      Units.convertArea(value, 'ha', this.props.res),
      decimals,
      Units.getAreaUnit(this.props.res)
    );
  };

  formatReportVolume = (liters, decimals = 0) => {
    const value = this.getOptionalNumber(liters);
    if (value === null) return '-';

    return this.formatJobTelemetryValue(
      Units.convertVolume(value, 'L', this.props.res),
      decimals,
      Units.getVolumeUnit(this.props.res)
    );
  };

  formatReportSpeed = (kilometersPerHour, decimals = 2) => {
    const value = this.getOptionalNumber(kilometersPerHour);
    if (value === null) return '-';

    return this.formatJobTelemetryValue(
      Units.convertSpeed(value, 'km/h', this.props.res),
      decimals,
      Units.getSpeedUnit(this.props.res)
    );
  };

  formatJobTelemetryTime = (value) => {
    const totalSeconds = Math.max(0, Math.round(toNumber(value, 0)));
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    return [hours, minutes, seconds]
      .map(item => String(item).padStart(2, '0'))
      .join(':');
  };

  getJobSwathWidth = (report) => {
    const polygons = report && Array.isArray(report.polygons) ? report.polygons : [];

    for (let i = 0; i < polygons.length; i++) {
      const polygon = polygons[i] || {};
      if (polygon.type === 'Z') continue;
      const abData = polygon.abData || {};
      const width = this.getOptionalNumber(abData.swathWidth);
      if (width !== null && width > 0) return width;
    }

    return null;
  };

  getTelemetryReport = (value) => {
    const visited = [];
    let bestReport = null;
    let bestScore = -1;

    const inspect = (current, depth) => {
      if (depth > 8 || current === null || current === undefined) return;

      if (typeof current === 'string') {
        const text = current.trim();
        if (!text) return;
        try {
          inspect(JSON.parse(text), depth + 1);
        } catch (e) { }
        return;
      }

      if (typeof current !== 'object') return;
      if (visited.indexOf(current) >= 0) return;
      visited.push(current);

      if (!Array.isArray(current)) {
        let score = 0;
        if (current.apl && typeof current.apl === 'object') score += 5;
        if (current.travel && typeof current.travel === 'object') score += 3;
        if (current.loop && typeof current.loop === 'object') score += 3;
        if (current.fuel && typeof current.fuel === 'object') score += 3;
        if (this.getOptionalNumber(current.tTotal) !== null) score += 2;
        if (this.getOptionalNumber(current.tVoo) !== null) score += 1;
        if (Array.isArray(current.polygons)) score += 1;

        if (score > bestScore) {
          bestScore = score;
          bestReport = current;
        }
      }

      if (Array.isArray(current)) {
        for (let i = 0; i < current.length; i++) inspect(current[i], depth + 1);
        return;
      }

      const keys = Object.keys(current);
      for (let i = 0; i < keys.length; i++) {
        inspect(current[keys[i]], depth + 1);
      }
    };

    inspect(value, 0);
    return bestScore >= 5 ? bestReport : null;
  };

  getJobReportPolygonDate = (polygon, end) => {
    if (!polygon) return null;
    const candidates = end
      ? [polygon.time_fim, polygon.endDate, polygon.end_date, polygon.dateFim]
      : [polygon.time_ini, polygon.startDate, polygon.start_date, polygon.dateIni];
    for (let i = 0; i < candidates.length; i++) {
      const value = candidates[i];
      if (value === null || value === undefined || value === '') continue;
      const numeric = Number(value);
      if (Number.isFinite(numeric) && numeric > 0) return numeric;
      const parsed = new Date(value).getTime();
      if (Number.isFinite(parsed) && parsed > 0) return parsed;
    }
    return null;
  };

  getJobClimatePolygons = (report) => {
    const polygons = report && Array.isArray(report.polygons) ? report.polygons : [];
    return polygons.filter((polygon) => polygon && polygon.guid && String(polygon.type || '').toUpperCase() !== 'T');
  };

  readPolygonClimate = (idJob, guid) => {
    if (!idJob || !guid) return Promise.resolve([]);
    const apiUrl = Config.server + '/polygon_climate/' + idJob + '/' + guid;
    return fetch(apiUrl, {
      method: 'GET',
      headers: { 'Authorization': localStorage.getItem('access-token-jwt') }
    }).then((res) => res.status === 200 ? res.json() : [])
      .then((result) => Array.isArray(result) ? result : [])
      .catch(() => []);
  };

  getPolygonClimateEndpointData = (records, type) => {
    const list = Array.isArray(records) ? records : [];
    for (let i = 0; i < list.length; i++) {
      const record = list[i] || {};
      let values = [];
      try {
        values = JSON.parse(type === 'start' ? (record.stationsStartData || '[]') : (record.stationsEndData || '[]'));
      } catch (e) {
        values = [];
      }
      if (!Array.isArray(values) || !values.length) continue;
      const selectedIdSource = record.id_source;
      const selected = values.find((item) => item && item.id_source === selectedIdSource) || values[0];
      if (selected) return selected;
    }
    return null;
  };

  getJobClimatePolygonPoint = (polygon) => {
    const coordinates = polygon && polygon.poly && polygon.poly.geometry && Array.isArray(polygon.poly.geometry.coordinates)
      ? polygon.poly.geometry.coordinates
      : null;
    const first = coordinates && coordinates[0] && coordinates[0][0];
    if (!Array.isArray(first) || first.length < 2) return null;
    const lng = this.getOptionalNumber(first[0]);
    const lat = this.getOptionalNumber(first[1]);
    return lat !== null && lng !== null ? { lat: lat, lng: lng } : null;
  };

  getJobClimateThermalInversion = (data) => {
    const res = this.props.res || {};
    const temperature = this.getOptionalNumber(data && data.temperature);
    const airTemperature = this.getOptionalNumber(data && data.air_temperature);
    if (temperature === null || airTemperature === null) return res.DESABILITADO || '-';
    if (airTemperature - 5 > temperature) return res.GRANDES_INVERSÃO_TERMICA;
    if (airTemperature - 1 > temperature) return res.PODE_ESTAR_INVERSÃO_TERMICA;
    return res.NAO_OCORRENDO_INVERSAO_TERMICA;
  };

  readWeatherDataByLocation = (polygon, end) => {
    const point = this.getJobClimatePolygonPoint(polygon);
    const date = this.getJobReportPolygonDate(polygon, end);
    if (!point || !date) return Promise.resolve(null);

    const apiUrl = Config.server + '/weather_data/location/' + point.lat + '/' + point.lng + '/' + date;
    return fetch(apiUrl, {
      method: 'GET',
      headers: { 'Authorization': localStorage.getItem('access-token-jwt') }
    }).then((res) => res.status === 200 ? res.json() : [])
      .then((result) => {
        const values = Array.isArray(result) ? result : [];
        if (!values.length) return null;
        const data = Object.assign({}, values[0]);
        data.lat = this.getOptionalNumber(data.lat);
        data.lng = this.getOptionalNumber(data.lng);
        data.thermalInversion = this.getJobClimateThermalInversion(data);
        if (end) {
          data.endDate = data.datetime;
          data.endDateApplication = date;
        } else {
          data.startDate = data.datetime;
          data.startDateApplication = date;
        }
        return data;
      })
      .catch(() => null);
  };

  readJobClimateSummary = (job, report) => {
    const idJob = toNumber(job && job.id, 0);
    const polygons = this.getJobClimatePolygons(report);
    if (idJob <= 0 || !polygons.length) return Promise.resolve(null);

    const firstPolygon = polygons.slice().sort((a, b) => {
      const dateA = this.getJobReportPolygonDate(a, false);
      const dateB = this.getJobReportPolygonDate(b, false);
      if (dateA === null && dateB === null) return polygons.indexOf(a) - polygons.indexOf(b);
      if (dateA === null) return 1;
      if (dateB === null) return -1;
      return dateA - dateB;
    })[0];
    const lastPolygon = polygons.slice().sort((a, b) => {
      const dateA = this.getJobReportPolygonDate(a, true);
      const dateB = this.getJobReportPolygonDate(b, true);
      if (dateA === null && dateB === null) return polygons.indexOf(a) - polygons.indexOf(b);
      if (dateA === null) return -1;
      if (dateB === null) return 1;
      return dateA - dateB;
    })[polygons.length - 1];

    const firstGuid = String(firstPolygon.guid || '');
    const lastGuid = String(lastPolygon.guid || '');
    const firstRequest = this.readPolygonClimate(idJob, firstGuid);
    const lastRequest = firstGuid === lastGuid ? firstRequest : this.readPolygonClimate(idJob, lastGuid);

    return Promise.all([firstRequest, lastRequest]).then((result) => {
      const savedStart = this.getPolygonClimateEndpointData(result[0], 'start');
      const savedEnd = this.getPolygonClimateEndpointData(result[1], 'end');
      const startRequest = savedStart ? Promise.resolve(savedStart) : this.readWeatherDataByLocation(firstPolygon, false);
      const endRequest = savedEnd ? Promise.resolve(savedEnd) : this.readWeatherDataByLocation(lastPolygon, true);

      return Promise.all([startRequest, endRequest]).then((climate) => {
        const start = climate[0];
        const end = climate[1];
        if (!start && !end) return null;
        return {
          start: start || null,
          end: end || null,
          startDate: (start && (start.datetime || start.startDateApplication || start.startDate)) || this.getJobReportPolygonDate(firstPolygon, false),
          endDate: (end && (end.datetime || end.endDateApplication || end.endDate)) || this.getJobReportPolygonDate(lastPolygon, true)
        };
      });
    });
  };

  handleJobClimateSummary = (job) => {
    const idJob = toNumber(job && job.id, 0);
    if (!this.props.hasWeatherStations || idJob <= 0 || !this.hasJobExecution(job)) return Promise.resolve(null);
    if (Object.prototype.hasOwnProperty.call(this.jobClimateSummaries, idJob)) {
      return Promise.resolve(this.jobClimateSummaries[idJob]);
    }
    if (this.jobClimateSummaryRequests[idJob]) return this.jobClimateSummaryRequests[idJob];

    const request = this.handleTelemetryReport(job)
      .then((value) => this.readJobClimateSummary(job, this.getTelemetryReport(value)))
      .then((summary) => {
        this.jobClimateSummaries[idJob] = summary || null;
        delete this.jobClimateSummaryRequests[idJob];
        return summary || null;
      })
      .catch(() => {
        this.jobClimateSummaries[idJob] = null;
        delete this.jobClimateSummaryRequests[idJob];
        return null;
      });

    this.jobClimateSummaryRequests[idJob] = request;
    return request;
  };

  handleJobClimateSummaries = (jobs) => {
    const safeJobs = Array.isArray(jobs) ? jobs : [];
    return Promise.all(safeJobs.map((job) => this.handleJobClimateSummary(job))).then((summaries) => {
      const byJob = {};
      for (let i = 0; i < safeJobs.length; i++) {
        if (safeJobs[i] && safeJobs[i].id && summaries[i]) byJob[safeJobs[i].id] = summaries[i];
      }
      return byJob;
    });
  };

  getJobTelemetryData = (focusItem) => {
    const report = this.getTelemetryReport(
      focusItem && focusItem.telemetryReport
        ? focusItem.telemetryReport
        : (focusItem && focusItem.applicationReport ? focusItem.applicationReport : null)
    );
    if (!report) return null;

    const application = report.apl || {};
    const travel = report.travel || {};
    const loop = report.loop || {};
    const fuel = report.fuel || {};
    const applicationTime = toNumber(application.tApl, 0);
    const travelTime = toNumber(travel.tTravel, 0) + toNumber(fuel.tFuelS, 0);
    const loopTime = toNumber(loop.tLoop, 0);
    const weightedTime = applicationTime + toNumber(travel.tTravel, 0) + loopTime;
    const averageSpeed = weightedTime > 0
      ? ((applicationTime * toNumber(application.vApl, 0)) +
        (toNumber(travel.tTravel, 0) * toNumber(travel.vTravel, 0)) +
        (loopTime * toNumber(loop.vLoop, 0))) / weightedTime * 3.6
      : null;
    const totalTime = toNumber(report.tTotal, 0);
    const area = this.getOptionalNumber(application.area_aplicada);
    const yieldValue = area !== null && totalTime > 0 ? area / (totalTime / 3600) : null;
    const days = Array.isArray(report.days) ? report.days : [];
    const uniqueDays = {};
    for (let i = 0; i < days.length; i++) {
      const date = new Date(days[i] && days[i].date);
      if (!isNaN(date.getTime())) uniqueDays[date.getFullYear() + '-' + date.getMonth() + '-' + date.getDate()] = true;
    }
    let executionStart = null;
    let executionEnd = null;
    if (days.length) {
      const firstDate = new Date(days[0].date);
      const lastDate = new Date(days[days.length - 1].date);
      if (!isNaN(firstDate.getTime())) executionStart = firstDate;
      if (!isNaN(lastDate.getTime())) {
        lastDate.setSeconds(lastDate.getSeconds() + toNumber(days[days.length - 1].time, 0));
        executionEnd = lastDate;
      }
    }

    return {
      client: report.customerName || '',
      aircraft: report.aeronavePrefix || report.planeName || report.plane || '',
      area: application.area_aplicada,
      consumption: Math.ceil(toNumber(application.totalApl, 0)),
      averageFlow: application.mApl,
      refuels: fuel.qtdFuel,
      swathWidth: this.getJobSwathWidth(report),
      averageHeight: application.aApl,
      averageSpeed: averageSpeed,
      applicationSpeed: this.getOptionalNumber(application.vApl) !== null ? Number(application.vApl) * 3.6 : null,
      travelSpeed: this.getOptionalNumber(fuel.vFuelS) !== null ? Number(fuel.vFuelS) * 3.6 : null,
      loopSpeed: this.getOptionalNumber(loop.vLoop) !== null ? Number(loop.vLoop) * 3.6 : null,
      yield: yieldValue,
      totalDays: Object.keys(uniqueDays).length,
      executionStart: executionStart,
      executionEnd: executionEnd,
      totalTime: report.tTotal,
      groundTime: toNumber(report.tTotal, 0) - toNumber(report.tVoo, 0),
      travelTime: travelTime,
      loopTime: loopTime,
      applicationTime: applicationTime
    };
  };

  formatReportAreaRate = (hectaresPerHour, decimals = 2) => {
    const value = this.getOptionalNumber(hectaresPerHour);
    if (value === null) return '-';
    return this.formatJobTelemetryValue(
      Units.convertAreaRate(value, 'ha', this.props.res),
      decimals,
      Units.getAreaRateUnit(this.props.res)
    );
  };

  formatClimateTemperature = (value) => {
    const numberValue = this.getOptionalNumber(value);
    if (numberValue === null) return '-';
    if (this.props.res.LINGUA === 'en-US') return ((numberValue * 9 / 5) + 32).toFixed(1) + ' °F';
    return numberValue.toFixed(1) + ' °C';
  };

  formatClimateWindSpeed = (value) => {
    const numberValue = this.getOptionalNumber(value);
    if (numberValue === null) return '-';
    return this.formatJobTelemetryValue(
      Units.convertSpeed(numberValue, 'm/s', this.props.res),
      1,
      Units.getSpeedUnit(this.props.res)
    );
  };

  getJobClimateStationName = (data) => {
    const res = this.props.res || {};
    if (!data) return '-';

    const sourceId = toNumber(
      data.id_source !== undefined
        ? data.id_source
        : (data.source_id !== undefined ? data.source_id : data.idSource),
      0
    );

    if (sourceId > 0) {
      const stations = Array.isArray(this.props.weatherStations) ? this.props.weatherStations : [];
      const station = stations.find((item) => this.getWeatherStationId(item) === sourceId);
      if (station) {
        const stationName = station.source_name || station.name;
        if (stationName) return stationName;
      }
    }

    const savedName = data.source_name || data.sourceName || data.weather_source_name || data.name;
    if (savedName) return savedName;

    return sourceId > 0 ? res.ESTACAO + ' ' + sourceId : '-';
  };

  renderJobClimateSummary = (summary) => {
    if (!summary || (!summary.start && !summary.end)) return null;
    const res = this.props.res || {};
    const renderEndpoint = (title, data, dateValue) => {
      if (!data) return null;
      const thermalInversionText = this.getWeatherThermalInversionText(data);
      const thermalInversionColor = this.getWeatherThermalInversionColor(data);
      const deltaTConditionText = this.getWeatherDeltaTConditionText(data.delta_t);
      const deltaTConditionColor = this.getWeatherDeltaTConditionColor(data.delta_t);
      const deltaTValue = this.getOptionalNumber(data.delta_t);
      const deltaTText = deltaTValue !== null && deltaTValue !== -99
        ? deltaTValue.toFixed(1) + ' · ' + deltaTConditionText
        : '-';
      const dateText = this.formatPlanePositionDate(dateValue);

      return <React.Fragment key={title}>
        <div className="telemetry-section-heading">
          <div className="telemetry-section-title">{title}</div>
          <strong>{dateText}</strong>
        </div>
        {this.renderJobTelemetryRow(res.ESTACAO, this.getJobClimateStationName(data))}
        {this.renderJobTelemetryRow(res.TEMPERATURA_, this.formatClimateTemperature(data.air_temperature))}
        {this.renderJobTelemetryRow(res.UMIDADE_, this.getOptionalNumber(data.air_humidity) !== null ? Number(data.air_humidity).toFixed(1) + ' %' : '-')}
        {this.renderJobTelemetryRow(res.VENTO_VELOCIDADE, this.formatClimateWindSpeed(data.wind_speed))}
        {this.renderJobTelemetryRow(res.DIRECAO_VENTO, this.renderWeatherWindDirection(data.wind_direction))}
        {this.renderJobTelemetryRow(res.PRESSAO, this.getOptionalNumber(data.pressure) !== null ? Number(data.pressure).toFixed(1) + ' hPa' : '-')}
        {this.renderJobTelemetryRow(
          res.DELTA_T,
          deltaTConditionColor
            ? <span style={{ color: deltaTConditionColor }}>{deltaTText}</span>
            : deltaTText
        )}
        {this.renderJobTelemetryRow(
          res.INVERSAO_TERMICA,
          thermalInversionColor
            ? <span style={{ color: thermalInversionColor }}>{thermalInversionText}</span>
            : thermalInversionText
        )}
      </React.Fragment>;
    };

    return <div className="telemetry-climate-summary">
      <div className="telemetry-section-title telemetry-section-main-title">{res.ANALISE_CLIMA || res.VARIAVEIS_CLIMA || res.ESTACAO}</div>
      {renderEndpoint(res.INICIO, summary.start, summary.startDate)}
      {renderEndpoint(res.FIM, summary.end, summary.endDate)}
    </div>;
  };

  formatTelemetryText = (value) => {
    const textValue = value === undefined || value === null ? '' : String(value).trim();
    if (!textValue) return '';
    return textValue.charAt(0).toUpperCase() + textValue.slice(1).toLowerCase();
  };

  renderJobTelemetryRow = (label, value) => (
    <div className="telemetry-row" key={label}>
      <span>{label}</span>
      <strong>{value}</strong>
    </div>
  );

  renderMobileWorkIcon = () => (
    <SvgIcon viewBox="0 0 1000 1000" className="mobile-focus-svg-icon mobile-focus-work-icon" style={{ color: 'white', transform: 'rotate(180deg)' }}>
      <path d="M122.513 646.499c3.66376,13.0115 11.813,21.1608 24.4821,24.3451l273.344 0 36.1582 0.47937 25.5093 0 -0.513611 13.662 0 107.002c4.17737,9.51892 9.69012,14.8605 16.504,16.1959 6.84814,1.16418 14.5181,3.97192 23.0098,8.14929 8.14929,-4.17737 15.4768,-6.98511 22.1537,-8.14929 6.67694,-1.33539 12.3267,-6.84814 16.8464,-16.6752l0 -106.523 0 -13.662 25.5093 0 35.6446 -0.47937 273.344 0c12.3267,-3.18439 20.5102,-11.3337 24.4821,-24.3451l-0.47937 -88.8204 -365.828 0 -6.84814 -69.3374 0.992981 10.1695 3.01318 -106.009 16.1616 0 4.34857 145.66 4.31433 -145.66 16.1616 0 4.34857 145.66 4.00616 -145.66 15.9904 0 4.48553 145.66 4.34857 -145.66 10.3407 0c0.992981,-0.513611 1.81476,-0.992981 2.67078,-1.33539 0.821777,-0.342407 1.64355,-0.650574 2.63653,-0.992981l4.34857 147.988 4.48553 -156.822c0.684814,-0.992981 1.19842,-2.67078 1.849,-5.34155l-1.33539 -6.33453 3.83496 -143.16 -18.0106 0 2.84198 102.174 -0.513611 -1.50659 -11.6418 0 3.32135 -100.668 -18.49 0 2.97894 100.668 -12.3267 0 3.01318 -100.668 -18.0106 0 2.84198 100.668 -12.1555 0 2.97894 -100.668 -18.1476 0 2.49957 100.668 -12.1897 0 2.84198 -100.668 -18.0106 0 2.84198 100.668 -1.81476 0c-4.00616,-1.67779 -7.36175,-4.17737 -10.3407,-7.32751l2.97894 -93.3402 -18.1476 0 2.49957 92.8266c-3.01318,3.32135 -6.50574,5.82092 -10.8543,7.84112l-0.821777 0 2.32837 -100.668 -17.9764 0 2.80774 100.668 -12.1555 0 3.01318 -100.668 -18.1818 0 3.01318 100.668 -12.6691 0 2.80774 -100.668 -17.9764 0 2.97894 100.668 -12.3267 0 3.01318 -100.668 -18.1818 0 3.01318 100.668 -12.1555 0 -2.49957 8.8341 1.98596 -7.32751 2.84198 -102.174 -18.0106 0 4.34857 143.16 -1.849 6.33453c-0.342407,-1.33539 0.342407,0.513611 1.849,5.34155l4.99914 156.822 4.31433 -147.988c0,0 1.67779,0.821777 4.82794,2.32837l10.8543 0 4.31433 145.66 4.51977 -145.66 15.9904 0 4.48553 145.66 3.83496 -145.66 16.6752 0 3.83496 145.66 4.34857 -145.66 16.1616 0 2.97894 100.668 -6.33453 64.5095 -365.828 0 -0.47937 88.8204zm19.6542 -419.997l9.17651 311.659 8.8341 -311.659 -18.0106 0zm24.8245 0l8.8341 311.659 9.17651 -311.659 -18.0106 0zm24.3451 0l9.31347 311.659 8.8341 -311.659 -18.1476 0zm24.9957 0l8.8341 311.659 9.17651 -311.659 -18.0106 0zm24.3451 0l9.31347 311.659 8.8341 -311.659 -18.1476 0zm24.9957 0l8.8341 311.659 9.14227 -311.659 -17.9764 0zm24.3109 0l9.34771 311.659 8.8341 -311.659 -18.1818 0zm24.5163 0l9.17651 311.659 9.31347 -311.659 -18.49 0zm24.8245 0l8.8341 311.659 9.34771 -311.659 -18.1818 0zm24.5163 0l9.14227 311.659 9.34771 -311.659 -18.49 0zm135.148 228.488l1.50659 -62.4893 4.34857 0 -5.85516 62.4893zm37.6648 -62.4893l4.86218 0 1.98596 72.3164 -6.84814 -72.3164zm110.837 -9.65588c0,-0.342407 0.171204,-0.513611 0.513611,-0.513611l-0.513611 0.513611zm12.1897 -156.343l9.31347 311.659 8.6629 -311.659 -17.9764 0zm24.8245 0l8.8341 311.659 9.31347 -311.659 -18.1476 0zm24.4821 0l9.34771 311.659 9.17651 -311.659 -18.5242 0zm24.8588 0l9.31347 311.659 8.8341 -311.659 -18.1476 0zm24.4821 0l9.17651 311.659 9.31347 -311.659 -18.49 0zm24.8245 0l9.34771 311.659 8.8341 -311.659 -18.1818 0zm24.0027 0l9.17651 311.659 8.8341 -311.659 -18.0106 0zm24.8588 0l9.31347 311.659 8.8341 -311.659 -18.1476 0zm24.4821 0l9.17651 311.659 9.31347 -311.659 -18.49 0zm24.8245 0l8.8341 311.659 9.34771 -311.659 -18.1818 0z" />
    </SvgIcon>
  );

  renderMobilePlaneIcon = () => (
    <SvgIcon viewBox="0 0 800 800" className="mobile-focus-svg-icon mobile-focus-plane-icon" style={{ color: 'white' }}>
      <path d="M 0 156.81 L 0 0 L 65.28 0 L 125.41 103.72 L 243.59 87.54 C 260.71 67.18 282.02 49.29 307.72 33.88 C 333.41 18.46 354.72 10.66 371.85 10.66 L 381.93 10.66 C 395.25 10.28 409.14 19.04 423.8 36.54 C 438.26 54.24 448.73 67.56 455.58 76.51 L 734.36 95.92 L 734.36 21.32 L 753.2 21.32 L 753.2 116.66 C 757.39 116.66 762.34 117.61 768.62 119.7 C 774.71 121.8 777.75 123.89 777.75 126.17 C 777.75 128.65 774.71 132.07 768.62 136.45 C 762.34 140.64 757.58 142.73 753.77 142.73 L 753.77 242.06 L 734.74 242.06 L 734.74 168.99 L 569.18 208 L 569.18 251.01 C 583.46 256.15 590.5 266.23 590.5 281.08 C 590.5 302.58 579.84 313.43 558.72 313.43 C 537.02 313.43 526.37 302.58 526.37 281.08 C 526.37 265.47 533.98 255.19 549.2 250.44 L 549.2 210.29 L 501.82 225.89 L 384.78 225.89 L 0 156.81 Z M 23.98 430.46 C 23.98 416.38 30.83 409.34 44.53 409.34 C 58.81 409.34 65.85 416.38 65.85 430.46 C 65.85 444.16 58.81 451.2 44.53 451.2 C 30.83 451.2 23.98 444.16 23.98 430.46 Z M 26.83 579.27 C 26.83 565.19 33.88 558.15 47.96 558.15 C 61.66 558.15 68.51 565.19 68.51 579.27 C 68.51 593.16 61.66 600.01 47.96 600.01 C 33.88 600.01 26.83 593.16 26.83 579.27 Z M 65.85 652.92 C 65.85 639.22 72.89 632.37 86.97 632.37 C 100.67 632.37 107.71 639.22 107.71 652.92 C 107.71 667 100.67 674.04 86.97 674.04 C 72.89 674.04 65.85 667 65.85 652.92 Z M 66.42 504.11 C 66.42 490.02 73.46 482.98 87.54 482.98 C 101.24 482.98 108.09 490.02 108.09 504.11 C 108.09 517.81 101.24 524.66 87.54 524.66 C 73.46 524.66 66.42 517.81 66.42 504.11 Z M 106.57 430.46 C 106.57 416.38 113.61 409.34 127.69 409.34 C 141.39 409.34 148.24 416.38 148.24 430.46 C 148.24 444.16 141.39 451.2 127.69 451.2 C 113.61 451.2 106.57 444.16 106.57 430.46 Z M 109.23 579.27 C 109.23 565.19 116.28 558.15 130.55 558.15 C 144.25 558.15 151.1 565.19 151.1 579.27 C 151.1 593.16 144.25 600.01 130.55 600.01 C 116.28 600.01 109.23 593.16 109.23 579.27 Z M 148.24 652.92 C 148.24 639.22 155.29 632.37 169.56 632.37 C 183.26 632.37 190.11 639.22 190.11 652.92 C 190.11 667 183.26 674.04 169.56 674.04 C 155.29 674.04 148.24 667 148.24 652.92 Z M 149.39 504.11 C 149.39 490.02 156.24 482.98 170.13 482.98 C 183.83 482.98 190.68 490.02 190.68 504.11 C 190.68 517.81 183.83 524.66 170.13 524.66 C 156.24 524.66 149.39 517.81 149.39 504.11 Z M 151.1 355.86 C 151.1 341.97 157.95 335.12 171.65 335.12 C 185.92 335.12 192.97 341.97 192.97 355.86 C 192.97 369.95 185.92 376.99 171.65 376.99 C 157.95 376.99 151.1 369.95 151.1 355.86 Z M 188.97 430.46 C 188.97 416.38 196.01 409.34 210.28 409.34 C 223.98 409.34 230.83 416.38 230.83 430.46 C 230.83 444.16 223.98 451.2 210.28 451.2 C 196.01 451.2 188.97 444.16 188.97 430.46 Z M 192.39 579.27 C 192.39 565.19 199.25 558.15 212.95 558.15 C 226.65 558.15 233.69 565.19 233.69 579.27 C 233.69 593.16 226.65 600.01 212.95 600.01 C 199.25 600.01 192.39 593.16 192.39 579.27 Z M 231.41 652.92 C 231.41 639.22 238.26 632.37 251.96 632.37 C 266.23 632.37 273.27 639.22 273.27 652.92 C 273.27 667 266.23 674.04 251.96 674.04 C 238.26 674.04 231.41 667 231.41 652.92 Z M 231.98 504.11 C 231.98 490.02 238.83 482.98 252.53 482.98 C 266.61 482.98 273.84 490.02 273.84 504.11 C 273.84 517.81 266.61 524.66 252.53 524.66 C 238.83 524.66 231.98 517.81 231.98 504.11 Z M 233.69 355.86 C 233.69 341.97 240.54 335.12 254.24 335.12 C 268.32 335.12 275.36 341.97 275.36 355.86 C 275.36 369.95 268.32 376.99 254.24 376.99 C 240.54 376.99 233.69 369.95 233.69 355.86 Z M 272.13 430.46 C 272.13 416.38 278.98 409.34 292.68 409.34 C 306.76 409.34 313.81 416.38 313.81 430.46 C 313.81 444.16 306.76 451.2 292.68 451.2 C 278.98 451.2 272.13 444.16 272.13 430.46 Z M 274.79 579.27 C 274.79 565.19 281.83 558.15 295.54 558.15 C 309.62 558.15 316.66 565.19 316.66 579.27 C 316.66 593.16 309.62 600.01 295.54 600.01 C 281.83 600.01 274.79 593.16 274.79 579.27 Z M 277.08 284.5 C 277.08 270.23 283.93 263.19 297.82 263.19 C 311.9 263.19 318.94 270.23 318.94 284.5 C 318.94 298.2 311.9 305.05 297.82 305.05 C 283.93 305.05 277.08 298.2 277.08 284.5 Z M 313.81 652.92 C 313.81 639.22 321.04 632.37 335.12 632.37 C 348.82 632.37 355.67 639.22 355.67 652.92 C 355.67 667 348.82 674.04 335.12 674.04 C 321.04 674.04 313.81 667 313.81 652.92 Z M 314.38 504.11 C 314.38 490.02 321.61 482.98 335.69 482.98 C 349.39 482.98 356.24 490.02 356.24 504.11 C 356.24 517.81 349.39 524.66 335.69 524.66 C 321.61 524.66 314.38 517.81 314.38 504.11 Z M 316.09 355.86 C 316.09 341.97 323.13 335.12 337.4 335.12 C 351.1 335.12 357.95 341.97 357.95 355.86 C 357.95 369.95 351.1 376.99 337.4 376.99 C 323.13 376.99 316.09 369.95 316.09 355.86 Z M 345.58 76.51 L 418.66 76.51 L 382.5 39.21 L 345.58 39.21 L 345.58 76.51 Z M 354.53 430.46 C 354.53 416.38 361.38 409.34 375.27 409.34 C 389.35 409.34 396.39 416.38 396.39 430.46 C 396.39 444.16 389.35 451.2 375.27 451.2 C 361.38 451.2 354.53 444.16 354.53 430.46 Z M 357.38 579.27 C 357.38 565.19 364.42 558.15 378.51 558.15 C 392.4 558.15 399.25 565.19 399.25 579.27 C 399.25 593.16 392.4 600.01 378.51 600.01 C 364.42 600.01 357.38 593.16 357.38 579.27 Z M 359.67 284.5 C 359.67 270.23 366.71 263.19 380.79 263.19 C 394.49 263.19 401.34 270.23 401.34 284.5 C 401.34 298.2 394.49 305.05 380.79 305.05 C 366.71 305.05 359.67 298.2 359.67 284.5 Z M 369.18 143.87 C 387.26 149.58 405.53 152.82 423.99 153.96 C 442.44 155.1 461.1 155.67 480.12 155.67 C 498.58 155.29 516.85 154.34 534.74 152.82 C 552.44 151.29 570.13 147.3 587.64 140.64 C 568.99 134.93 550.73 131.31 532.65 129.79 C 514.76 128.08 496.87 126.74 479.55 126.17 C 432.36 128.65 395.44 134.55 369.18 143.87 Z M 396.96 504.11 C 396.96 490.02 404.01 482.98 418.09 482.98 C 431.98 482.98 438.83 490.02 438.83 504.11 C 438.83 517.81 431.98 524.66 418.09 524.66 C 404.01 524.66 396.96 517.81 396.96 504.11 Z M 398.68 355.86 C 398.68 341.97 405.72 335.12 419.8 335.12 C 433.5 335.12 440.54 341.97 440.54 355.86 C 440.54 369.95 433.5 376.99 419.8 376.99 C 405.72 376.99 398.68 369.95 398.68 355.86 Z M 437.12 430.46 C 437.12 416.38 444.16 409.34 458.24 409.34 C 472.13 409.34 478.98 416.38 478.98 430.46 C 478.98 444.16 472.13 451.2 458.24 451.2 C 444.16 451.2 437.12 444.16 437.12 430.46 Z M 442.06 284.5 C 442.06 270.23 449.11 263.19 463.38 263.19 C 477.08 263.19 483.93 270.23 483.93 284.5 C 483.93 298.2 477.08 305.05 463.38 305.05 C 449.11 305.05 442.06 298.2 442.06 284.5 Z" />
    </SvgIcon>
  );


  getSelectedStationFocusItem = () => {
    const items = this.getFilteredStationFocusItems();
    if (!items.length) return null;
    return items[this.getSafeJobIndex(this.state.currentStationIndex, items.length)] || null;
  };

  flyToSelectedStation = () => {
    this.cameraFocusNavigationDetached = false;
    const focusItem = this.getSelectedStationFocusItem();
    this.cameraFollowEnabled = false;
    this.cameraFollowPlaneId = 0;
    const controller = this.viewer && !this.viewer.isDestroyed() && this.viewer.scene
      ? this.viewer.scene.screenSpaceCameraController
      : null;
    if (controller) controller.enableZoom = true;
    if (!focusItem || !focusItem.point || !this.viewer) return;

    const target = this.getWeatherStationCameraTarget(focusItem);
    if (!target) return;

    const rangeMeters = Math.max(cameraRangeMinimumMeters, Math.min(cameraRangeMaximumMeters, toNumber(this.state.cameraRangeMeters, 900)));
    this.setState({ freeMode: false, cameraFocusType: 'station', cameraRangeMeters: rangeMeters }, () => {
      this.viewer.camera.flyToBoundingSphere(new Cesium.BoundingSphere(target, 60), {
        offset: new Cesium.HeadingPitchRange(
          Cesium.Math.toRadians(this.state.cameraHeadingDeg),
          Cesium.Math.toRadians(this.state.cameraPitchDeg),
          rangeMeters
        ),
        duration: 0.7,
        complete: () => {
          if (!this.mounted || this.state.freeMode || this.state.cameraFocusType !== 'station') return;
          this.applyCollapsedMobileFocusOffset(rangeMeters);
        }
      });
    });
  };

  goToNextStation = () => {
    const items = this.getFilteredStationFocusItems();
    if (!items.length) return;
    const next = (this.state.currentStationIndex + 1) >= items.length ? 0 : this.state.currentStationIndex + 1;
    this.setState({ currentStationIndex: next, freeMode: false, cameraFocusType: 'station' }, () => { this.updateWeatherStationCoverageVisibility(); this.flyToSelectedStation(); });
  };

  goToPreviousStation = () => {
    const items = this.getFilteredStationFocusItems();
    if (!items.length) return;
    const previous = (this.state.currentStationIndex - 1) < 0 ? items.length - 1 : this.state.currentStationIndex - 1;
    this.setState({ currentStationIndex: previous, freeMode: false, cameraFocusType: 'station' }, () => { this.updateWeatherStationCoverageVisibility(); this.flyToSelectedStation(); });
  };

  activateStationFocus = () => {
    this.cameraFollowEnabled = false;
    this.cameraFollowPlaneId = 0;
    this.setState({ cameraFocusType: 'station', freeMode: false }, () => {
      this.updateWeatherStationCoverageVisibility();
      if (this.getFilteredStationFocusItems().length) this.flyToSelectedStation();
      else this.flyToPoints(this.getCurrentPoints());
    });
  };

  activateJobFocus = () => {
    this.cameraFollowEnabled = false;
    this.cameraFollowPlaneId = 0;

    this.setState({
      cameraFocusType: 'job',
      freeMode: false
    }, () => {
      const focusItems = this.state.focusItems || [];
      if (focusItems.length) {
        this.flyToSelectedJob();
      } else {
        this.flyToPoints(this.getCurrentPoints());
      }
    });
  };

  activatePlaneFocus = () => {
    this.cameraFollowEnabled = false;
    this.cameraFollowPlaneId = 0;

    this.setState({
      cameraFocusType: 'plane',
      freeMode: false
    }, () => {
      const planeFocusItems = this.getFilteredPlaneFocusItems();
      if (planeFocusItems.length) {
        this.flyToSelectedPlane();
      } else {
        this.flyToPoints(this.getCurrentPoints());
      }
    });
  };

  renderMobileFocusSwitcher = () => {
    const res = this.props.res || {};
    const hasJobs = (this.state.focusItems || []).length > 0;
    const hasPlanes = (this.state.planeFocusItems || []).length > 0;
    const hasStations = (this.state.stationFocusItems || []).length > 0;

    return (
      <div className="mobile-focus-switcher">
        <button
          type="button"
          className={`mobile-focus-switch-button${this.state.cameraFocusType === 'job' ? ' is-active' : ''}`}
          onClick={this.activateJobFocus}
          title={hasJobs ? res.TRABALHO : res.NENHUM_TRABALHO_EXECUCAO_HOJE}
          disabled={!hasJobs}
        >
          {this.renderMobileWorkIcon()}
          <span>{res.TRABALHO}</span>
        </button>
        <button
          type="button"
          className={`mobile-focus-switch-button${this.state.cameraFocusType === 'plane' ? ' is-active' : ''}`}
          onClick={this.activatePlaneFocus}
          title={hasPlanes ? res.AERONAVE : res.NENHUM_AERONAVE}
          disabled={!hasPlanes}
        >
          {this.renderMobilePlaneIcon()}
          <span>{res.AERONAVE}</span>
        </button>
        {this.props.hasWeatherStations && (
          <button
            type="button"
            className={`mobile-focus-switch-button${this.state.cameraFocusType === 'station' ? ' is-active' : ''}`}
            onClick={this.activateStationFocus}
            title={(this.state.stationFocusItems || []).length ? (res.ESTACAO) : (res.SEM_ESTACAO_ONLINE)}
            disabled={!hasStations}
          >
            <MaterialIcon icon="directions_car" className="mobile-focus-svg-icon" />
            <span>{res.ESTACAO}</span>
          </button>
        )}
      </div>
    );
  };

  getJobProcessItem = (list) => {
    const items = Array.isArray(list) ? list.filter(item => item && item.guid) : [];
    if (!items.length) return null;

    return items.find(item => Number(item.progress_task) === 100 || item.status_task === 'finished' || !item.status_task) || items[0];
  };

  getJobApplicationReportItem = (job) => {
    return this.getJobProcessItem(job && job.list_rel);
  };

  getJobQualityReportItem = (job) => {
    return this.getJobProcessItem(job && job.list_analise);
  };

  stopTelemetryActionEvent = (event) => {
    if (!event) return;
    if (event.preventDefault) event.preventDefault();
    if (event.stopPropagation) event.stopPropagation();
  };

  openJobApplicationReport = (event, reportItem) => {
    this.stopTelemetryActionEvent(event);
    if (!reportItem || !this.props.onOpenApplicationReport) return;
    this.props.onOpenApplicationReport(reportItem);
  };

  openJobQualityReport = (event, qualityItem) => {
    this.stopTelemetryActionEvent(event);
    if (!qualityItem || !this.props.onOpenQualityReport) return;
    this.props.onOpenQualityReport(qualityItem);
  };

  renderJobTelemetry = (forceVisible = false) => {
    if (this.state.freeMode || (!forceVisible && this.state.cameraFocusType !== 'job')) return null;

    const focusItem = this.getSelectedFocusItem();
    if (!focusItem) return null;

    const telemetry = this.getJobTelemetryData(focusItem);
    const point = focusItem.point;
    const canNavigateJobs = this.getFilteredJobFocusItems().length > 1;
    const isRealtime = focusItem.type === 'realtime';
    const rawPosition = isRealtime ? (focusItem.rawPosition || {}) : {};
    const realtimePlaneId = isRealtime && focusItem.plane
      ? this.getPlaneId(focusItem.plane)
      : 0;
    const jobConnected = !isRealtime || this.isPlaneRealtimeConnected(realtimePlaneId);
    const realtimeSourceType = isRealtime
      ? (this.playbackSourceByPlaneId[realtimePlaneId] || 'heartbeat')
      : 'activity';
    const jobExecutions = !isRealtime && focusItem.job && Array.isArray(focusItem.job.list_exe)
      ? focusItem.job.list_exe
      : [];
    const pilotNames = jobExecutions
      .map(item => item && item.pilot ? String(item.pilot).trim() : '')
      .filter((value, index, values) => value && values.indexOf(value) === index);
    const planeNames = jobExecutions
      .map(item => item && (item.plane_name || item.plane) ? String(item.plane_name || item.plane).trim() : '')
      .filter((value, index, values) => value && values.indexOf(value) === index);
    const applicationReportItem = !isRealtime ? this.getJobApplicationReportItem(focusItem.job) : null;
    const qualityReportItem = !isRealtime ? this.getJobQualityReportItem(focusItem.job) : null;

    return (
      <div className={`telemetry-board telemetry-job-board${!isRealtime && focusItem.climateSummary ? ' telemetry-job-board-climate' : ''}${!this.state.mobileCompact && this.isJobFocusItemExecuted(focusItem) ? (this.shouldShowDesktopStationTelemetry() ? ' telemetry-job-board-station-visible' : ' telemetry-job-board-executed-expanded') : ''}`}>
        <div className="telemetry-plane-navigation">
          <button
            type="button"
            className="telemetry-plane-arrow"
            onClick={this.goToPreviousJob}
            disabled={!canNavigateJobs}
            title={this.props.res.ANTERIOR}
          >
            ‹
          </button>
          <button
            type="button"
            className={`telemetry-plane-focus${jobConnected ? '' : ' telemetry-plane-focus-disconnected'}`}
            onClick={this.flyToSelectedJob}
            title={this.props.res.CENTRALIZAR}
          >
            {focusItem.type === 'realtime' ? focusItem.label : this.getJobDisplayName(focusItem.job)}
          </button>
          <button
            type="button"
            className="telemetry-plane-arrow"
            onClick={this.goToNextJob}
            disabled={!canNavigateJobs}
            title={this.props.res.PROXIMO}
          >
            ›
          </button>
        </div>
        <div className="telemetry-job-body">
          <div className="telemetry-grid">
            {isRealtime ? <>
              {this.renderJobTelemetryRow(this.formatTelemetryText(this.props.res.STATUS), this.formatTelemetryText(focusItem.status === 'running' ? this.props.res.STATUS_EM_EXECUCAO : this.props.res.AGUARDANDO_PROCESSAMENTO))}
              {this.renderJobTelemetryRow(this.formatTelemetryText(this.props.res.AERONAVE), this.getPlaneDisplayName(focusItem.plane))}
              {this.renderJobTelemetryRow(this.formatTelemetryText(this.props.res.LATITUDE), point ? this.formatPlaneTelemetryValue(point.lat, 6) : '-')}
              {this.renderJobTelemetryRow(this.formatTelemetryText(this.props.res.LONGITUDE), point ? this.formatPlaneTelemetryValue(point.lng, 6) : '-')}
              {this.renderJobTelemetryRow(
                this.formatTelemetryText(this.props.res.VELOCIDADE),
                rawPosition.speed !== undefined && rawPosition.speed !== null
                  ? this.formatRealtimeSpeed(rawPosition.speed, 2, realtimeSourceType)
                  : '-'
              )}
              {this.renderJobTelemetryRow(
                this.formatTelemetryText(this.props.res.ALTURA),
                (rawPosition.alt !== undefined && rawPosition.alt !== null) || (rawPosition.altitude !== undefined && rawPosition.altitude !== null)
                  ? this.formatRealtimeLength(rawPosition.alt !== undefined ? rawPosition.alt : rawPosition.altitude, 2)
                  : '-'
              )}
              {this.renderJobTelemetryRow(this.formatTelemetryText(this.props.res.SPRAY), this.formatTelemetryText(toNumber(rawPosition.active, rawPosition.spray ? 1 : 0) > 0 ? this.props.res.APLICANDO : this.props.res.DESLIGADO))}
              {this.renderJobTelemetryRow(
                this.formatTelemetryText(this.props.res.FLUXO),
                (rawPosition.lph !== undefined && rawPosition.lph !== null) || (rawPosition.flow !== undefined && rawPosition.flow !== null)
                  ? this.formatRealtimeFlow(rawPosition.lph !== undefined ? rawPosition.lph : rawPosition.flow, 2)
                  : '-'
              )}
              {this.renderJobTelemetryRow(
                this.formatTelemetryText(this.props.res.LARGURA_FAIXA),
                (rawPosition.swathwidth !== undefined && rawPosition.swathwidth !== null) || (rawPosition.swathWidth !== undefined && rawPosition.swathWidth !== null)
                  ? this.formatRealtimeLength(rawPosition.swathwidth !== undefined ? rawPosition.swathwidth : rawPosition.swathWidth, 2)
                  : '-'
              )}
              {this.renderJobTelemetryRow(this.formatTelemetryText(this.props.res.FAIXA_APP), rawPosition.lockedline || '-')}
              {this.renderJobTelemetryRow(this.formatTelemetryText(this.props.res.ULTIMA_ATUALIZACAO), this.formatPlanePositionDate(rawPosition.datetime || rawPosition.created))}
            </> : <>
              {this.renderJobTelemetryRow(this.formatTelemetryText(this.props.res.LATITUDE), point ? this.formatPlaneTelemetryValue(point.lat, 6) : '-')}
              {this.renderJobTelemetryRow(this.formatTelemetryText(this.props.res.LONGITUDE), point ? this.formatPlaneTelemetryValue(point.lng, 6) : '-')}
              {this.renderJobTelemetryRow(this.formatTelemetryText(this.props.res.CLIENTE), telemetry && telemetry.client ? telemetry.client : '-')}
              {this.renderJobTelemetryRow(this.formatTelemetryText(this.props.res.PILOTO), pilotNames.length ? pilotNames.join(', ') : '-')}
              {this.renderJobTelemetryRow(this.formatTelemetryText(this.props.res.AERONAVE), telemetry && telemetry.aircraft ? telemetry.aircraft : (planeNames.length ? planeNames.join(', ') : '-'))}
              {this.renderJobTelemetryRow(String(this.props.res._RENDIMENTO || '').replace(':?', Units.getAreaRateUnit(this.props.res)), telemetry ? this.formatReportAreaRate(telemetry.yield, 2) : '-')}
              {this.renderJobTelemetryRow(this.formatTelemetryText(this.props.res.TOTAL_DIAS), telemetry ? ((telemetry.totalDays || 0) + ' ' + ((telemetry.totalDays || 0) === 1 ? this.props.res.DIA : this.props.res.DIAS)) : '-')}
              {this.renderJobTelemetryRow(this.formatTelemetryText(this.props.res.DATA_EXECUCAO), telemetry && telemetry.executionStart ? this.formatPlanePositionDate(telemetry.executionStart) + (telemetry.executionEnd ? ' - ' + this.formatPlanePositionDate(telemetry.executionEnd) : '') : '-')}
              {this.renderJobTelemetryRow(this.formatTelemetryText(Units.getAreaUnit(this.props.res) === 'ac' ? this.props.res.TOTAL_ACRE : this.props.res.TOTAL_HECTARE), telemetry ? this.formatReportArea(telemetry.area, 2) : '-')}
              {this.renderJobTelemetryRow(this.formatTelemetryText(this.props.res.CONSUMO_TOTAL), telemetry ? this.formatReportVolume(telemetry.consumption, 0) : '-')}
              {this.renderJobTelemetryRow(this.formatTelemetryText(this.props.res.FLUXO_MEDIO), telemetry ? this.formatRealtimeFlow(telemetry.averageFlow, 2) : '-')}
              {this.renderJobTelemetryRow(this.formatTelemetryText(this.props.res.REABASTECIMENTO), telemetry ? this.formatJobTelemetryValue(telemetry.refuels, 0) : '-')}
              {this.renderJobTelemetryRow(this.formatTelemetryText(this.props.res.LARGURA_FAIXA), telemetry ? this.formatRealtimeLength(telemetry.swathWidth, 2) : '-')}
              {this.renderJobTelemetryRow(this.formatTelemetryText(this.props.res.ALTURA_MEDIA), telemetry ? this.formatRealtimeLength(telemetry.averageHeight, 2) : '-')}
              {this.renderJobTelemetryRow(this.formatTelemetryText(this.props.res.VELOCIDADE_MEDIA), telemetry ? this.formatReportSpeed(telemetry.averageSpeed, 2) : '-')}
              {this.renderJobTelemetryRow(this.formatTelemetryText(this.props.res.VELOCIDADE_APLICACAO_MEDIA), telemetry ? this.formatReportSpeed(telemetry.applicationSpeed, 2) : '-')}
              {this.renderJobTelemetryRow(this.formatTelemetryText(this.props.res.VELOCIDADE_TRANSLADO), telemetry ? this.formatReportSpeed(telemetry.travelSpeed, 2) : '-')}
              {this.renderJobTelemetryRow(this.formatTelemetryText(this.props.res.VELOCIDADE_MEDIA_BALAO), telemetry ? this.formatReportSpeed(telemetry.loopSpeed, 2) : '-')}
              {this.renderJobTelemetryRow(this.formatTelemetryText(this.props.res.TEMPO_TOTAL), telemetry ? this.formatJobTelemetryTime(telemetry.totalTime) : '-')}
              {this.renderJobTelemetryRow(this.formatTelemetryText(this.props.res.TEMPO_SOLO), telemetry ? this.formatJobTelemetryTime(telemetry.groundTime) : '-')}
              {this.renderJobTelemetryRow(this.formatTelemetryText(this.props.res.TEMPO_TRANSLADO), telemetry ? this.formatJobTelemetryTime(telemetry.travelTime) : '-')}
              {this.renderJobTelemetryRow(this.formatTelemetryText(this.props.res.TEMPO_BALAO), telemetry ? this.formatJobTelemetryTime(telemetry.loopTime) : '-')}
              {this.renderJobTelemetryRow(this.formatTelemetryText(this.props.res.TEMPO_APLICACAO), telemetry ? this.formatJobTelemetryTime(telemetry.applicationTime) : '-')}
              {this.renderJobTelemetryRow(this.formatTelemetryText(this.props.res.ULTIMA_ATUALIZACAO), this.formatPlanePositionDate(focusItem.job && focusItem.job.updated))}
            </>}
          </div>
          {!isRealtime && this.renderJobClimateSummary(focusItem.climateSummary)}
        </div>
        {!isRealtime && (
          <div className="telemetry-report-actions" onMouseDown={this.stopTelemetryActionEvent} onTouchStart={this.stopTelemetryActionEvent}>
            <button
              type="button"
              className="telemetry-report-button"
              disabled={!applicationReportItem}
              onClick={(event) => this.openJobApplicationReport(event, applicationReportItem)}
              title={this.props.res.RELATORIO_APLICACAO || this.props.res.RELATORIO}
            >
              <MaterialIcon icon="description" />
              <span>{this.props.res.RELATORIO_APLICACAO || this.props.res.RELATORIO}</span>
            </button>
            <button
              type="button"
              className="telemetry-report-button"
              disabled={!qualityReportItem}
              onClick={(event) => this.openJobQualityReport(event, qualityReportItem)}
              title={this.props.res.ANALISE_QUALIDADE}
            >
              <MaterialIcon icon="assessment" />
              <span>{this.props.res.ANALISE_QUALIDADE}</span>
            </button>
          </div>
        )}
      </div>
    );
  };

  isPlaneRealtimeConnected = (idPlane) => {
    const state = this.planeOnlineStateByPlaneId[idPlane];
    if (!state) return false;

    const now = Date.now();
    const heartbeatTime = toNumber(state.datetime, 0);
    const activityTime = toNumber(state.activityDatetime, 0);
    const latestPositionTime = Math.max(heartbeatTime, activityTime);

    if (latestPositionTime <= 0) return false;

    const positionAge = now - latestPositionTime;
    return positionAge >= -60000 && positionAge <= planePositionActiveWindowMs;
  };

  renderPlaneTelemetry = (forceVisible = false) => {
    if (this.state.freeMode || (!forceVisible && this.state.cameraFocusType !== 'plane')) return null;

    const focusItem = this.getSelectedPlaneFocusItem();
    if (!focusItem) return null;

    const rawPosition = focusItem.rawPosition || {};
    const point = focusItem.point || this.getValidPosition(rawPosition);
    const speed = rawPosition.speed;
    const canNavigatePlanes = this.getFilteredPlaneFocusItems().length > 1;
    const canNavigateStations = this.getFilteredStationFocusItems().length > 1;
    const planeName = this.getPlaneDisplayName(focusItem.plane);
    const idPlane = this.getPlaneId(focusItem.plane);
    const realtimeConnected = this.isPlaneRealtimeConnected(idPlane);
    const realtimeSourceType = this.playbackSourceByPlaneId[idPlane] || 'heartbeat';

    return (
      <div className="telemetry-board">
        <div className="telemetry-plane-navigation">
          <button
            type="button"
            className="telemetry-plane-arrow"
            onClick={this.goToPreviousPlane}
            disabled={!canNavigatePlanes}
            title={this.props.res.ANTERIOR}
          >
            ‹
          </button>
          <button
            type="button"
            className={`telemetry-plane-focus${realtimeConnected ? '' : ' telemetry-plane-focus-disconnected'}`}
            onClick={this.flyToSelectedPlane}
            title={this.props.res.CENTRALIZAR}
          >
            {planeName}
          </button>
          <button
            type="button"
            className="telemetry-plane-arrow"
            onClick={this.goToNextPlane}
            disabled={!canNavigatePlanes}
            title={this.props.res.PROXIMO}
          >
            ›
          </button>
        </div>
        <div className="telemetry-grid">
          <div className="telemetry-row">
            <span>{this.formatTelemetryText(this.props.res.LATITUDE)}</span>
            <strong>{point ? this.formatPlaneTelemetryValue(point.lat, 6) : '-'}</strong>
          </div>
          <div className="telemetry-row">
            <span>{this.formatTelemetryText(this.props.res.LONGITUDE)}</span>
            <strong>{point ? this.formatPlaneTelemetryValue(point.lng, 6) : '-'}</strong>
          </div>
          <div className="telemetry-row">
            <span>{this.formatTelemetryText(this.props.res.STATUS)}</span>
            <strong>{this.formatTelemetryText(
              this.hasOpenRealtimeExecutionForPlane(this.getPlaneId(focusItem.plane))
                ? (toNumber(rawPosition.active, rawPosition.spray ? 1 : 0) > 0
                  ? this.props.res.APLICANDO
                  : this.props.res.STATUS_EM_EXECUCAO)
                : this.props.res.POSICAO
            )}</strong>
          </div>
          <div className="telemetry-row">
            <span>{this.formatTelemetryText(this.props.res.VELOCIDADE)}</span>
            <strong>{
              speed !== undefined && speed !== null
                ? this.formatRealtimeSpeed(speed, 2, realtimeSourceType)
                : '-'
            }</strong>
          </div>
          <div className="telemetry-row">
            <span>{this.formatTelemetryText(this.props.res.ALTURA)}</span>
            <strong>{
              (rawPosition.alt !== undefined && rawPosition.alt !== null) || (rawPosition.altitude !== undefined && rawPosition.altitude !== null)
                ? this.formatRealtimeLength(rawPosition.alt !== undefined ? rawPosition.alt : rawPosition.altitude, 2)
                : '-'
            }</strong>
          </div>
          <div className="telemetry-row">
            <span>{this.formatTelemetryText(this.props.res.SPRAY)}</span>
            <strong>{this.formatTelemetryText(
              toNumber(rawPosition.active, rawPosition.spray ? 1 : 0) > 0
                ? this.props.res.APLICANDO
                : this.props.res.DESLIGADO
            )}</strong>
          </div>
          <div className="telemetry-row">
            <span>{this.formatTelemetryText(this.props.res.FLUXO)}</span>
            <strong>{
              (rawPosition.lph !== undefined && rawPosition.lph !== null) || (rawPosition.flow !== undefined && rawPosition.flow !== null)
                ? this.formatRealtimeFlow(rawPosition.lph !== undefined ? rawPosition.lph : rawPosition.flow, 2)
                : '-'
            }</strong>
          </div>
          <div className="telemetry-row">
            <span>{this.formatTelemetryText(this.props.res.LARGURA_FAIXA)}</span>
            <strong>{
              (rawPosition.swathwidth !== undefined && rawPosition.swathwidth !== null) || (rawPosition.swathWidth !== undefined && rawPosition.swathWidth !== null)
                ? this.formatRealtimeLength(rawPosition.swathwidth !== undefined ? rawPosition.swathwidth : rawPosition.swathWidth, 2)
                : '-'
            }</strong>
          </div>
          <div className="telemetry-row">
            <span>{this.formatTelemetryText(this.props.res.FAIXA_APP)}</span>
            <strong>{rawPosition.lockedline || '-'}</strong>
          </div>
          <div className="telemetry-row">
            <span>{this.formatTelemetryText(this.props.res.ULTIMA_ATUALIZACAO)}</span>
            <strong>{this.formatPlanePositionDate(rawPosition.datetime || rawPosition.created)}</strong>
          </div>
        </div>
      </div>
    );
  };


  getWeatherWindSpeedUnit = () => {
    const res = this.props.res || {};
    const savedUnit = localStorage.getItem('windSpeedUnit');
    const normalizedUnit = savedUnit ? String(savedUnit).trim().toLowerCase() : '';

    if (normalizedUnit === String(res.MS || 'm/s').toLowerCase() || normalizedUnit === 'm/s') {
      return res.MS || 'm/s';
    }

    if (normalizedUnit === String(res.KMH || 'km/h').toLowerCase() || normalizedUnit === 'km/h') {
      return res.KMH || 'km/h';
    }

    return res.KMH || 'km/h';
  };

  formatWeatherWindSpeed = (value, decimals = 1) => {
    const numericValue = this.getOptionalNumber(value);
    if (numericValue === null || numericValue === -99) return '-';

    const unit = this.getWeatherWindSpeedUnit();
    const isMetersPerSecond = String(unit).trim().toLowerCase() === 'm/s';
    const convertedValue = isMetersPerSecond ? numericValue : numericValue * 3.6;

    return convertedValue.toFixed(decimals) + ' ' + unit;
  };

  getWeatherThermalInversionText = (station) => {
    const res = this.props.res || {};
    const surfaceTemperature = this.getOptionalNumber(station && station.temperature);
    const airTemperature = this.getOptionalNumber(station && station.air_temperature);

    if (surfaceTemperature === null || airTemperature === null || surfaceTemperature === -99 || airTemperature === -99) {
      return '-';
    }

    if (airTemperature - (surfaceTemperature + 3) > 0) {
      return res.INVERSAO_ALTA_CHANCE;
    }

    if (airTemperature - (surfaceTemperature + 1) > 0) {
      return res.INVERSAO_POSSIVEL;
    }

    return res.SEM_INVERSAO_TERMICA;
  };

  getWeatherThermalInversionColor = (station) => {
    const surfaceTemperature = this.getOptionalNumber(station && station.temperature);
    const airTemperature = this.getOptionalNumber(station && station.air_temperature);

    if (surfaceTemperature === null || airTemperature === null || surfaceTemperature === -99 || airTemperature === -99) {
      return null;
    }

    if (airTemperature - (surfaceTemperature + 3) > 0) return 'red';
    if (airTemperature - (surfaceTemperature + 1) > 0) return 'yellow';
    return 'lawngreen';
  };

  getWeatherDeltaTConditionText = (deltaT) => {
    const res = this.props.res || {};
    const numericValue = this.getOptionalNumber(deltaT);
    if (numericValue === null || numericValue === -99) return '-';

    if (numericValue >= 2 && numericValue <= 8) {
      return res.CONDICOES_ADEQUADAS;
    }

    if (numericValue > 10) {
      return res.CONDICOES_INADEQUADAS;
    }

    return res.CONDICOES_ARRISCADAS;
  };

  getWeatherDeltaTConditionColor = (deltaT) => {
    const numericValue = this.getOptionalNumber(deltaT);
    if (numericValue === null || numericValue === -99) return null;

    if (numericValue >= 2 && numericValue <= 8) return 'lawngreen';
    if (numericValue > 10) return 'red';
    return 'yellow';
  };

  renderWeatherWindDirection = (windDirection) => {
    const numericValue = this.getOptionalNumber(windDirection);
    if (numericValue === null || numericValue === -99) return '-';
    const normalizedDirection = ((numericValue % 360) + 360) % 360;
    const windTargetDirection = (normalizedDirection + 180) % 360;

    return (
      <span style={{
        display: 'inline-grid',
        gridTemplateColumns: 'auto 17px',
        alignItems: 'center',
        justifyContent: 'end',
        columnGap: '6px',
        minWidth: '70px',
        paddingRight: '7px',
        boxSizing: 'border-box',
        whiteSpace: 'nowrap',
        verticalAlign: 'middle'
      }}>
        <span style={{ color: '#fff', fontWeight: 700, whiteSpace: 'nowrap', lineHeight: 1 }}>{normalizedDirection.toFixed(1)}°</span>
        <svg
          aria-hidden="true"
          title={normalizedDirection.toFixed(1) + '°'}
          viewBox="0 0 24 24"
          style={{
            width: '17px',
            height: '17px',
            color: '#FA7D34',
            transform: `rotate(${windTargetDirection}deg)`,
            transformOrigin: '50% 50%',
            flex: '0 0 auto'
          }}
        >
          <path fill="currentColor" d="M12 2L6 21l6-4 6 4-6-19z" />
        </svg>
      </span>
    );
  };

  getWeatherSolarRadiationText = (solar) => {
    const res = this.props.res || {};
    const numericValue = this.getOptionalNumber(solar);
    if (numericValue === null || numericValue === -99) return '-';

    let condition = '-';
    if (numericValue < 3) {
      condition = res.RADIACAO_SOLAR_SEM;
    } else if (numericValue >= 3 && numericValue <= 400) {
      condition = res.RADIACAO_SOLAR_BAIXA;
    } else if (numericValue >= 401 && numericValue <= 800) {
      condition = res.RADIACAO_SOLAR_MODERADA;
    } else if (numericValue > 801) {
      condition = res.RADIACAO_SOLAR_ALTA;
    }

    return condition || '-';
  };

  renderStationTelemetry = (forceVisible = false) => {
    if (!forceVisible && (this.state.freeMode || this.state.cameraFocusType !== 'station')) return null;
    const focusItem = this.getSelectedStationFocusItem();
    if (!focusItem) return null;
    const station = focusItem.station || {};
    const point = this.getWeatherStationPoint(station) || focusItem.point;
    const canNavigate = this.getFilteredStationFocusItems().length > 1;
    const res = this.props.res || {};
    const value = (v, digits) => (v === undefined || v === null || v === '' ? '-' : this.formatPlaneTelemetryValue(v, digits));
    const dateText = this.formatPlanePositionDate(station.datetime);
    const windSpeedText = this.formatWeatherWindSpeed(station.wind_speed, 1);
    const thermalInversionText = this.getWeatherThermalInversionText(station);
    const thermalInversionColor = this.getWeatherThermalInversionColor(station);
    const deltaTConditionText = this.getWeatherDeltaTConditionText(station.delta_t);
    const deltaTConditionColor = this.getWeatherDeltaTConditionColor(station.delta_t);
    const solarRadiationText = this.getWeatherSolarRadiationText(station.solar);
    const deltaTText = station.delta_t !== undefined && station.delta_t !== null && this.getOptionalNumber(station.delta_t) !== -99
      ? value(station.delta_t, 1) + ' · ' + deltaTConditionText
      : '-';
    const stationOnline = station.online === true;

    return (
      <div className="telemetry-board telemetry-station-board">
        <div className="telemetry-plane-navigation">
          <button type="button" className="telemetry-plane-arrow" onClick={this.goToPreviousStation} disabled={!canNavigate} title={res.ANTERIOR}>‹</button>
          <button type="button" className={`telemetry-plane-focus${stationOnline ? '' : ' telemetry-plane-focus-disconnected'}`} onClick={this.flyToSelectedStation} title={res.CENTRALIZAR}>{this.getWeatherStationName(station)}</button>
          <button type="button" className="telemetry-plane-arrow" onClick={this.goToNextStation} disabled={!canNavigate} title={res.PROXIMO}>›</button>
        </div>
        <div className="telemetry-grid">
          {this.renderJobTelemetryRow(res.STATUS, stationOnline ? res.ONLINE : res.OFFLINE)}
          {this.renderJobTelemetryRow(res.LATITUDE, point ? value(point.lat, 6) : '-')}
          {this.renderJobTelemetryRow(res.LONGITUDE, point ? value(point.lng, 6) : '-')}
          {this.renderJobTelemetryRow(res.TEMPERATURA_, station.air_temperature !== undefined && station.air_temperature !== null ? value(station.air_temperature, 1) + ' ' + res.C : '-')}
          {this.renderJobTelemetryRow(res.UMIDADE_, station.air_humidity !== undefined && station.air_humidity !== null ? value(station.air_humidity, 1) + ' %' : '-')}
          {this.renderJobTelemetryRow(res.VENTO_VELOCIDADE, windSpeedText)}
          {this.renderJobTelemetryRow(res.DIRECAO_VENTO, this.renderWeatherWindDirection(station.wind_direction))}
          {this.renderJobTelemetryRow(
            res.INVERSAO_TERMICA,
            thermalInversionColor
              ? <span style={{ color: thermalInversionColor }}>{thermalInversionText}</span>
              : thermalInversionText
          )}
          {this.renderJobTelemetryRow(
            res.DELTA_T,
            deltaTConditionColor
              ? <span style={{ color: deltaTConditionColor }}>{deltaTText}</span>
              : deltaTText
          )}
          {this.renderJobTelemetryRow(res.PRESSAO, station.pressure !== undefined && station.pressure !== null ? value(station.pressure, 1) + ' ' + res.HPA : '-')}
          {this.renderJobTelemetryRow(res.ALTITUDE, station.height !== undefined && station.height !== null ? value(station.height, 1) + ' ' + res.M : '-')}
          {this.renderJobTelemetryRow(res.RADIACAO_SOLAR, solarRadiationText)}
          {this.renderJobTelemetryRow(res.CHUVA, station.rain !== undefined && station.rain !== null ? value(station.rain, 1) + ' ' + res.MM : '-')}
          {this.renderJobTelemetryRow(res.ULTIMA_ATUALIZACAO, dateText)}
        </div>
      </div>
    );
  };


  isDesktopExecutedJobSelected = () => {
    if (this.state.mobileCompact) return false;
    const preferredType = this.desktopOperationalFocusType || this.state.cameraFocusType || 'job';
    if (preferredType !== 'job') return false;
    return this.isJobFocusItemExecuted(this.getSelectedFocusItem());
  };

  shouldShowDesktopStationTelemetry = () => {
    if (!this.props.hasWeatherStations || !this.getFilteredStationFocusItems().length) return false;
    if (this.isDesktopExecutedJobSelected()) {
      return this.state.desktopExecutedStationVisible === true;
    }
    return this.state.desktopStationManuallyHidden !== true;
  };

  toggleDesktopExecutedStationTelemetry = () => {
    if (!this.props.hasWeatherStations || !this.getFilteredStationFocusItems().length) return;
    if (this.isDesktopExecutedJobSelected()) {
      this.setState((state) => ({
        desktopExecutedStationVisible: !state.desktopExecutedStationVisible
      }));
      return;
    }
    this.setState((state) => ({
      desktopStationManuallyHidden: !state.desktopStationManuallyHidden
    }));
  };

  renderDesktopOperationalTelemetry = () => {
    if (this.state.cameraFocusType === 'job' || this.state.cameraFocusType === 'plane') {
      this.desktopOperationalFocusType = this.state.cameraFocusType;
    }

    const preferredType = this.desktopOperationalFocusType || 'job';
    if (preferredType === 'plane') {
      return this.renderPlaneTelemetry(true) || this.renderJobTelemetry(true);
    }
    return this.renderJobTelemetry(true) || this.renderPlaneTelemetry(true);
  };

  renderPanel = () => {
    const res = this.props.res || {};
    const jobs = this.state.jobs || [];
    const planes = this.state.planes || [];
    const filteredJobFocusItems = this.getFilteredJobFocusItems();
    const filteredPlaneFocusItems = this.getFilteredPlaneFocusItems();
    const filteredStationFocusItems = this.getFilteredStationFocusItems();
    const canNavigateJobs = filteredJobFocusItems.length > 1;
    const canNavigatePlanes = filteredPlaneFocusItems.length > 1;
    const canNavigateStations = filteredStationFocusItems.length > 1;

    return (
      <aside className="panel">
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', marginBottom: 18 }}>
          <img
            src="/assets/covers/logo.png"
            alt="Travicar Connect"
            style={{ width: 125, height: 48, objectFit: 'contain' }}
          />
        </div>
        <div className="panel-header">
          <h1>{res.MAPA_FAZENDA_ONLINE}</h1>
          <button id="panelToggleBtn" className="panel-toggle" type="button" aria-pressed="false" title={res.OCULTAR_MENU_LATERAL} onClick={this.togglePanelVisibility}>{res.OCULTAR_MENU}</button>
        </div>
        <p className="subtitle">{res.MAPA_3D_TRABALHOS_EXECUCAO_CICLO}</p>

        <div className="field">
          <div className="controls one-col">
            <button type="button" onClick={this.toggleFreeMode}>{this.state.freeMode ? res.FOCAR_TRABALHO_SELECIONADO : res.MODO_LIVRE}</button>
          </div>
        </div>

        <div className="field desktop-filter-field">
          <div className="desktop-filter-title-row">
            <label>{res.TRABALHO_EM_FOCO}</label>
            <button type="button" className="desktop-item-selector-button" title={res.FILTRO} onClick={() => this.toggleItemSelector('job')}>
              <MaterialIcon icon="filter_list" />
            </button>
          </div>
          {this.renderDesktopItemSelector('job', this.state.focusItems || [])}
          <div className="job-focus-filter-group" role="group">
            <button
              type="button"
              className={this.state.jobFocusFilter === 'all' ? 'is-active' : ''}
              aria-pressed={this.state.jobFocusFilter === 'all'}
              onClick={() => this.setJobFocusFilter('all')}
            >
              {res.TODOS}
            </button>
            <button
              type="button"
              className={this.state.jobFocusFilter === 'executed' ? 'is-active' : ''}
              aria-pressed={this.state.jobFocusFilter === 'executed'}
              onClick={() => this.setJobFocusFilter('executed')}
            >
              {res.EXECUTADOS || res.CONCLUIDO}
            </button>
            <button
              type="button"
              className={this.state.jobFocusFilter === 'running' ? 'is-active' : ''}
              aria-pressed={this.state.jobFocusFilter === 'running'}
              onClick={() => this.setJobFocusFilter('running')}
            >
              {res.STATUS_EM_EXECUCAO}
            </button>
            <button
              type="button"
              className={this.state.jobFocusFilter === 'pending' ? 'is-active' : ''}
              aria-pressed={this.state.jobFocusFilter === 'pending'}
              onClick={() => this.setJobFocusFilter('pending')}
            >
              {res.PLANEJADO}
            </button>
          </div>
          <div className="status" style={{ marginTop: 0 }}>
            <div><strong>{this.getSelectedFocusTitle()}</strong></div>
            <div style={{ marginTop: 4 }}>{this.getSelectedFocusSubtitle()}</div>
          </div>
          <div className="controls map-controls">
            <button type="button" onClick={this.goToPreviousJob} disabled={!canNavigateJobs}>{res.ANTERIOR}</button>
            <button type="button" onClick={this.flyToSelectedJob} disabled={!filteredJobFocusItems.length}>{res.IR_PARA}</button>
            <button type="button" onClick={this.goToNextJob} disabled={!canNavigateJobs}>{res.PROXIMO}</button>
          </div>
        </div>

        <div className="field desktop-filter-field">
          <div className="desktop-filter-title-row">
            <label>{res.AERONAVE}</label>
            <button type="button" className="desktop-item-selector-button" title={res.FILTRO} onClick={() => this.toggleItemSelector('plane')}>
              <MaterialIcon icon="filter_list" />
            </button>
          </div>
          {this.renderDesktopItemSelector('plane', this.state.planeFocusItems || [])}
          <div className="job-focus-filter-group" role="group">
            <button
              type="button"
              className={this.state.planeFocusFilter === 'all' ? 'is-active' : ''}
              aria-pressed={this.state.planeFocusFilter === 'all'}
              onClick={() => this.setPlaneFocusFilter('all')}
            >
              {res.TODOS}
            </button>
            <button
              type="button"
              className={this.state.planeFocusFilter === 'online' ? 'is-active' : ''}
              aria-pressed={this.state.planeFocusFilter === 'online'}
              onClick={() => this.setPlaneFocusFilter('online')}
            >
              {res.ONLINE}
            </button>
            <button
              type="button"
              className={this.state.planeFocusFilter === 'offline' ? 'is-active' : ''}
              aria-pressed={this.state.planeFocusFilter === 'offline'}
              onClick={() => this.setPlaneFocusFilter('offline')}
            >
              {res.OFFLINE}
            </button>
          </div>
          <div className="status" style={{ marginTop: 0 }}>
            <div><strong>{this.getSelectedPlaneFocusTitle()}</strong></div>
            <div style={{ marginTop: 4 }}>{this.getSelectedPlaneFocusSubtitle()}</div>
          </div>
          <div className="controls map-controls">
            <button type="button" onClick={this.goToPreviousPlane} disabled={!canNavigatePlanes}>{res.ANTERIOR}</button>
            <button type="button" onClick={this.flyToSelectedPlane} disabled={!filteredPlaneFocusItems.length}>{res.IR_PARA}</button>
            <button type="button" onClick={this.goToNextPlane} disabled={!canNavigatePlanes}>{res.PROXIMO}</button>
          </div>
        </div>


        {this.props.hasWeatherStations && (
          <>
            <div className="field desktop-filter-field">
              <div className="desktop-filter-title-row">
                <label>{res.ESTACAO}</label>
                <div className="desktop-filter-title-actions">
                  <button
                    type="button"
                    className={`desktop-item-selector-button desktop-station-visibility-button${this.shouldShowDesktopStationTelemetry() ? ' is-active' : ''}`}
                    title={this.shouldShowDesktopStationTelemetry() ? (res.OCULTAR || 'Ocultar') : (res.MOSTRAR || 'Mostrar')}
                    aria-pressed={this.shouldShowDesktopStationTelemetry()}
                    onClick={this.toggleDesktopExecutedStationTelemetry}
                  >
                    <MaterialIcon icon={this.shouldShowDesktopStationTelemetry() ? 'visibility' : 'visibility_off'} />
                  </button>
                  <button type="button" className="desktop-item-selector-button" title={res.FILTRO} onClick={() => this.toggleItemSelector('station')}>
                    <MaterialIcon icon="filter_list" />
                  </button>
                </div>
              </div>
              {this.renderDesktopItemSelector('station', this.state.stationFocusItems || [])}
              <div className="job-focus-filter-group" role="group">
                <button
                  type="button"
                  className={this.state.stationFocusFilter === 'all' ? 'is-active' : ''}
                  aria-pressed={this.state.stationFocusFilter === 'all'}
                  onClick={() => this.setStationFocusFilter('all')}
                >
                  {res.TODOS}
                </button>
                <button
                  type="button"
                  className={this.state.stationFocusFilter === 'online' ? 'is-active' : ''}
                  aria-pressed={this.state.stationFocusFilter === 'online'}
                  onClick={() => this.setStationFocusFilter('online')}
                >
                  {res.ONLINE}
                </button>
                <button
                  type="button"
                  className={this.state.stationFocusFilter === 'offline' ? 'is-active' : ''}
                  aria-pressed={this.state.stationFocusFilter === 'offline'}
                  onClick={() => this.setStationFocusFilter('offline')}
                >
                  {res.OFFLINE}
                </button>
              </div>
              <div className="status" style={{ marginTop: 0 }}>
                <div><strong>{this.getSelectedStationFocusItem() ? this.getWeatherStationName(this.getSelectedStationFocusItem().station) : (res.SEM_CONEXAO || 'Nenhuma estação online')}</strong></div>
                <div style={{ marginTop: 4 }}>{(() => {
                  const stationItems = this.state.stationFocusItems || [];
                  if (!stationItems.length) return '-';
                  const onlineCount = stationItems.filter(item => item && item.station && item.station.online === true).length;
                  const offlineCount = stationItems.length - onlineCount;
                  return res.ONLINE + ': ' + onlineCount + ' | ' + res.OFFLINE + ': ' + offlineCount;
                })()}</div>
              </div>
              <div className="controls map-controls">
                <button type="button" onClick={this.goToPreviousStation} disabled={!canNavigateStations}>{res.ANTERIOR}</button>
                <button type="button" onClick={this.flyToSelectedStation} disabled={!filteredStationFocusItems.length}>{res.IR_PARA}</button>
                <button type="button" onClick={this.goToNextStation} disabled={!canNavigateStations}>{res.PROXIMO}</button>
              </div>
            </div>

          </>
        )}

        <div className="field camera-slider-field">
          <label htmlFor="onlineFarmCameraHeading">
            <span>{res.GIRO}</span>
            <strong>{Math.round(this.state.cameraHeadingDeg)}°</strong>
          </label>
          <input
            id="onlineFarmCameraHeading"
            type="range"
            min="-180"
            max="180"
            step="1"
            value={this.state.cameraHeadingDeg}
            onChange={this.handleCameraHeadingChange}
          />
        </div>

        <div className="field camera-slider-field">
          <label htmlFor="onlineFarmCameraPitch">
            <span>{res.INCLINACAO}</span>
            <strong>{Math.round(this.state.cameraPitchDeg)}°</strong>
          </label>
          <input
            id="onlineFarmCameraPitch"
            type="range"
            min="-90"
            max="-10"
            step="1"
            value={this.state.cameraPitchDeg}
            onChange={this.handleCameraPitchChange}
          />
        </div>

        <div className="field camera-slider-field">
          <label htmlFor="onlineFarmCameraRange">
            <span>{res.DISTANCIA}</span>
            <strong>{Math.round(this.state.cameraRangeMeters)} m</strong>
          </label>
          <input
            id="onlineFarmCameraRange"
            type="range"
            min={cameraRangeMinimumMeters}
            max={cameraRangeMaximumMeters}
            step="25"
            value={this.state.cameraRangeMeters}
            onChange={this.handleCameraRangeChange}
          />
        </div>

        {this.props.hasWeatherStations && (
          <div className="field camera-slider-field">
            <label htmlFor="onlineFarmWeatherStationCoverageOpacity">
              <span>{res.OPACIDADE_RAIO_ESTACAO}</span>
              <strong>{Math.round(this.state.weatherStationCoverageOpacity * 100)}%</strong>
            </label>
            <input
              id="onlineFarmWeatherStationCoverageOpacity"
              type="range"
              min="0"
              max="30"
              step="1"
              value={Math.round(this.state.weatherStationCoverageOpacity * 100)}
              onChange={this.handleWeatherStationCoverageOpacityChange}
            />
          </div>
        )}

        <div className="status">
          <div><strong>{res.TRABALHOS}:</strong> {(this.state.focusItems || []).length}</div>
          <div><strong>{res.AERONAVE}:</strong> {planes.length}</div>
          {this.props.hasWeatherStations ? (
            <div><strong>{res.ESTACAO}:</strong> {(this.state.stationFocusItems || []).length}</div>
          ) : null}
          {this.state.message ? <div style={{ marginTop: 8 }}>{this.state.message}</div> : null}
        </div>
      </aside>
    );
  };

  getCurrentPoints = () => {
    const entities = this.viewer && this.viewer.entities ? this.viewer.entities.values : [];
    const points = [];
    for (let i = 0; i < entities.length; i++) {
      const position = entities[i].position && entities[i].position.getValue ? entities[i].position.getValue(Cesium.JulianDate.now()) : null;
      if (!position) continue;
      const cartographic = Cesium.Cartographic.fromCartesian(position);
      points.push({ lat: Cesium.Math.toDegrees(cartographic.latitude), lng: Cesium.Math.toDegrees(cartographic.longitude) });
    }
    return points;
  };

  updateFlightCompassHeading = (idPlane, headingDeg) => {
    if (this.state.freeMode || this.state.cameraFocusType !== 'plane') return;

    const focusItem = this.getSelectedPlaneFocusItem();
    const selectedPlaneId = focusItem ? this.getPlaneId(focusItem.plane) : 0;
    if (!selectedPlaneId || toNumber(idPlane, 0) !== toNumber(selectedPlaneId, 0)) return;

    const heading = this.normalizeHeadingDegrees(headingDeg);
    if (this.flightCompassArrowElement) {
      this.flightCompassArrowElement.style.transform = 'rotate(' + heading + 'deg)';
    }
    if (this.flightCompassHeadingElement) {
      this.flightCompassHeadingElement.textContent = Math.round(heading) + '°';
    }
  };

  getSelectedPlaneCompassHeading = () => {
    const focusItem = this.getSelectedPlaneFocusItem();
    if (!focusItem) return 0;

    const idPlane = this.getPlaneId(focusItem.plane);
    const motion = this.aircraftMotionByPlaneId[idPlane];
    if (motion && Number.isFinite(motion.currentHeading)) {
      const model = this.getAircraftModel(focusItem.plane);
      return this.normalizeHeadingDegrees(
        motion.currentHeading - toNumber(model && model.modelHeadingOffsetDeg, 0)
      );
    }

    const currentPosition = (this.state.positionsByPlane && this.state.positionsByPlane[idPlane]) ||
      this.planePositionsById[idPlane] || focusItem.rawPosition || focusItem.plane;
    return this.normalizeHeadingDegrees(this.getHeadingFromPosition(currentPosition));
  };

  renderFlightCompass = () => {
    if (this.state.freeMode || this.state.cameraFocusType !== 'plane') return null;

    const focusItem = this.getSelectedPlaneFocusItem();
    if (!focusItem) return null;

    const heading = this.getSelectedPlaneCompassHeading();

    return (
      <div
        className="online-flight-compass"
        title={'Rumo da aeronave: ' + Math.round(heading) + '°'}
        aria-label={'Rumo da aeronave ' + Math.round(heading) + ' graus'}
      >
        <div className="online-flight-compass-ring">
          <span className="online-flight-compass-cardinal north">N</span>
          <span className="online-flight-compass-cardinal east">L</span>
          <span className="online-flight-compass-cardinal south">S</span>
          <span className="online-flight-compass-cardinal west">O</span>
          <span className="online-flight-compass-axis axis-ns" />
          <span className="online-flight-compass-axis axis-ew" />
          <span
            className="online-flight-compass-arrow"
            ref={(element) => { this.flightCompassArrowElement = element; }}
            style={{ transform: `rotate(${heading}deg)` }}
          >
            <span className="online-flight-compass-arrow-tip" />
            <span className="online-flight-compass-arrow-tail" />
          </span>
          <span className="online-flight-compass-center" />
        </div>
        <span
          className="online-flight-compass-heading"
          ref={(element) => { this.flightCompassHeadingElement = element; }}
        >
          {Math.round(heading)}°
        </span>
      </div>
    );
  };

  renderScreenCompass = () => {
    const res = this.props.res || {};
    const cardinals = [
      { key: 'north', bearing: 0, label: res.BUSSOLA_NORTE_SIGLA || 'N' },
      { key: 'east', bearing: 90, label: res.BUSSOLA_LESTE_SIGLA || 'L' },
      { key: 'south', bearing: 180, label: res.BUSSOLA_SUL_SIGLA || 'S' },
      { key: 'west', bearing: 270, label: res.BUSSOLA_OESTE_SIGLA || 'O' }
    ];

    return (
      <div className="online-screen-compass" aria-hidden="true">
        {cardinals.map((cardinal) => {
          const position = this.getScreenCompassPosition(cardinal.bearing);
          return (
            <span
              key={cardinal.key}
              className={`online-screen-compass-cardinal screen-compass-${cardinal.key}`}
              ref={(element) => { this.screenCompassElements[cardinal.key] = element; }}
              style={{ left: position.left + '%', top: position.top + '%' }}
            >
              {cardinal.label}
            </span>
          );
        })}
      </div>
    );
  };

  renderLoading = () => {
    const res = this.props.res || {};
    if (!this.state.loading) return null;
    return (
      <div className="replay3d-base-loading">
        <div className="replay3d-base-loading-card">
          <div className="replay3d-base-loading-spinner" />
          <div className="replay3d-base-loading-title">{res.CARREGANDO_MAPA_3D}</div>
          <div className="replay3d-base-loading-text">{res.PREPARANDO_MAPA_3D_TRABALHOS}</div>
        </div>
      </div>
    );
  };

  render() {
    const res = this.props.res || {};
    const fullscreen = this.state.fullscreen;
    const fullscreenIcon = fullscreen ? 'fullscreen_exit' : 'fullscreen';
    const fullscreenTitle = fullscreen ? res.SAIR_TELA_CHEIA : res.TELA_CHEIA;

    return (
      <div className="replay3d-base-overlay">
        <div className="replay3d-base-app-host">
          <div
            id="replay3dBaseApp"
            className={`${this.state.panelCollapsed ? 'panel-collapsed' : ''}${this.state.mobileCompact ? ' mobile-compact-layout' : ''}`}
          >
            {!this.state.mobileCompact ? this.renderPanel() : null}
            {this.state.mobileCompact ? (
              <div className="mobile-telemetry-stack">
                {this.renderMobileFocusSwitcher()}
                {this.renderJobTelemetry()}
                {this.renderPlaneTelemetry()}
                {this.renderStationTelemetry()}
              </div>
            ) : (
              <div className="desktop-telemetry-stack">
                {this.renderDesktopOperationalTelemetry()}
                {this.shouldShowDesktopStationTelemetry() ? this.renderStationTelemetry(true) : null}
              </div>
            )}
            {!this.state.mobileCompact ? (
              <button id="panelRestoreBtn" className="panel-restore-btn" type="button" aria-pressed="true" title={res.MOSTRAR_MENU_LATERAL} onClick={this.togglePanelVisibility}>{res.MOSTRAR_MENU}</button>
            ) : null}
            <div id="cesiumContainer">
              <div id={this.containerId} style={{ width: '100%', height: '100%' }} />
              {this.renderScreenCompass()}
            </div>
          </div>
        </div>

        {this.renderLoading()}

        {this.renderFlightCompass()}

        <div className="replay3d-base-action-buttons">
          <Button className="replay3d-base-action-button replay3d-base-fullscreen-button" onClick={this.toggleFullscreen} title={fullscreenTitle}>
            <MaterialIcon icon={fullscreenIcon} className="iconMap" />
          </Button>
          <Button className="replay3d-base-action-button replay3d-base-close-button" onClick={this.props.onClose} title={res.FECHAR_MAPA_3D}>
            <MaterialIcon icon="close" className="iconMap" />
          </Button>
        </div>
      </div>
    );
  }
}

export default Map3D;